import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import BopBackLink from '../../../components/BopBackLink';
import ApiInspector from '../../../components/ApiInspector';
import { apiFetch, apiDownload, apiOpenBlob } from '../../../lib/api';
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
  CollisionDetection,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { usePermesso } from '../../../context/PermessiContext';
import { useBloccoRecordContesto } from '../../../hooks/useBloccoRecordContesto';
import { showToast } from '../../../components/Toast';
import { formatCurrency, formatQuantity, formatNumber } from '../../../utils/formatters';
import { API_DOCS } from './PreventivoDettaglioPage.api';
import ModalConfigurazioneRiga from './ModalConfigurazioneRiga';
import PrintModal from '../../../components/PrintModal';
import ModalSceltaVistaStampa, { VistaStampa, StampaTotali, StampaAllegati } from './ModalSceltaVistaStampa';
import type { RigaLite, PreventivoLite } from './ModalConfigurazioneRiga';
import CondizioniHtmlEditor from '../../../components/CondizioniHtmlEditor';
import BadgeFlussoCommerciale, { FlussoCommerciale } from '../../../components/BadgeFlussoCommerciale';
import './PreventivoDettaglioPage.css';

const MIME_PREVIEWABILI = new Set([
  'application/pdf',
  'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml',
]);
const EXT_PREVIEWABILI = new Set(['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'svg']);
const isPreviewabile = (mimeType: string | null, nomeFile?: string) => {
  if (mimeType && MIME_PREVIEWABILI.has(mimeType)) return true;
  const ext = nomeFile?.split('.').pop()?.toLowerCase() ?? '';
  return EXT_PREVIEWABILI.has(ext);
};

// ── Icone ───────────────────────────────────────────────────────────────────
const PlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const XIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);
const CopyIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="14" height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
  </svg>
);
const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);
const BranchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="6" y1="3" x2="6" y2="15" />
    <circle cx="18" cy="6" r="3" />
    <circle cx="6" cy="18" r="3" />
    <path d="M18 9a9 9 0 0 1-9 9" />
  </svg>
);
const GripIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <circle cx="9" cy="6" r="1.6" /><circle cx="15" cy="6" r="1.6" />
    <circle cx="9" cy="12" r="1.6" /><circle cx="15" cy="12" r="1.6" />
    <circle cx="9" cy="18" r="1.6" /><circle cx="15" cy="18" r="1.6" />
  </svg>
);
const FocusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 7V3h4" /><path d="M21 7V3h-4" /><path d="M3 17v4h4" /><path d="M21 17v4h-4" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
const ChevronRightIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);
const ChevronDownSimpleIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const ChevronDownIcon = ({ collapsed }: { collapsed: boolean }) => (
  <svg
    width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
    strokeLinecap="round" strokeLinejoin="round"
    style={{ transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 200ms ease', flexShrink: 0 }}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

// ── Tipi ────────────────────────────────────────────────────────────────────
type TipoPreventivo = 'SEMPLICE' | 'CONFIGURATO_COMMESSA';
type StatoPreventivo = 'BOZZA' | 'INVIATO' | 'ACCETTATO' | 'RIFIUTATO' | 'SCADUTO' | 'SUPERATO' | 'CONVERTITO' | 'ANNULLATO' | 'ANNULLATO_DOPO_CONFERMA';
type TipoRigaPreventivo = 'ARTICOLO' | 'ARTICOLO_CONFIGURATO' | 'DESCRITTIVA' | 'A_CORPO' | 'RICORRENTE';
type SottotipoRicorrenza = 'LICENZA' | 'CANONE' | 'CONSUMI' | 'COMMISSIONI';
type PeriodicitaRicorrenza = 'MENSILE' | 'TRIMESTRALE' | 'SEMESTRALE' | 'ANNUALE';
type ModalitaPagamentoRicorrenza = 'A_PREVENTIVO' | 'A_CONSUNTIVO_VOLUMI' | 'A_CONSUNTIVO_TRANSAZIONI';

interface TotaliRamoAcconto {
  percentuale: number;
  imponibile: number;
  iva: number;
  totale: number;
  condizionePagamento: { id: number; nome: string } | null;
}

interface TotaliRamoPreventivo {
  imponibile: number;
  aliquotaIva: number;
  codiceIva: { id: number; codice: string; descrizione: string } | null;
  iva: number;
  totale: number;
  acconto: TotaliRamoAcconto | null;
  saldoTotale: number;
}

interface TotaliDettaglioPreventivo {
  isImpresaAcquirente: boolean;
  impresa: TotaliRamoPreventivo;
  acquirente: TotaliRamoPreventivo | null;
}

interface Preventivo {
  id: number;
  numero: string;
  versione: number;
  tipo: TipoPreventivo;
  flussoCommerciale: FlussoCommerciale | null;
  stato: StatoPreventivo;
  oggetto: string;
  dataEmissione: string;
  dataValidita: string | null;
  note: string | null;
  indirizzoLavori: string | null;
  condizioniStampa: string | null;
  societaId: number;
  sedeId: number;
  commessaId: number | null;
  offertaCapitolatoId: number | null;
  acquirenteId: number | null;
  codiceIvaDefaultId: number;
  condizionePagamentoId: number | null;
  condizionePagamentoAccontoId: number | null;
  condizionePagamentoExtraAccontoId: number | null;
  condizionePagamentoExtraSaldoId: number | null;
  codiceIvaExtraAcquirenteId: number | null;
  percentualeAcconto: number | string | null;
  percentualeAccontoExtraAcquirente: number | string | null;
  nominativoId: number | null;
  clienteRagioneSociale: string | null;
  clienteNome: string | null;
  clienteCognome: string | null;
  clientePartitaIva: string | null;
  clienteCodiceFiscale: string | null;
  clienteIndirizzo: string | null;
  clienteCap: string | null;
  clienteCitta: string | null;
  clienteProvincia: string | null;
  clienteEmail: string | null;
  clienteTelefono: string | null;
  scontoGlobaleTipo: 'PERCENTUALE' | 'IMPORTO' | null;
  scontoGlobaleValore: string | null;
  ricaricoImpresaFreeze: string | null;
  tipoFatturazioneFreeze: 'IMPRESA_PAGA_TUTTO' | 'CLIENTE_PAGA_DIRETTAMENTE' | null;
  totaleImponibileCliente: string | null;
  totaleIvaCliente: string | null;
  totaleCliente: string | null;
  totaleImponibileInterno: string | null;
  raggruppamenti: Raggruppamento[];
  righe: Riga[];
  allegati: Allegato[];
  commessa: { id: number; numero: string; descrizione: string } | null;
  offertaCapitolato: { id: number; codice: string; oggetto: string; tipoFatturazione: string; ricarico: string } | null;
  acquirente: {
    id: number;
    nome: string;
    cognome: string;
    unitaCodice: string;
    email: string | null;
    telefono: string | null;
    allegati: AllegatoAcquirente[];
  } | null;
  codiceIvaDefault: { id: number; codice: string; descrizione: string; aliquota: string };
  condizionePagamento: { id: number; nome: string } | null;
  condizionePagamentoAcconto: { id: number; nome: string } | null;
  condizionePagamentoExtraAcconto: { id: number; nome: string } | null;
  condizionePagamentoExtraSaldo: { id: number; nome: string } | null;
  codiceIvaExtraAcquirente: { id: number; codice: string; descrizione: string; aliquota: string } | null;
  importoAcconto: number | null;
  importoSaldo: number | null;
  totaliDettaglio: TotaliDettaglioPreventivo;
  sede: { id: number; nome: string };
  createdBy: { id: number; nome: string; cognome: string; email: string };
  preventivoOrigine: { id: number; numero: string } | null;
  supersededBy: { id: number; numero: string } | null;
  commessaGenerata: { id: number; numero: string } | null;
  ordiniGenerati: { id: number; numero: string; destinatarioOrdine: string | null; isSoloImporto: boolean; totale: string }[];

  // Tracciatura invio (Fase C)
  dataInvio: string | null;
  noteInvio: string | null;

  // Tracciatura firma cliente (Fase C)
  firmatoAt: string | null;
  firmatoBy: string | null;
  firmaAllegatoId: number | null;
  firmaModalita: 'UPLOAD_CARTACEO' | 'FIRMA_DIGITALE' | null;

  // Firma digitale Zoho Sign (Fase D)
  zohoRequestId: string | null;
  zohoStato: 'INVIATA' | 'FIRMATA' | 'RIFIUTATA' | 'RECALL' | 'SCADUTA' | null;
  firmaConflittoVersione: boolean;
}

interface Raggruppamento {
  id: number;
  livello: 1 | 2 | 3;
  parentId: number | null;
  ordine: number;
  etichetta: string;
  descrizione: string | null;
}

interface Riga {
  id: number;
  preventivoId: number;
  raggruppamentoId: number | null;
  ordine: number;
  tipoRiga: TipoRigaPreventivo;
  alternativaGruppo: string | null;
  alternativaScelta: boolean;
  etichettaAlternativa: string | null;
  articoloId: number | null;
  codicePrecopy: string | null;
  descrizione: string;
  descrizioneEstesa: string | null;
  umVendita: string | null;
  quantita: string | null;
  prezzoUnitario: string | null;
  prezzoCalcolato: string | null;
  sconto: string | null;
  importoNetto: string | null;
  prezzoListino: string | null;
  prezzoSovrascritto: boolean;
  scontoAcquistoCodice: string | null;
  scontoAcquistoPercentuale: string | null;
  scontoVenditaCodice: string | null;
  scontoVenditaPercentuale: string | null;
  codiceIvaId: number | null;
  modalitaConfigurazione: 'DA_MODELLO' | 'DA_PRECONFIGURATO' | 'DIFFERITA_IN_ORDINE' | null;
  modelloConfigurazioneId: number | null;
  articoloPrecConfiguratoId: number | null;
  approccioCalcolo: 'NESSUNO' | 'RICARICO_SU_COSTI' | 'SCONTO_DA_LISTINO' | null;
  percentualeRicaricoRiga: string | null;
  tipoRigaCapitolato: 'BASE_CAPITOLATO' | 'EXTRA_CAPITOLATO_SOSTITUTIVO' | 'EXTRA_CAPITOLATO_AGGIUNTIVO';
  offertaCapRigaId: number | null;
  offertaCapRigaAccessoriaId: number | null;
  scorporoCapitolato: string | null;
  deltaCalcolato: string | null;
  extraAcquirenteCalcolato: string | null;
  importoImpresaCalcolato: string | null;
  // Freeze ricorrenza (Nodo 1) — valorizzati solo per tipoRiga = 'RICORRENTE'
  sottotipoRicorrenza?: SottotipoRicorrenza | null;
  periodicita?: PeriodicitaRicorrenza | null;
  durataMesi?: number | null;
  dataInizio?: string | null;
  dataFine?: string | null;
  modalitaPagamento?: ModalitaPagamentoRicorrenza | null;
  tariffaUnitaria?: string | null;
  tariffaFissaTransazione?: string | null;
  percentualeTransazione?: string | null;
  componenti: Componente[];
}

interface Componente {
  id: number;
  ordine: number;
  tipologia: string;
  descrizione: string;
  quantita: string | null;
  sfrido: string | null;
  umVendita: string | null;
  codicePrecopy: string | null;
  isPrincipale: boolean;
  costoUnitario: string | null;
  prezzoVenditaUnitario: string | null;
  scontoAcquistoCodice: string | null;
  scontoVenditaCodice: string | null;
}

interface Allegato {
  id: number;
  nome: string;
  nomeFile: string;
  mimeType: string | null;
  dimensione: number | null;
  visibileUfficioTecnico: boolean;
  stampaConPreventivo: boolean;
  uploadedAt: string;
  uploadedBy: { id: number; nome: string } | null;
}

interface AllegatoAcquirente {
  id: number;
  nome: string;
  nomeFile: string;
  mimeType: string | null;
  dimensione: number | null;
  ivaAgevolata: boolean;
  uploadedAt: string;
  uploadedBy: { id: number; nome: string } | null;
}

interface CodiceIvaOption {
  id: number;
  codice: string;
  descrizione: string;
  aliquota: string;
  attivo: boolean;
}

interface PagamentoOption {
  id: number;
  nome: string;
}

interface LogEntry {
  id: number;
  preventivoId: number;
  utenteId: number | null;
  utenteName: string | null;
  azione: string;
  dettaglio: string | null;
  createdAt: string;
}

interface AcquirenteCommessa {
  id: number;
  commessaId: number;
  unitaCodice: string;
  unitaDescrizione: string | null;
  nome: string | null;
  cognome: string | null;
  email: string | null;
  telefono: string | null;
}

type VistaRighe = 'cliente' | 'interna';
type TipoRigaCapitolato = 'BASE_CAPITOLATO' | 'EXTRA_CAPITOLATO_SOSTITUTIVO' | 'EXTRA_CAPITOLATO_AGGIUNTIVO' | 'NON_CAPITOLATO';

const TIPO_RIGA_LABEL: Record<TipoRigaPreventivo, string> = {
  ARTICOLO: 'Articolo',
  ARTICOLO_CONFIGURATO: 'Configurato',
  DESCRITTIVA: 'Descrittiva',
  A_CORPO: 'A corpo',
  RICORRENTE: 'Ricorrente',
};

const TIPO_RIGA_CSS: Record<TipoRigaPreventivo, string> = {
  ARTICOLO: 'prev-tipo-riga-label--articolo',
  ARTICOLO_CONFIGURATO: 'prev-tipo-riga-label--configurato',
  DESCRITTIVA: 'prev-tipo-riga-label--descrittiva',
  A_CORPO: 'prev-tipo-riga-label--a-corpo',
  RICORRENTE: 'prev-tipo-riga-label--ricorrente',
};

const SOTTOTIPO_RICORRENZA_LABEL: Record<SottotipoRicorrenza, string> = {
  LICENZA: 'Licenza',
  CANONE: 'Canone',
  CONSUMI: 'Consumi',
  COMMISSIONI: 'Commissioni',
};

const PERIODICITA_RICORRENZA_LABEL: Record<PeriodicitaRicorrenza, string> = {
  MENSILE: 'Mensile',
  TRIMESTRALE: 'Trimestrale',
  SEMESTRALE: 'Semestrale',
  ANNUALE: 'Annuale',
};

const MODALITA_PAGAMENTO_LABEL: Record<ModalitaPagamentoRicorrenza, string> = {
  A_PREVENTIVO: 'A preventivo',
  A_CONSUNTIVO_VOLUMI: 'A consuntivo volumi',
  A_CONSUNTIVO_TRANSAZIONI: 'A consuntivo transazioni',
};

const TIPO_CAPITOLATO_BADGE: Record<TipoRigaCapitolato, { label: string; cls: string } | null> = {
  BASE_CAPITOLATO: { label: 'BASE', cls: 'prev-badge-capitolato--base' },
  EXTRA_CAPITOLATO_SOSTITUTIVO: { label: 'SOSTITUZIONE', cls: 'prev-badge-capitolato--sostituzione' },
  EXTRA_CAPITOLATO_AGGIUNTIVO: { label: 'AGGIUNTA', cls: 'prev-badge-capitolato--aggiunta' },
  // Mai renderizzato (gating su hasCapitolato), presente solo per soddisfare il Record
  NON_CAPITOLATO: { label: '', cls: '' },
};

const TIPO_FATTURAZIONE_LABEL: Record<'IMPRESA_PAGA_TUTTO' | 'CLIENTE_PAGA_DIRETTAMENTE', string> = {
  IMPRESA_PAGA_TUTTO: 'Impresa paga tutto',
  CLIENTE_PAGA_DIRETTAMENTE: 'Cliente paga direttamente',
};

// ── Etichette ───────────────────────────────────────────────────────────────
const STATO_LABEL: Record<StatoPreventivo, string> = {
  BOZZA: 'Bozza',
  INVIATO: 'Inviato',
  ACCETTATO: 'Accettato',
  RIFIUTATO: 'Rifiutato',
  SCADUTO: 'Scaduto',
  SUPERATO: 'Superato',
  CONVERTITO: 'Convertito',
  ANNULLATO: 'Annullato',
  ANNULLATO_DOPO_CONFERMA: 'Annullato dopo conferma',
};

const STATO_CLS: Record<StatoPreventivo, string> = {
  BOZZA: 'prev-badge-stato--bozza',
  INVIATO: 'prev-badge-stato--inviato',
  ACCETTATO: 'prev-badge-stato--accettato',
  RIFIUTATO: 'prev-badge-stato--rifiutato',
  SCADUTO: 'prev-badge-stato--scaduto',
  SUPERATO: 'prev-badge-stato--superato',
  CONVERTITO: 'prev-badge-stato--convertito',
  ANNULLATO: 'prev-badge-stato--annullato',
  ANNULLATO_DOPO_CONFERMA: 'prev-badge-stato--annullato-conferma',
};

// ── Azioni di stato della testata ───────────────────────────────────────────
// Descrittore unico: da qui nascono sia i bottoni sia l'helper che li spiega
// (comprese le azioni non disponibili, con il motivo per cui non si vedono).
type AzioneStato = {
  key: string;
  label: string;
  variante: 'primary' | 'secondary' | 'danger';
  /** Cosa fa l'azione, in una frase. Usato come tooltip e nell'helper. */
  descrizione: string;
  /** false → il bottone non viene renderizzato, l'helper spiega perché. */
  disponibile: boolean;
  /** Visibile ma non cliccabile (es. conversione senza firma registrata). */
  disabilitato?: boolean;
  /** Tooltip alternativo alla descrizione (es. motivo del disabilitato). */
  titolo?: string;
  motivoNonDisponibile: string;
  onClick: () => void;
};

// ── Helper azioni ───────────────────────────────────────────────────────────
// Riga richiudibile sopra i bottoni di testata: spiega cosa fa ogni azione
// disponibile e, per quelle che non compaiono, perché non sono proponibili
// nello stato attuale del preventivo.
function HelperAzioni({ azioni, canEdit }: { azioni: AzioneStato[]; canEdit: boolean }) {
  const [aperto, setAperto] = useState(false);
  const disponibili = azioni.filter(a => a.disponibile);
  const nascoste = azioni.filter(a => !a.disponibile);

  return (
    <div className="bop-azioni-helper">
      <button
        type="button"
        className="bop-azioni-helper__toggle"
        onClick={() => setAperto(v => !v)}
        aria-expanded={aperto}
      >
        <span className="bop-azioni-helper__icona" aria-hidden="true">?</span>
        Cosa fanno questi pulsanti
        {nascoste.length > 0 && !aperto && (
          <span className="bop-azioni-helper__conteggio">
            {nascoste.length === 1
              ? '1 azione non disponibile ora'
              : `${nascoste.length} azioni non disponibili ora`}
          </span>
        )}
      </button>
      {aperto && (
        <div className="bop-azioni-helper__panel">
          {!canEdit && (
            <p className="bop-azioni-helper__nota">
              Non hai il permesso di modifica su questo preventivo: i pulsanti
              sono visibili ma non attivi.
            </p>
          )}
          {disponibili.length > 0 && (
            <>
              <div className="bop-azioni-helper__titolo">Azioni disponibili ora</div>
              <ul className="bop-azioni-helper__lista">
                {disponibili.map(a => (
                  <li key={a.key}>
                    <strong>{a.label}</strong> — {a.descrizione}
                  </li>
                ))}
              </ul>
            </>
          )}
          {nascoste.length > 0 && (
            <>
              <div className="bop-azioni-helper__titolo">Azioni non disponibili ora</div>
              <ul className="bop-azioni-helper__lista bop-azioni-helper__lista--nascoste">
                {nascoste.map(a => (
                  <li key={a.key}>
                    <strong>{a.label}</strong> — {a.motivoNonDisponibile}
                  </li>
                ))}
              </ul>
            </>
          )}
          <p className="bop-azioni-helper__nota">
            <strong>Stampa / Email</strong>, <strong>Vista JSON</strong> e{' '}
            <strong>Duplica</strong> sono sempre disponibili e non cambiano lo
            stato del preventivo (la duplicazione crea una nuova versione).
          </p>
        </div>
      )}
    </div>
  );
}

function formatDateTime(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('it-IT');
}

function toDateInput(s: string | null | undefined): string {
  if (!s) return '';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function num(s: string | null | undefined): number | null {
  if (s === null || s === undefined || s === '') return null;
  const n = typeof s === 'number' ? s : parseFloat(s);
  return isNaN(n) ? null : n;
}

// ── Modal JSON ──────────────────────────────────────────────────────────────
function ModalJson({ prev, onClose }: { prev: Preventivo; onClose: () => void }) {
  const jsonStr = useMemo(() => JSON.stringify(prev, null, 2), [prev]);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(jsonStr);
      showToast('JSON copiato', 'success');
    } catch {
      showToast('Copia non riuscita', 'error');
    }
  };
  return (
    <div className="prev-modal">
      <div className="prev-modal__box prev-modal__box--lg">
        <div className="prev-modal__header">
          <h2 className="prev-modal__title">Vista JSON</h2>
          <button className="prev-modal__close" onClick={onClose}><XIcon /></button>
        </div>
        <div className="prev-modal__body">
          <pre className="prev-json-modal-pre">{jsonStr}</pre>
        </div>
        <div className="prev-modal__footer">
          <button className="bop-btn bop-btn-secondary" onClick={handleCopy}>Copia</button>
          <button className="bop-btn bop-btn-primary" onClick={onClose}>Chiudi</button>
        </div>
      </div>
    </div>
  );
}

// ── Modal duplica preventivo ────────────────────────────────────────────────
interface DuplicaInfo {
  id: number;
  numero: string;
  versione: number;
  stato: StatoPreventivo;
  /** true se il cliente aveva già confermato (ACCETTATO/CONVERTITO) */
  richiedeAnnullamentoDopoConferma: boolean;
  documentiCollegati: {
    commessa: { id: number; numero: string; descrizione: string } | null;
    ordini: { id: number; numero: string; stato: string }[];
    proforme: { id: number; numero: string; stato: string }[];
  };
  mail: {
    disponibile: boolean;
    motivoNonDisponibile: string | null;
    mittente: { nome: string | null; email: string | null } | null;
    destinatariSuggeriti: { email: string; label: string }[];
    destinatariDefault: string[];
    oggetto: string;
    corpo: string;
  };
}

function ModalDuplicaPreventivo({
  preventivo,
  onClose,
  onDuplicated,
}: {
  preventivo: Preventivo;
  onClose: () => void;
  onDuplicated: (newId: number, newNumero: string) => void;
}) {
  const [modalita, setModalita] = useState<'COME_NUOVA_VERSIONE' | 'COME_BASE'>('COME_NUOVA_VERSIONE');
  const [saving, setSaving] = useState(false);
  const [info, setInfo] = useState<DuplicaInfo | null>(null);
  const [loading, setLoading] = useState(true);

  // Mail di avviso al cliente (solo annullamento dopo conferma)
  const [inviaMail, setInviaMail] = useState(true);
  const [destinatari, setDestinatari] = useState<string[]>([]);
  const [mailOggetto, setMailOggetto] = useState('');
  const [mailCorpo, setMailCorpo] = useState('');

  useEffect(() => {
    let annullato = false;
    (async () => {
      try {
        // @api GET /api/preventivi/:id/duplica-info -> {"success":true,"data":{"id":"number","numero":"string","versione":"number","stato":"string","richiedeAnnullamentoDopoConferma":"boolean","documentiCollegati":{"commessa":"object|null","ordini":"array","proforme":"array"},"mail":{"disponibile":"boolean","motivoNonDisponibile":"string|null","mittente":"object|null","destinatariSuggeriti":"array","destinatariDefault":"array","oggetto":"string","corpo":"string"}}}
        const r = await apiFetch<{ success: boolean; data: DuplicaInfo; error?: string }>(
          `/api/preventivi/${preventivo.id}/duplica-info`
        );
        if (annullato) return;
        if (r.success) {
          setInfo(r.data);
          setDestinatari(r.data.mail.destinatariDefault);
          setMailOggetto(r.data.mail.oggetto);
          setMailCorpo(r.data.mail.corpo);
          setInviaMail(r.data.mail.disponibile);
        } else {
          showToast(r.error || 'Errore nel caricamento delle informazioni', 'error');
        }
      } catch (e) {
        if (!annullato) showToast((e as Error).message || 'Errore nel caricamento', 'error');
      } finally {
        if (!annullato) setLoading(false);
      }
    })();
    return () => { annullato = true; };
  }, [preventivo.id]);

  // L'annullamento dopo conferma scatta solo scegliendo "nuova versione"
  const annullaDopoConferma = !!info?.richiedeAnnullamentoDopoConferma && modalita === 'COME_NUOVA_VERSIONE';
  const mailAttiva = annullaDopoConferma && inviaMail && !!info?.mail.disponibile;
  const mailIncompleta = mailAttiva && (!destinatari.length || !mailOggetto.trim() || !mailCorpo.trim());

  const toggleDestinatario = (email: string) => {
    setDestinatari(prev => prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]);
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      // @api POST /api/preventivi/:id/duplica {"modalita":"COME_NUOVA_VERSIONE|COME_BASE","inviaMailAnnullamento":"boolean?","mailDestinatari":"string[]?","mailOggetto":"string?","mailCorpo":"string?"} -> {"success":true,"data":{"id":"number","numero":"string","annullatoDopoConferma":"boolean","statoOrigine":"string","mailAnnullamento":"object|null"}}
      const r = await apiFetch<{
        success: boolean;
        data: {
          id: number; numero: string;
          annullatoDopoConferma: boolean;
          statoOrigine: string;
          mailAnnullamento: { inviata: boolean; errore: string | null; destinatari: string[] } | null;
        };
        error?: string;
      }>(
        `/api/preventivi/${preventivo.id}/duplica`,
        {
          method: 'POST',
          body: JSON.stringify({
            modalita,
            ...(annullaDopoConferma ? {
              inviaMailAnnullamento: mailAttiva,
              mailDestinatari: destinatari,
              mailOggetto,
              mailCorpo,
            } : {}),
          }),
        }
      );
      if (r.success) {
        if (r.data.annullatoDopoConferma) {
          if (r.data.mailAnnullamento?.inviata) {
            showToast(`Preventivo annullato dopo conferma — mail inviata a ${r.data.mailAnnullamento.destinatari.join(', ')}`, 'success');
          } else if (r.data.mailAnnullamento) {
            showToast(`Preventivo annullato dopo conferma, ma la mail NON è stata inviata: ${r.data.mailAnnullamento.errore}`, 'error');
          } else {
            showToast('Preventivo annullato dopo conferma — nessuna mail inviata', 'success');
          }
        } else {
          showToast('Preventivo duplicato', 'success');
        }
        onDuplicated(r.data.id, r.data.numero);
      } else {
        showToast(r.error || 'Errore duplicazione', 'error');
      }
    } catch (e) {
      showToast((e as Error).message || 'Errore duplicazione', 'error');
    } finally {
      setSaving(false);
    }
  };

  const docs = info?.documentiCollegati;
  const haDocumenti = !!docs && (docs.ordini.length > 0 || !!docs.commessa || docs.proforme.length > 0);

  return (
    <div className="prev-modal">
      <div className="prev-modal__box prev-modal__box--lg">
        <div className="prev-modal__header">
          <h2 className="prev-modal__title">Duplica preventivo {preventivo.numero}</h2>
          <button className="prev-modal__close" onClick={onClose}><XIcon /></button>
        </div>
        <div className="prev-modal__body">
          {loading && <p className="mdup-intro">Caricamento…</p>}

          {!loading && info?.richiedeAnnullamentoDopoConferma && (
            <div className="mdup-alert">
              <div className="mdup-alert__title">
                Questo preventivo è già stato confermato dal cliente (stato: {STATO_LABEL[info.stato]})
              </div>
              <p className="mdup-alert__text">
                Rifarlo come nuova versione significa <strong>disdire un impegno già accettato</strong>:
                per questo il preventivo attuale non viene marcato come “superato”, ma passa allo stato
                <strong> ANNULLATO DOPO CONFERMA</strong> e il cliente viene avvisato via email.
              </p>
            </div>
          )}

          {!loading && (
            <>
              <p className="mdup-intro">Scegli come duplicare:</p>

              <div className={`mdup-option${modalita === 'COME_NUOVA_VERSIONE' ? ' mdup-option--sel' : ''}`}>
                <label className="mdup-option__label">
                  <input
                    type="radio"
                    name="modalita-duplica"
                    value="COME_NUOVA_VERSIONE"
                    checked={modalita === 'COME_NUOVA_VERSIONE'}
                    onChange={() => setModalita('COME_NUOVA_VERSIONE')}
                  />
                  <strong>
                    {info?.richiedeAnnullamentoDopoConferma
                      ? 'Come nuova versione — annulla il preventivo confermato'
                      : 'Come nuova versione (raccomandato)'}
                  </strong>
                </label>
                {info?.richiedeAnnullamentoDopoConferma ? (
                  <ul className="mdup-option__bullets">
                    <li>Il preventivo {preventivo.numero} passa a <strong>ANNULLATO DOPO CONFERMA</strong> e non sarà più modificabile né riattivabile.</li>
                    <li>Al cliente viene inviata <strong>automaticamente una email</strong> che comunica l’annullamento dell’offerta accettata (testo modificabile qui sotto).</li>
                    <li>La nuova copia nasce in BOZZA come <strong>versione {(info.versione || 1) + 1}</strong> e mantiene il collegamento alla storia.</li>
                    <li>Ordini, commessa e proforme già generati <strong>restano attivi</strong>: vanno gestiti a mano (vedi sotto).</li>
                  </ul>
                ) : (
                  <ul className="mdup-option__bullets">
                    <li>Il preventivo attuale verrà marcato come SUPERATO e non sarà più modificabile.</li>
                    <li>La nuova copia erediterà la storia.</li>
                    <li>Utile quando hai ricevuto modifiche dal cliente e devi rifare il preventivo.</li>
                  </ul>
                )}
              </div>

              <div className={`mdup-option${modalita === 'COME_BASE' ? ' mdup-option--sel' : ''}`}>
                <label className="mdup-option__label">
                  <input
                    type="radio"
                    name="modalita-duplica"
                    value="COME_BASE"
                    checked={modalita === 'COME_BASE'}
                    onChange={() => setModalita('COME_BASE')}
                  />
                  <strong>Come base per nuovo preventivo</strong>
                </label>
                <ul className="mdup-option__bullets">
                  <li>
                    Il preventivo attuale resta intatto
                    {info?.richiedeAnnullamentoDopoConferma
                      ? ` (rimane ${STATO_LABEL[info.stato]}): nessun annullamento, nessuna email al cliente.`
                      : '.'}
                  </li>
                  <li>La copia è indipendente, senza storia, e riparte dalla versione 1.</li>
                  <li>Utile come template per preventivi simili.</li>
                </ul>
              </div>

              {/* ── Mail di avviso al cliente ─────────────────────────── */}
              {annullaDopoConferma && (
                <div className="mdup-mail">
                  <div className="mdup-mail__head">
                    <label className="mdup-option__label">
                      <input
                        type="checkbox"
                        checked={inviaMail && info!.mail.disponibile}
                        disabled={!info!.mail.disponibile}
                        onChange={e => setInviaMail(e.target.checked)}
                      />
                      <strong>Invia al cliente la email di avviso annullamento</strong>
                    </label>
                    {info!.mail.mittente?.email && (
                      <span className="mdup-mail__from">
                        Mittente: {info!.mail.mittente!.nome ? `${info!.mail.mittente!.nome} — ` : ''}{info!.mail.mittente!.email}
                      </span>
                    )}
                  </div>

                  {!info!.mail.disponibile && (
                    <div className="mdup-warn">{info!.mail.motivoNonDisponibile}</div>
                  )}

                  {info!.mail.disponibile && inviaMail && (
                    <>
                      <div className="bop-form-group">
                        <label className="bop-label">Destinatari</label>
                        <div className="mdup-mail__dest">
                          {info!.mail.destinatariSuggeriti.map(d => (
                            <label key={d.email} className="mdup-mail__dest-item">
                              <input
                                type="checkbox"
                                checked={destinatari.includes(d.email)}
                                onChange={() => toggleDestinatario(d.email)}
                              />
                              <span>{d.email}</span>
                              <span className="bop-text-muted">• {d.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      <div className="bop-form-group">
                        <label className="bop-label">Oggetto</label>
                        <input
                          className="bop-input"
                          value={mailOggetto}
                          onChange={e => setMailOggetto(e.target.value)}
                        />
                      </div>

                      <div className="bop-form-group">
                        <label className="bop-label">Testo della email</label>
                        <textarea
                          className="bop-input mdup-mail__corpo"
                          rows={10}
                          value={mailCorpo}
                          onChange={e => setMailCorpo(e.target.value)}
                        />
                      </div>

                      {mailIncompleta && (
                        <div className="mdup-warn">
                          Indica almeno un destinatario e compila oggetto e testo, oppure disattiva l’invio.
                        </div>
                      )}
                    </>
                  )}

                  {info!.mail.disponibile && !inviaMail && (
                    <div className="mdup-warn">
                      Nessuna email verrà inviata: dovrai comunicare l’annullamento al cliente per altra via.
                    </div>
                  )}
                </div>
              )}

              {/* ── Documenti già generati ────────────────────────────── */}
              {annullaDopoConferma && haDocumenti && (
                <div className="mdup-warn-box">
                  <div className="mdup-info__title">Documenti già generati — restano attivi</div>
                  <ul className="mdup-info__list">
                    {docs!.commessa && <li>• Commessa {docs!.commessa.numero} — {docs!.commessa.descrizione}</li>}
                    {docs!.ordini.map(o => <li key={o.id}>• Ordine cliente {o.numero} ({o.stato})</li>)}
                    {docs!.proforme.map(p => <li key={p.id}>• Proforma acconto {p.numero} ({p.stato})</li>)}
                  </ul>
                  <p className="mdup-alert__text">
                    L’annullamento riguarda solo il preventivo. Questi documenti <strong>non vengono toccati</strong>:
                    valuta tu se chiuderli, rettificarli o lasciarli in attesa della nuova versione.
                  </p>
                </div>
              )}

              <div className="mdup-divider" />

              <div className="mdup-info">
                <div className="mdup-info__title">Cosa viene copiato in entrambi i casi:</div>
                <ul className="mdup-info__list">
                  <li>✓ Testata (oggetto, cliente, commessa, ecc.)</li>
                  <li>✓ Raggruppamenti e righe</li>
                  <li>✓ Componenti configurati</li>
                  <li>✓ Capitolato di riferimento e freeze</li>
                  <li>✗ Log, firma, ordini generati, data conversione</li>
                </ul>
              </div>
            </>
          )}
        </div>
        <div className="prev-modal__footer">
          <button className="bop-btn bop-btn-secondary" onClick={onClose} disabled={saving}>Annulla</button>
          <button
            className={`bop-btn ${annullaDopoConferma ? 'bop-btn-danger' : 'bop-btn-primary'}`}
            onClick={handleSubmit}
            disabled={saving || loading || mailIncompleta}
          >
            {saving
              ? 'Duplicazione...'
              : annullaDopoConferma
                ? 'Invalida preventivo e crea nuova versione'
                : 'Duplica e apri nuovo'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modal converti preventivo ───────────────────────────────────────────────
interface OrdineCreato {
  id: number;
  numero: string;
  destinatarioOrdine: string | null;
  importoTotale: number;
}

interface OrdinePreviewAcconto {
  percentuale: number;
  imponibile: number;
  iva: number;
  totale: number;
  saldo: number;
  condizionePagamento: string | null;
}

interface OrdinePreview {
  etichetta: string;
  destinatario: 'IMPRESA' | 'ACQUIRENTE' | null;
  lordo: number;
  scontoImporto: number;
  netto: number;
  iva: number;
  totale: number;
  nRighe: number;
  isSoloImporto?: boolean;
  acconto?: OrdinePreviewAcconto | null;
}

interface CommessaOption {
  id: number;
  numero: string;
  descrizione: string;
}

function ModalConvertiPreventivo({
  preventivo,
  onClose,
  onConverted,
}: {
  preventivo: Preventivo;
  onClose: () => void;
  onConverted: (commessaId: number, ordini: OrdineCreato[]) => void;
}) {
  const [dataOrdine, setDataOrdine] = useState<string>(new Date().toISOString().slice(0, 10));
  const [usaCommessaEsistente, setUsaCommessaEsistente] = useState<boolean>(false);
  const [commessaIdTarget, setCommessaIdTarget] = useState<number | null>(null);
  const [commesseDisponibili, setCommesseDisponibili] = useState<CommessaOption[]>([]);
  const [saving, setSaving] = useState(false);

  const isConfig = preventivo.tipo === 'CONFIGURATO_COMMESSA';

  // Carica le commesse del cliente del preventivo (solo SEMPLICE, per la scelta
  // "aggancia a esistente"). Una commessa è legata 1=1 a un nominativo-cliente:
  // l'elenco DEVE essere filtrato per cliente, mai proporre tutte le commesse
  // della società (altrimenti si aggancia un ordine a una commessa di un
  // cliente diverso da quello del preventivo).
  useEffect(() => {
    if (isConfig) return;
    (async () => {
      try {
        // @api GET /api/preventivi/:id/commesse-cliente -> {"success":true,"data":[{"id":"number","numero":"string","descrizione":"string"}]}
        const r = await apiFetch<{ success: boolean; data: CommessaOption[] }>(
          `/api/preventivi/${preventivo.id}/commesse-cliente`
        );
        setCommesseDisponibili(r.data || []);
      } catch {
        setCommesseDisponibili([]);
      }
    })();
  }, [isConfig, preventivo.id]);

  // Tutte le righe che diventeranno righe d'ordine: incluse le DESCRITTIVE
  // (il backend le propaga sull'ordine), escluse solo le alternative non scelte.
  const righeOrdine = useMemo(
    () => preventivo.righe.filter(r => !r.alternativaGruppo || r.alternativaScelta),
    [preventivo.righe]
  );

  const aliquotaIva = parseFloat(preventivo.codiceIvaDefault?.aliquota || '0');

  const ordiniPreview = useMemo<OrdinePreview[]>(() => {
    const calcImporto = (r: Riga, prezzoOverride?: number) => {
      const q = parseFloat(r.quantita || '0');
      const sc = parseFloat(r.sconto || '0');
      const p = prezzoOverride != null ? prezzoOverride : parseFloat(r.prezzoUnitario || '0');
      return p * q * (1 - sc / 100);
    };
    const tipoSconto = preventivo.scontoGlobaleTipo;
    const valoreSconto = preventivo.scontoGlobaleValore != null
      ? parseFloat(preventivo.scontoGlobaleValore) || 0
      : 0;
    // Sconto globale come importo assoluto, calcolato su una base lorda.
    const scontoSu = (lordo: number): number => {
      if (tipoSconto === 'IMPORTO' && valoreSconto) return valoreSconto;
      if (tipoSconto === 'PERCENTUALE' && valoreSconto) return lordo * valoreSconto / 100;
      return 0;
    };
    // Breakdown ricostruito a partire dal netto di testata: garantisce che i
    // numeri del modale coincidano esattamente con i totali del preventivo.
    const breakdownDaNetto = (netto: number, iva: number, totale: number) => {
      let scontoImporto = 0;
      let lordo = netto;
      if (tipoSconto === 'IMPORTO' && valoreSconto) {
        scontoImporto = valoreSconto;
        lordo = netto + valoreSconto;
      } else if (tipoSconto === 'PERCENTUALE' && valoreSconto) {
        const f = 1 - valoreSconto / 100;
        lordo = f > 0 ? netto / f : netto;
        scontoImporto = lordo - netto;
      }
      return { lordo, scontoImporto, netto, iva, totale };
    };

    // Acconto di un ramo, ricavato dal totaliDettaglio del preventivo
    // (stessa fonte usata dalla sezione TOTALI e dalla conversione).
    const accontoFrom = (ramo: TotaliRamoPreventivo | null): OrdinePreviewAcconto | null => {
      if (!ramo || !ramo.acconto) return null;
      return {
        percentuale: ramo.acconto.percentuale,
        imponibile: ramo.acconto.imponibile,
        iva: ramo.acconto.iva,
        totale: ramo.acconto.totale,
        saldo: ramo.saldoTotale,
        condizionePagamento: ramo.acconto.condizionePagamento?.nome ?? null,
      };
    };
    const td = preventivo.totaliDettaglio;

    const nettoTestata = num(preventivo.totaleImponibileCliente) ?? 0;
    const ivaTestata = num(preventivo.totaleIvaCliente) ?? 0;
    const totaleTestata = num(preventivo.totaleCliente) ?? 0;

    if (preventivo.tipo === 'SEMPLICE') {
      return [{
        etichetta: 'Ordine cliente',
        destinatario: null,
        ...breakdownDaNetto(nettoTestata, ivaTestata, totaleTestata),
        nRighe: righeOrdine.length,
        acconto: accontoFrom(td.impresa),
      }];
    }
    const tipoFatt = preventivo.tipoFatturazioneFreeze;
    // "Fuori capitolato": CLIENTE_PAGA_DIRETT senza acquirente -> stesso comportamento di IMPRESA_PAGA_TUTTO
    const fuoriCapitolato = tipoFatt === 'CLIENTE_PAGA_DIRETTAMENTE' && !preventivo.acquirenteId;
    if (tipoFatt === 'IMPRESA_PAGA_TUTTO' || !tipoFatt || fuoriCapitolato) {
      const etichetta = fuoriCapitolato
        ? 'Ordine impresa (fuori capitolato)'
        : 'Ordine impresa (tradizionale)';
      return [{
        etichetta,
        destinatario: 'IMPRESA',
        ...breakdownDaNetto(nettoTestata, ivaTestata, totaleTestata),
        nRighe: righeOrdine.length,
        acconto: accontoFrom(td.impresa),
      }];
    }
    // CLIENTE_PAGA_DIRETTAMENTE con acquirente — split impresa / acquirente
    const lordoCliente = righeOrdine.reduce((s, r) => s + calcImporto(r), 0);
    let totImpresa = 0;
    let totAcq = 0;
    for (const r of righeOrdine) {
      if (r.importoImpresaCalcolato != null) {
        totImpresa += calcImporto(r, parseFloat(r.importoImpresaCalcolato));
        totAcq += calcImporto(r, parseFloat(r.extraAcquirenteCalcolato || '0'));
      } else {
        totImpresa += calcImporto(r);
      }
    }
    const scontoImpresa = scontoSu(lordoCliente);
    const nettoImpresa = totImpresa - scontoImpresa;
    const ivaImpresa = nettoImpresa * aliquotaIva / 100;
    const ordini: OrdinePreview[] = [{
      etichetta: 'Ordine impresa',
      destinatario: 'IMPRESA',
      lordo: totImpresa,
      scontoImporto: scontoImpresa,
      netto: nettoImpresa,
      iva: ivaImpresa,
      totale: nettoImpresa + ivaImpresa,
      nRighe: righeOrdine.length,
      isSoloImporto: false,
      acconto: accontoFrom(td.impresa),
    }];
    if (totAcq > 0) {
      ordini.push({
        etichetta: 'Ordine acquirente (solo importo)',
        destinatario: 'ACQUIRENTE',
        lordo: totAcq,
        scontoImporto: 0,
        netto: totAcq,
        iva: td.acquirente?.iva ?? 0,
        totale: totAcq,
        nRighe: 1,
        isSoloImporto: true,
        acconto: accontoFrom(td.acquirente),
      });
    }
    return ordini;
  }, [preventivo, righeOrdine, aliquotaIva]);

  const statoValido = preventivo.stato === 'ACCETTATO' || preventivo.stato === 'INVIATO';
  // Caso "fuori capitolato": preventivo CLIENTE_PAGA_DIRETT senza acquirente.
  // Non blocca la conversione, ma cambia il comportamento (1 ordine impresa, no split).
  const fuoriCapitolato =
    isConfig &&
    preventivo.tipoFatturazioneFreeze === 'CLIENTE_PAGA_DIRETTAMENTE' &&
    !preventivo.acquirenteId;

  const commessaNonScelta = !isConfig && !preventivo.commessaId && usaCommessaEsistente && !commessaIdTarget;

  const disabled = !statoValido || commessaNonScelta || saving;
  const tooltip = !statoValido
    ? `Il preventivo deve essere in stato ACCETTATO o INVIATO (stato corrente: ${preventivo.stato})`
    : commessaNonScelta
      ? 'Seleziona una commessa esistente'
      : fuoriCapitolato
        ? 'Verrà creato un solo ordine all\'impresa (preventivo fuori capitolato).'
        : undefined;

  const handleSubmit = async () => {
    if (disabled) return;
    setSaving(true);
    try {
      // @api POST /api/preventivi/:id/converti {"dataOrdine":"string","commessaIdTarget":"number|null"} -> {"success":true,"data":{"commessaId":"number","commessaNumero":"string","ordini":[{"id":"number","numero":"string","destinatarioOrdine":"string|null","importoTotale":"number"}]}}
      const r = await apiFetch<{
        success: boolean;
        data: { commessaId: number; commessaNumero: string; ordini: OrdineCreato[] };
        error?: string;
      }>(`/api/preventivi/${preventivo.id}/converti`, {
        method: 'POST',
        body: JSON.stringify({
          dataOrdine,
          commessaIdTarget: usaCommessaEsistente ? commessaIdTarget : null,
        }),
      });
      if (r.success) {
        showToast(`Creati ${r.data.ordini.length} ordine/i`, 'success');
        onConverted(r.data.commessaId, r.data.ordini);
      } else {
        showToast(r.error || 'Errore conversione', 'error');
      }
    } catch (e) {
      showToast((e as Error).message || 'Errore conversione', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="prev-modal">
      <div className="prev-modal__box prev-modal__box--lg">
        <div className="prev-modal__header">
          <h2 className="prev-modal__title">Converti preventivo {preventivo.numero} in ordine/i</h2>
          <button className="prev-modal__close" onClick={onClose}><XIcon /></button>
        </div>
        <div className="prev-modal__body">
          {/* Riepilogo */}
          <div className="mconv-section">
            <div className="mconv-section-title">Riepilogo</div>
            <div className="mconv-riepilogo-grid">
              <div className="mconv-riepilogo-grid__label">Tipo:</div>
              <div className="mconv-riepilogo-grid__value">{preventivo.tipo}</div>
              <div className="mconv-riepilogo-grid__label">Commessa:</div>
              <div className="mconv-riepilogo-grid__value">
                {preventivo.commessa
                  ? `${preventivo.commessa.numero} — ${preventivo.commessa.descrizione}`
                  : '— (da assegnare)'}
              </div>
              {isConfig && (
                <>
                  <div className="mconv-riepilogo-grid__label">Capitolato:</div>
                  <div className="mconv-riepilogo-grid__value">
                    {preventivo.offertaCapitolato
                      ? `${preventivo.offertaCapitolato.codice} — Ricarico ${preventivo.ricaricoImpresaFreeze ?? preventivo.offertaCapitolato.ricarico ?? '0'}%`
                      : '—'}
                  </div>
                  <div className="mconv-riepilogo-grid__label">Fattur.:</div>
                  <div className="mconv-riepilogo-grid__value">
                    {preventivo.tipoFatturazioneFreeze
                      ? TIPO_FATTURAZIONE_LABEL[preventivo.tipoFatturazioneFreeze]
                      : '—'}
                  </div>
                  <div className="mconv-riepilogo-grid__label">Acquirente:</div>
                  <div className="mconv-riepilogo-grid__value">
                    {preventivo.acquirente
                      ? `${preventivo.acquirente.unitaCodice} — ${[preventivo.acquirente.nome, preventivo.acquirente.cognome].filter(Boolean).join(' ')}`
                      : '— (non impostato)'}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Warning */}
          <div className="mconv-warning-banner">
            <div className="mconv-warning-banner__title">Al momento della conversione:</div>
            <ul className="mconv-warning-banner__list">
              <li>Il preventivo passerà in stato CONVERTITO</li>
              <li>Non sarà più modificabile</li>
              <li>Se vorrai modificarlo dovrai duplicarlo</li>
            </ul>
          </div>

          {/* Banner errore stato */}
          {!statoValido && (
            <div className="mconv-error-banner">
              Il preventivo deve essere in stato ACCETTATO o INVIATO (stato corrente: {preventivo.stato})
            </div>
          )}

          {/* Banner info "fuori capitolato" */}
          {fuoriCapitolato && (
            <div className="mconv-info-banner">
              Preventivo "fuori capitolato": verrà creato un solo ordine all'impresa, senza split verso l'acquirente.
            </div>
          )}

          {/* Anteprima ordini */}
          <div className="mconv-section">
            <div className="mconv-section-title">Ordini che verranno creati</div>
            <div className="mconv-ordini-list">
              {ordiniPreview.map((o, i) => (
                <div key={i} className="mconv-ordine-card">
                  <div className="mconv-ordine-card__title">Ordine {i + 1} — {o.etichetta}</div>
                  <div className="mconv-ordine-card__row">
                    <span className="mconv-ordine-card__label">Righe:</span>
                    <span className="mconv-ordine-card__value">
                      {o.nRighe}{o.isSoloImporto ? ' (extra capitolato)' : ''}
                    </span>
                  </div>
                  {o.isSoloImporto ? (
                    <div className="mconv-ordine-card__row mconv-ordine-card__row--totale">
                      <span className="mconv-ordine-card__label">Importo:</span>
                      <span className={`mconv-ordine-card__value ${o.totale < 0 ? 'mconv-ordine-importo--negativo' : ''}`}>
                        € {formatCurrency(o.totale)}
                      </span>
                    </div>
                  ) : (
                    <>
                      <div className="mconv-ordine-card__row">
                        <span className="mconv-ordine-card__label">Imponibile lordo:</span>
                        <span className="mconv-ordine-card__value">€ {formatCurrency(o.lordo)}</span>
                      </div>
                      {o.scontoImporto !== 0 && (
                        <div className="mconv-ordine-card__row">
                          <span className="mconv-ordine-card__label">Sconto globale:</span>
                          <span className="mconv-ordine-card__value">− € {formatCurrency(o.scontoImporto)}</span>
                        </div>
                      )}
                      <div className="mconv-ordine-card__row">
                        <span className="mconv-ordine-card__label">Imponibile netto:</span>
                        <span className={`mconv-ordine-card__value ${o.netto < 0 ? 'mconv-ordine-importo--negativo' : ''}`}>
                          € {formatCurrency(o.netto)}
                        </span>
                      </div>
                      <div className="mconv-ordine-card__row">
                        <span className="mconv-ordine-card__label">IVA (al {formatNumber(aliquotaIva)}%):</span>
                        <span className="mconv-ordine-card__value">€ {formatCurrency(o.iva)}</span>
                      </div>
                      <div className="mconv-ordine-card__row mconv-ordine-card__row--totale">
                        <span className="mconv-ordine-card__label">Totale:</span>
                        <span className={`mconv-ordine-card__value ${o.totale < 0 ? 'mconv-ordine-importo--negativo' : ''}`}>
                          € {formatCurrency(o.totale)}
                        </span>
                      </div>
                    </>
                  )}
                  {o.acconto && (
                    <div className="mconv-ordine-card__acconto">
                      <div className="mconv-ordine-card__acconto-title">
                        Acconto previsto ({formatNumber(o.acconto.percentuale)}%)
                      </div>
                      <div className="mconv-ordine-card__row">
                        <span className="mconv-ordine-card__label">Imponibile acconto:</span>
                        <span className="mconv-ordine-card__value">€ {formatCurrency(o.acconto.imponibile)}</span>
                      </div>
                      <div className="mconv-ordine-card__row">
                        <span className="mconv-ordine-card__label">IVA acconto:</span>
                        <span className="mconv-ordine-card__value">€ {formatCurrency(o.acconto.iva)}</span>
                      </div>
                      <div className="mconv-ordine-card__row mconv-ordine-card__row--totale">
                        <span className="mconv-ordine-card__label">Totale acconto:</span>
                        <span className="mconv-ordine-card__value">€ {formatCurrency(o.acconto.totale)}</span>
                      </div>
                      <div className="mconv-ordine-card__row">
                        <span className="mconv-ordine-card__label">Saldo residuo:</span>
                        <span className="mconv-ordine-card__value">€ {formatCurrency(o.acconto.saldo)}</span>
                      </div>
                      {o.acconto.condizionePagamento && (
                        <div className="mconv-ordine-card__row">
                          <span className="mconv-ordine-card__label">Pagamento acconto:</span>
                          <span className="mconv-ordine-card__value">{o.acconto.condizionePagamento}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {ordiniPreview.some(o => o.acconto) && (
              <div className="mconv-info-banner">
                Per ogni ordine con acconto previsto verrà generata automaticamente una
                proforma-acconto da incassare, con la relativa condizione di pagamento.
              </div>
            )}
          </div>

          {/* Data ordine */}
          <div className="bop-form-group">
            <label className="bop-label">Data ordine</label>
            <input
              type="date"
              className="bop-input"
              value={dataOrdine}
              onChange={e => setDataOrdine(e.target.value)}
            />
          </div>

          {/* Scelta commessa solo per SEMPLICE senza commessaId */}
          {!isConfig && !preventivo.commessaId && (
            <div className="mconv-commessa-scelta">
              <div className="mconv-section-title">Commessa di destinazione</div>
              <label className="mconv-radio-option">
                <input
                  type="radio"
                  name="scelta-commessa"
                  checked={usaCommessaEsistente}
                  onChange={() => setUsaCommessaEsistente(true)}
                />
                <span>Aggancia a commessa esistente</span>
              </label>
              {usaCommessaEsistente && (
                commesseDisponibili.length > 0 ? (
                  <select
                    className="bop-select"
                    value={commessaIdTarget ?? ''}
                    onChange={e => setCommessaIdTarget(e.target.value ? parseInt(e.target.value, 10) : null)}
                  >
                    <option value="">— Seleziona una commessa —</option>
                    {commesseDisponibili.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.numero} — {c.descrizione}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="bop-text-muted">
                    Il cliente del preventivo non ha commesse esistenti: scegli "Crea nuova commessa placeholder".
                  </div>
                )
              )}
              <label className="mconv-radio-option">
                <input
                  type="radio"
                  name="scelta-commessa"
                  checked={!usaCommessaEsistente}
                  onChange={() => setUsaCommessaEsistente(false)}
                />
                <span>Crea nuova commessa placeholder</span>
              </label>
            </div>
          )}
        </div>
        <div className="prev-modal__footer">
          <button className="bop-btn bop-btn-secondary" onClick={onClose} disabled={saving}>Annulla</button>
          <button
            className="bop-btn bop-btn-primary"
            onClick={handleSubmit}
            disabled={disabled}
            title={tooltip}
          >
            {saving ? 'Conversione...' : 'Converti in ordini'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modal aggiungi riga ─────────────────────────────────────────────────────
interface NuovaRigaDraft {
  tipoRiga: TipoRigaPreventivo;
  raggruppamentoId: number | null;
  descrizione: string;
  descrizioneEstesa: string;
  umVendita: string;
  quantita: string;
  prezzoUnitario: string;
  sconto: string;
  // null = riga indipendente; -1 = nuova alternativa (nuovo gruppo dal backend);
  // >0 = id riga target di un cluster esistente
  alternativaTargetRigaId: number | null;
  etichettaAlternativa: string;
}

function ModalNuovaRiga({
  raggruppamenti,
  defaultRaggruppamentoId,
  righeEsistenti,
  onClose,
  onSave,
}: {
  raggruppamenti: Raggruppamento[];
  defaultRaggruppamentoId: number | null;
  righeEsistenti: Riga[];
  onClose: () => void;
  onSave: (d: NuovaRigaDraft) => Promise<void>;
}) {
  const [draft, setDraft] = useState<NuovaRigaDraft>({
    tipoRiga: 'ARTICOLO',
    raggruppamentoId: defaultRaggruppamentoId,
    descrizione: '',
    descrizioneEstesa: '',
    umVendita: '',
    quantita: '1',
    prezzoUnitario: '',
    sconto: '',
    alternativaTargetRigaId: null,
    etichettaAlternativa: '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!draft.descrizione.trim()) {
      showToast('Descrizione obbligatoria', 'error');
      return;
    }
    setSaving(true);
    try {
      await onSave(draft);
      onClose();
    } catch (e) {
      showToast((e as Error).message || 'Errore creazione riga', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="prev-modal">
      <div className="prev-modal__box">
        <div className="prev-modal__header">
          <h2 className="prev-modal__title">Aggiungi riga</h2>
          <button className="prev-modal__close" onClick={onClose}><XIcon /></button>
        </div>
        <div className="prev-modal__body">
          <div className="bop-form-group">
            <label className="bop-label">Tipo riga</label>
            <select
              className="bop-select"
              value={draft.tipoRiga}
              onChange={e => setDraft({ ...draft, tipoRiga: e.target.value as TipoRigaPreventivo })}
            >
              <option value="ARTICOLO">Articolo</option>
              <option value="DESCRITTIVA">Descrittiva</option>
            </select>
          </div>
          <div className="bop-form-group">
            <label className="bop-label">Raggruppamento</label>
            <select
              className="bop-select"
              value={draft.raggruppamentoId ?? ''}
              onChange={e => setDraft({ ...draft, raggruppamentoId: e.target.value ? parseInt(e.target.value, 10) : null })}
            >
              <option value="">— Nessuno —</option>
              {raggruppamenti.map(r => (
                <option key={r.id} value={r.id}>{r.etichetta}</option>
              ))}
            </select>
          </div>
          <div className="bop-form-group">
            <label className="bop-label">Descrizione *</label>
            <input
              className="bop-input"
              value={draft.descrizione}
              onChange={e => setDraft({ ...draft, descrizione: e.target.value })}
              autoFocus
            />
          </div>
          <div className="bop-form-group">
            <label className="bop-label">Descrizione estesa</label>
            <textarea
              className="bop-input"
              rows={2}
              value={draft.descrizioneEstesa}
              onChange={e => setDraft({ ...draft, descrizioneEstesa: e.target.value })}
            />
          </div>
          {draft.tipoRiga !== 'DESCRITTIVA' && (
            <div className="bop-form-row">
              <div className="bop-form-group">
                <label className="bop-label">U.M.</label>
                <input
                  className="bop-input"
                  value={draft.umVendita}
                  onChange={e => setDraft({ ...draft, umVendita: e.target.value })}
                  placeholder="es. nr, mq, h"
                />
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Quantità</label>
                <input
                  className="bop-input"
                  type="number"
                  step="0.01"
                  value={draft.quantita}
                  onChange={e => setDraft({ ...draft, quantita: e.target.value })}
                />
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Prezzo unitario</label>
                <input
                  className="bop-input"
                  type="number"
                  step="0.01"
                  value={draft.prezzoUnitario}
                  onChange={e => setDraft({ ...draft, prezzoUnitario: e.target.value })}
                />
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Sconto %</label>
                <input
                  className="bop-input"
                  type="number"
                  step="0.01"
                  value={draft.sconto}
                  onChange={e => setDraft({ ...draft, sconto: e.target.value })}
                />
              </div>
            </div>
          )}
          {draft.raggruppamentoId && draft.tipoRiga !== 'DESCRITTIVA' && (
            <div className="prev-modal__section">
              <h4 className="prev-modal__section-title">
                Alternativa <span className="bop-text-muted">(opzionale)</span>
              </h4>
              <div className="bop-form-group">
                <label className="bop-label">Alternativa di</label>
                <select
                  className="bop-select"
                  value={
                    draft.alternativaTargetRigaId == null
                      ? ''
                      : draft.alternativaTargetRigaId === -1
                        ? '__nuova__'
                        : String(draft.alternativaTargetRigaId)
                  }
                  onChange={e => {
                    const v = e.target.value;
                    if (v === '') setDraft({ ...draft, alternativaTargetRigaId: null });
                    else if (v === '__nuova__') setDraft({ ...draft, alternativaTargetRigaId: -1 });
                    else setDraft({ ...draft, alternativaTargetRigaId: parseInt(v, 10) });
                  }}
                >
                  <option value="">— Riga indipendente —</option>
                  <option value="__nuova__">+ Nuova alternativa (nuovo gruppo)</option>
                  {righeEsistenti
                    .filter(r =>
                      r.raggruppamentoId === draft.raggruppamentoId &&
                      r.tipoRiga !== 'DESCRITTIVA'
                    )
                    .map(r => {
                      const label = r.alternativaGruppo
                        ? `Aggiungi al Gruppo ${r.alternativaGruppo} — ${r.descrizione}`
                        : `Crea cluster con: ${r.descrizione}`;
                      return <option key={r.id} value={r.id}>{label}</option>;
                    })}
                </select>
                <div className="prev-modal__hint">
                  La nuova riga verrà inserita come "non scelta": potrai cambiare la scelta dal radio in tabella.
                </div>
              </div>
              {draft.alternativaTargetRigaId != null && (
                <div className="bop-form-group">
                  <label className="bop-label">Etichetta alternativa <span className="bop-text-muted">(opzionale)</span></label>
                  <input
                    className="bop-input"
                    value={draft.etichettaAlternativa}
                    onChange={e => setDraft({ ...draft, etichettaAlternativa: e.target.value })}
                    placeholder="es. economica, premium, wood"
                  />
                </div>
              )}
            </div>
          )}
        </div>
        <div className="prev-modal__footer">
          <button className="bop-btn bop-btn-secondary" onClick={onClose} disabled={saving}>Annulla</button>
          <button className="bop-btn bop-btn-primary" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Salvataggio...' : 'Aggiungi'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modal "Rendi alternativa di…" ───────────────────────────────────────────
function ModalRendiAlternativa({
  riga,
  righeRaggruppamento,
  onClose,
  onSave,
}: {
  riga: Riga;
  righeRaggruppamento: Riga[];
  onClose: () => void;
  onSave: (rigaTargetId: number, etichetta: string) => Promise<void>;
}) {
  const [targetId, setTargetId] = useState<number | null>(null);
  const [etichetta, setEtichetta] = useState(riga.etichettaAlternativa || '');
  const [saving, setSaving] = useState(false);

  const candidate = righeRaggruppamento.filter(r => r.id !== riga.id && r.tipoRiga !== 'DESCRITTIVA');

  const submit = async () => {
    if (!targetId) {
      showToast('Seleziona una riga target', 'error');
      return;
    }
    setSaving(true);
    try {
      await onSave(targetId, etichetta.trim());
      onClose();
    } catch (e) {
      showToast((e as Error).message || 'Errore', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="prev-modal">
      <div className="prev-modal__box">
        <div className="prev-modal__header">
          <h2 className="prev-modal__title">Rendi alternativa di…</h2>
          <button className="prev-modal__close" onClick={onClose}><XIcon /></button>
        </div>
        <div className="prev-modal__body">
          <p className="bop-text-muted">
            Stai marcando come alternativa: <strong>{riga.descrizione}</strong>.
            Scegli a quale riga del raggruppamento legarla.
          </p>
          {candidate.length === 0 ? (
            <div className="bop-empty-state">
              Nessuna riga disponibile in questo raggruppamento.
            </div>
          ) : (
            <ul className="prev-alt-target-list">
              {candidate.map(r => (
                <li
                  key={r.id}
                  className={`prev-alt-target-item ${targetId === r.id ? 'prev-alt-target-item--selected' : ''}`}
                  onClick={() => setTargetId(r.id)}
                >
                  <input
                    type="radio"
                    checked={targetId === r.id}
                    onChange={() => setTargetId(r.id)}
                  />
                  <div className="prev-alt-target-item__content">
                    <div className="prev-alt-target-item__title">
                      {r.descrizione}
                      {r.alternativaGruppo && (
                        <span className="prev-badge-gruppo">Gruppo {r.alternativaGruppo}</span>
                      )}
                    </div>
                    <div className="prev-alt-target-item__sub bop-text-muted">
                      {r.alternativaGruppo
                        ? 'Verrà aggiunta a questo cluster esistente'
                        : 'Verrà creato un nuovo cluster di alternative'}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <div className="bop-form-group prev-alt-etichetta-group">
            <label className="bop-label">Etichetta alternativa <span className="bop-text-muted">(opzionale)</span></label>
            <input
              className="bop-input"
              value={etichetta}
              onChange={e => setEtichetta(e.target.value)}
              placeholder="es. economica, premium"
            />
          </div>
        </div>
        <div className="prev-modal__footer">
          <button className="bop-btn bop-btn-secondary" onClick={onClose} disabled={saving}>Annulla</button>
          <button className="bop-btn bop-btn-primary" onClick={submit} disabled={saving || !targetId}>
            {saving ? 'Salvataggio...' : 'Conferma'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Firma cliente (Fase C) ─────────────────────────────────────────────────
function FirmaNonRegistrata({
  stato,
  onApriUpload,
}: {
  stato: StatoPreventivo;
  onApriUpload: () => void;
}) {
  const puoFirmare = stato === 'INVIATO';
  return (
    <div className="prev-firma__non-registrata">
      <div className="prev-firma__messaggio">
        {puoFirmare
          ? 'Nessuna firma cliente registrata. Carica il PDF firmato dal cliente per registrare la firma e marcare il preventivo come ACCETTATO.'
          : `La firma cliente potrà essere registrata quando il preventivo sarà in stato INVIATO (attuale: ${stato}).`}
      </div>
      <button
        type="button"
        className="bop-btn bop-btn-primary"
        onClick={onApriUpload}
        disabled={!puoFirmare}
      >
        Carica preventivo firmato
      </button>
    </div>
  );
}

function FirmaInfoCorrente({
  firmatoAt,
  firmatoBy,
  firmaAllegatoId,
  firmaModalita,
  preventivoId,
  onMostraStorico,
}: {
  firmatoAt: string;
  firmatoBy: string | null;
  firmaAllegatoId: number | null;
  firmaModalita: string | null;
  preventivoId: number;
  onMostraStorico: () => void;
}) {
  const modalitaLabel =
    firmaModalita === 'FIRMA_DIGITALE'
      ? 'Firma digitale'
      : firmaModalita === 'UPLOAD_CARTACEO'
        ? 'Upload manuale (PDF firmato)'
        : '—';

  const scaricaFirma = async () => {
    if (!firmaAllegatoId) return;
    try {
      // @api GET /api/preventivi/:id/allegati/:allegatoId/download -> file stream
      await apiDownload(
        `/api/preventivi/${preventivoId}/allegati/${firmaAllegatoId}/download`,
        `preventivo-firmato-${preventivoId}.pdf`
      );
    } catch {
      showToast('Errore nel download del file firmato', 'error');
    }
  };

  return (
    <div className="prev-firma__corrente">
      <div className="prev-firma__riga">
        <span className="prev-firma__label">Data firma</span>
        <strong>{formatDateTime(firmatoAt)}</strong>
      </div>
      <div className="prev-firma__riga">
        <span className="prev-firma__label">Caricato da</span>
        <strong>{firmatoBy || '—'}</strong>
      </div>
      <div className="prev-firma__riga">
        <span className="prev-firma__label">Modalità</span>
        <strong>{modalitaLabel}</strong>
      </div>
      <div className="prev-firma__azioni">
        {firmaAllegatoId && (
          <button
            type="button"
            className="bop-btn bop-btn-secondary"
            onClick={scaricaFirma}
          >
            Scarica PDF firmato
          </button>
        )}
        <button
          type="button"
          className="bop-btn bop-btn-secondary"
          onClick={onMostraStorico}
        >
          Storico firme
        </button>
      </div>
    </div>
  );
}

function ModalCaricaPreventivoFirmato({
  preventivoId,
  onClose,
  onFirmaRegistrata,
}: {
  preventivoId: number;
  onClose: () => void;
  onFirmaRegistrata: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [disclaimerAccettato, setDisclaimerAccettato] = useState(false);
  const [caricamento, setCaricamento] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  const handleClose = () => {
    if (caricamento) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (!file || !disclaimerAccettato) return;
    setCaricamento(true);
    setErrore(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('disclaimer', 'true');
      fd.append('modalita', 'UPLOAD_CARTACEO');
      // @api POST /api/preventivi/:id/registra-firma (multipart) -> {"success":true}
      const res = await apiFetch<{ success: boolean; error?: string }>(
        `/api/preventivi/${preventivoId}/registra-firma`,
        { method: 'POST', body: fd }
      );
      if (!res.success) {
        setErrore(res.error || 'Errore durante la registrazione della firma');
        return;
      }
      showToast('Firma cliente registrata', 'success');
      onFirmaRegistrata();
    } catch (e) {
      setErrore((e as Error)?.message || 'Errore imprevisto');
    } finally {
      setCaricamento(false);
    }
  };

  return (
    <div className="prev-modal">
      <div className="prev-modal__box">
        <div className="prev-modal__header">
          <h2 className="prev-modal__title">Carica preventivo firmato</h2>
          <button className="prev-modal__close" onClick={handleClose} disabled={caricamento}>
            <XIcon />
          </button>
        </div>
        <div className="prev-modal__body">
          <div className="prev-modal-firma__disclaimer">
            <strong>Presa di responsabilità</strong>
            <p>
              Caricando il file qui sotto, confermi di aver verificato che il
              documento sia stato <strong>realmente firmato dal cliente in tutte
              le sue parti</strong>. La tua identità verrà registrata accanto al
              file caricato e l'azione sarà tracciata nello storico del
              preventivo.
            </p>
          </div>

          <div className="bop-form-group">
            <label className="bop-label">File PDF firmato</label>
            <input
              type="file"
              className="bop-input"
              accept="application/pdf,.pdf"
              onChange={e => setFile(e.target.files?.[0] || null)}
              disabled={caricamento}
            />
            {file && (
              <small className="bop-text-muted">
                {file.name} — {(file.size / 1024).toFixed(1)} KB
              </small>
            )}
          </div>

          <label className="prev-checkbox-row">
            <input
              type="checkbox"
              checked={disclaimerAccettato}
              onChange={e => setDisclaimerAccettato(e.target.checked)}
              disabled={caricamento}
            />
            <span>
              Confermo di aver verificato che il PDF caricato è firmato dal
              cliente in tutte le sue parti.
            </span>
          </label>

          {errore && (
            <div className="prev-modal-firma__errore">{errore}</div>
          )}
        </div>
        <div className="prev-modal__footer">
          <button
            type="button"
            className="bop-btn bop-btn-secondary"
            onClick={handleClose}
            disabled={caricamento}
          >
            Annulla
          </button>
          <button
            type="button"
            className="bop-btn bop-btn-primary"
            onClick={handleSubmit}
            disabled={!file || !disclaimerAccettato || caricamento}
          >
            {caricamento ? 'Caricamento…' : 'Registra firma'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface FirmaStoricoEntry {
  id: number;
  azione: string;
  utenteName: string | null;
  dettaglio: string | null;
  createdAt: string;
}

function ModalStoricoFirme({
  preventivoId,
  onClose,
}: {
  preventivoId: number;
  onClose: () => void;
}) {
  const [storico, setStorico] = useState<FirmaStoricoEntry[] | null>(null);
  const [caricamento, setCaricamento] = useState(false);

  useEffect(() => {
    setCaricamento(true);
    // @api GET /api/preventivi/:id/firme -> {"success":true,"data":{"storico":[...]}}
    apiFetch<{ success: boolean; data: { storico: FirmaStoricoEntry[] } }>(
      `/api/preventivi/${preventivoId}/firme`
    )
      .then(res => setStorico(res.success ? (res.data?.storico ?? []) : []))
      .catch(() => setStorico([]))
      .finally(() => setCaricamento(false));
  }, [preventivoId]);

  return (
    <div className="prev-modal">
      <div className="prev-modal__box">
        <div className="prev-modal__header">
          <h2 className="prev-modal__title">Storico firme</h2>
          <button className="prev-modal__close" onClick={onClose}><XIcon /></button>
        </div>
        <div className="prev-modal__body">
          {caricamento && <div className="bop-text-muted">Caricamento…</div>}
          {!caricamento && storico && storico.length === 0 && (
            <div className="bop-text-muted">Nessuna firma registrata.</div>
          )}
          {!caricamento && storico && storico.length > 0 && (
            <ul className="prev-storico-firme">
              {storico.map(s => (
                <li key={s.id}>
                  <strong>{formatDateTime(s.createdAt)}</strong> — {s.azione}
                  <div className="bop-text-muted">
                    {s.utenteName || '—'}{s.dettaglio ? ` · ${s.dettaglio}` : ''}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="prev-modal__footer">
          <button type="button" className="bop-btn bop-btn-primary" onClick={onClose}>
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modale: Marca come inviato (Fase C - interazioni) ────────────────────
// Apre una textarea con nota obbligatoria e chiama PUT /:id/stato.
// Se il backend ritorna 400 con mancanti[], li mostra come elenco user-facing.
function ModalMarcaComeInviato({
  preventivoId,
  isOpen,
  onClose,
  onInviato,
}: {
  preventivoId: number;
  isOpen: boolean;
  onClose: () => void;
  onInviato: () => void;
}) {
  const [note, setNote] = useState('');
  const [salvataggio, setSalvataggio] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [campiMancanti, setCampiMancanti] = useState<string[]>([]);

  const reset = () => {
    setNote('');
    setSalvataggio(false);
    setErrore(null);
    setCampiMancanti([]);
  };

  const handleClose = () => {
    if (salvataggio) return;
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!note.trim()) return;
    setSalvataggio(true);
    setErrore(null);
    setCampiMancanti([]);
    try {
      // @api PUT /api/preventivi/:id/stato {"stato":"INVIATO","note":"string"} -> {"success":true}
      await apiFetch<{ success: boolean }>(`/api/preventivi/${preventivoId}/stato`, {
        method: 'PUT',
        body: JSON.stringify({ stato: 'INVIATO', note: note.trim() }),
      });
      reset();
      onInviato();
    } catch (e) {
      const err = e as Error & { payload?: { error?: string; mancanti?: string[] } };
      const payload = err.payload;
      if (payload && Array.isArray(payload.mancanti) && payload.mancanti.length > 0) {
        setCampiMancanti(payload.mancanti);
        setErrore(payload.error || 'Campi obbligatori mancanti per l\'invio');
      } else {
        setErrore(err.message || 'Errore durante il cambio di stato');
      }
    } finally {
      setSalvataggio(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="prev-modal">
      <div className="prev-modal__box">
        <div className="prev-modal__header">
          <h2 className="prev-modal__title">Marca come inviato</h2>
          <button className="prev-modal__close" onClick={handleClose} disabled={salvataggio}>
            <XIcon />
          </button>
        </div>
        <div className="prev-modal__body">
          <p className="prev-modal-marca-inviato__intro">
            Stai per marcare questo preventivo come <strong>INVIATO</strong>.
            Indica con quale modalità è stato inviato (email, consegna a mano,
            posta, ecc.).
          </p>

          <div className="bop-form-group">
            <label className="bop-label">
              Note invio <span className="prev-required">*</span>
            </label>
            <textarea
              className="bop-input"
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Es. Inviato via email a mario.rossi@example.com"
              rows={3}
              disabled={salvataggio}
            />
          </div>

          {campiMancanti.length > 0 && (
            <div className="prev-alert-mancanti">
              <strong>Non è possibile marcare come inviato.</strong>
              <p>Mancano i seguenti dati obbligatori:</p>
              <ul>
                {campiMancanti.map((c, i) => (
                  <li key={i}>{c}</li>
                ))}
              </ul>
              <p className="prev-alert-mancanti__hint">
                Compila i campi mancanti nelle card del preventivo, poi riprova.
              </p>
            </div>
          )}

          {errore && campiMancanti.length === 0 && (
            <div className="prev-modal-firma__errore">{errore}</div>
          )}
        </div>
        <div className="prev-modal__footer">
          <button
            type="button"
            className="bop-btn bop-btn-secondary"
            onClick={handleClose}
            disabled={salvataggio}
          >
            Annulla
          </button>
          <button
            type="button"
            className="bop-btn bop-btn-primary"
            onClick={handleSubmit}
            disabled={!note.trim() || salvataggio}
          >
            {salvataggio ? 'Salvataggio…' : 'Conferma invio'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modale: conferma invio post-mail (Fase C - interazioni) ──────────────
// Si apre quando una mail viene effettivamente spedita dal runtime editor
// (BroadcastChannel 'bop-email-inviata') e il preventivo è in BOZZA.
function ModalConfermaInvioPostMail({
  preventivoId,
  destinatario,
  inviatoIl,
  isOpen,
  onClose,
  onConfermato,
}: {
  preventivoId: number;
  destinatario: string;
  inviatoIl: Date;
  isOpen: boolean;
  onClose: () => void;
  onConfermato: () => void;
}) {
  const { utente } = usePermesso();
  const [salvataggio, setSalvataggio] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [campiMancanti, setCampiMancanti] = useState<string[]>([]);

  const utenteName = utente?.nome || utente?.email || 'utente';
  const noteAutomatica = `Inviato via email a ${destinatario} il ${formatDateTime(inviatoIl.toISOString())} da ${utenteName}`;

  const handleConferma = async () => {
    setSalvataggio(true);
    setErrore(null);
    setCampiMancanti([]);
    try {
      // @api PUT /api/preventivi/:id/stato {"stato":"INVIATO","note":"string"} -> {"success":true}
      await apiFetch<{ success: boolean }>(`/api/preventivi/${preventivoId}/stato`, {
        method: 'PUT',
        body: JSON.stringify({ stato: 'INVIATO', note: noteAutomatica }),
      });
      onConfermato();
    } catch (e) {
      const err = e as Error & { payload?: { error?: string; mancanti?: string[] } };
      const payload = err.payload;
      if (payload && Array.isArray(payload.mancanti) && payload.mancanti.length > 0) {
        setCampiMancanti(payload.mancanti);
        setErrore('Non posso marcare come inviato: mancano dati obbligatori');
      } else {
        setErrore(err.message || 'Errore durante il cambio di stato');
      }
    } finally {
      setSalvataggio(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="prev-modal">
      <div className="prev-modal__box">
        <div className="prev-modal__header">
          <h2 className="prev-modal__title">Email inviata. Vuoi marcare come INVIATO?</h2>
          <button className="prev-modal__close" onClick={onClose} disabled={salvataggio}>
            <XIcon />
          </button>
        </div>
        <div className="prev-modal__body">
          <p>
            L'email è stata inviata correttamente. Vuoi anche marcare il
            preventivo come <strong>INVIATO</strong>?
          </p>
          <div className="prev-note-anteprima">
            <small>Nota che verrà salvata:</small>
            <em>{noteAutomatica}</em>
          </div>

          {campiMancanti.length > 0 && (
            <div className="prev-alert-mancanti">
              <strong>Impossibile marcare come inviato.</strong>
              <p>Mancano i seguenti dati nel preventivo:</p>
              <ul>
                {campiMancanti.map((c, i) => (
                  <li key={i}>{c}</li>
                ))}
              </ul>
              <p className="prev-alert-mancanti__hint">
                Compila i dati mancanti e riprova manualmente.
              </p>
            </div>
          )}

          {errore && campiMancanti.length === 0 && (
            <div className="prev-modal-firma__errore">{errore}</div>
          )}
        </div>
        <div className="prev-modal__footer">
          <button
            type="button"
            className="bop-btn bop-btn-secondary"
            onClick={onClose}
            disabled={salvataggio}
          >
            No, lascia come BOZZA
          </button>
          <button
            type="button"
            className="bop-btn bop-btn-primary"
            onClick={handleConferma}
            disabled={salvataggio || campiMancanti.length > 0}
          >
            {salvataggio ? 'Salvataggio…' : 'Sì, marca come inviato'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Pagina principale ──────────────────────────────────────────────────────
export default function PreventivoDettaglioPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = usePermesso();

  const [prev, setPrev] = useState<Preventivo | null>(null);
  const [loading, setLoading] = useState(true);
  const [vistaJsonOpen, setVistaJsonOpen] = useState(false);
  const [duplicaOpen, setDuplicaOpen] = useState(false);
  const [stampaOpen, setStampaOpen] = useState(false);
  const [vistaSceltaOpen, setVistaSceltaOpen] = useState(false);
  const [vistaStampa, setVistaStampa] = useState<VistaStampa | null>(null);
  const [stampaTotali, setStampaTotali] = useState<StampaTotali>('CON_TOTALI');
  const [stampaAllegati, setStampaAllegati] = useState<StampaAllegati>('CON_ALLEGATI');
  const [convertiOpen, setConvertiOpen] = useState(false);
  const [nuovaRigaOpen, setNuovaRigaOpen] = useState(false);
  const [configRigaOpen, setConfigRigaOpen] = useState(false);
  const [configRigaEditing, setConfigRigaEditing] = useState<Riga | null>(null);
  const [rendiAltOpen, setRendiAltOpen] = useState(false);
  const [rendiAltRiga, setRendiAltRiga] = useState<Riga | null>(null);
  const [logsOpen, setLogsOpen] = useState(false);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  // Firma cliente (Fase C)
  const [uploadFirmaOpen, setUploadFirmaOpen] = useState(false);
  const [storicoFirmeOpen, setStoricoFirmeOpen] = useState(false);
  const [firmaCollassata, setFirmaCollassata] = useState(false);
  // Marca come inviato (Fase C - interazioni)
  const [marcaInviatoOpen, setMarcaInviatoOpen] = useState(false);
  // Firma digitale Zoho Sign (Fase D)
  const [firmaDigitaleSceltaOpen, setFirmaDigitaleSceltaOpen] = useState(false);
  const [firmaDigitaleInCorso, setFirmaDigitaleInCorso] = useState(false);
  const [syncFirmaInCorso, setSyncFirmaInCorso] = useState(false);
  // Toast post-invio email (Fase C - interazioni)
  const [emailInviataInfo, setEmailInviataInfo] = useState<{ destinatario: string; inviatoIl: Date } | null>(null);
  const [codiciIva, setCodiciIva] = useState<CodiceIvaOption[]>([]);
  const [pagamenti, setPagamenti] = useState<PagamentoOption[]>([]);
  const [renameRaggrId, setRenameRaggrId] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState('');
  // Drill-down + DnD: focus + open sets per livello + drag attivo
  const [focusGruppoId, setFocusGruppoId] = useState<number | null>(null);
  const [openL1, setOpenL1] = useState<Set<number>>(new Set());
  const [openL2, setOpenL2] = useState<Set<number>>(new Set());
  const [openL3, setOpenL3] = useState<Set<number>>(new Set());
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [vista, setVista] = useState<VistaRighe>('cliente');
  const [expandedRighe, setExpandedRighe] = useState<Set<number>>(new Set());
  const toggleExpanded = (id: number) => setExpandedRighe(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const [acquirentiCommessa, setAcquirentiCommessa] = useState<AcquirenteCommessa[]>([]);
  // Allegati acquirente capitolato
  const [allegatoAcqOpen, setAllegatoAcqOpen] = useState(false);
  const [loadingAllegatiAcq, setLoadingAllegatiAcq] = useState(false);
  const [savingAllegatoAcq, setSavingAllegatoAcq] = useState(false);
  const [fileDaCaricare, setFileDaCaricare] = useState<File | null>(null);
  const [nomeAllegatoNuovo, setNomeAllegatoNuovo] = useState('');
  const [ivaAgevolataNuovo, setIvaAgevolataNuovo] = useState(false);
  const fileInputAllegatoRef = useRef<HTMLInputElement>(null);
  // Modale reminder IVA agevolata
  const [modalReminderIva, setModalReminderIva] = useState(false);
  // Allegati preventivo
  const [allegatoPrevOpen, setAllegatoPrevOpen] = useState(true);
  const [loadingAllegatiPrev, setLoadingAllegatiPrev] = useState(false);
  const [savingAllegatoPrev, setSavingAllegatoPrev] = useState(false);
  const [fileDaCaricarePrev, setFileDaCaricarePrev] = useState<File | null>(null);
  const [nomeAllegatoNuovoPrev, setNomeAllegatoNuovoPrev] = useState('');
  const [visibileUtNuovoPrev, setVisibileUtNuovoPrev] = useState(false);
  const fileInputAllegatoPrevRef = useRef<HTMLInputElement>(null);
  const [commesseCliente, setCommesseCliente] = useState<{
    id: number; numero: string; descrizione: string; clienteRagioneSociale: string;
  }[]>([]);

  // Draft testata (editabile)
  const [testataDraft, setTestataDraft] = useState<Partial<Preventivo>>({});
  // Draft cliente
  const [clienteDraft, setClienteDraft] = useState<Partial<Preventivo>>({});
  const [aggiornandoDaNominativo, setAggiornandoDaNominativo] = useState(false);
  // Draft sconto globale
  const [scontoDraft, setScontoDraft] = useState<{ tipo: string; valore: string }>({ tipo: '', valore: '' });
  // Draft note
  const [noteDraft, setNoteDraft] = useState<string>('');
  // Draft condizioni generali (HTML)
  const [condizioniDraft, setCondizioniDraft] = useState<string>('');
  // Un unico set di "sezioni·campi" modificati. Esempi di chiavi:
  //   "testata.oggetto", "testata.dataValidita", "testata.commessaId"
  //   "cliente.clienteRagioneSociale", "cliente.clienteEmail"
  //   "condizioni.codiceIvaDefaultId", "condizioni.percentualeAcconto"
  //   "note"
  //   "scontoGlobale"
  const [headerDirty, setHeaderDirty] = useState<Set<string>>(new Set());

  // ── Salvataggio automatico della testata ──────────────────────────────────
  // Ogni modifica incrementa la "versione" della sua chiave: a salvataggio
  // concluso si tolgono solo le chiavi che nel frattempo non sono cambiate di
  // nuovo, così un campo scritto mentre la PUT è in volo resta in coda.
  const dirtyVersionRef = useRef<Map<string, number>>(new Map());
  const [revisioneModifiche, setRevisioneModifiche] = useState(0);
  const [statoSalvataggio, setStatoSalvataggio] = useState<'idle' | 'salvando' | 'salvato' | 'errore'>('idle');
  const salvataggioInCorsoRef = useRef<Promise<void> | null>(null);
  const salvaModificheRef = useRef<(() => Promise<void>) | null>(null);
  // Valori correnti letti dal salvataggio (che gira fuori dal ciclo di render).
  const bozzeRef = useRef({
    prev: null as Preventivo | null,
    testata: {} as Partial<Preventivo>,
    cliente: {} as Partial<Preventivo>,
    sconto: { tipo: '', valore: '' },
    note: '',
    condizioni: '',
  });
  bozzeRef.current = {
    prev,
    testata: testataDraft,
    cliente: clienteDraft,
    sconto: scontoDraft,
    note: noteDraft,
    condizioni: condizioniDraft,
  };

  const marcaModificato = useCallback((...chiavi: string[]) => {
    for (const k of chiavi) {
      dirtyVersionRef.current.set(k, (dirtyVersionRef.current.get(k) ?? 0) + 1);
    }
    setHeaderDirty(new Set(dirtyVersionRef.current.keys()));
    setRevisioneModifiche(r => r + 1);
  }, []);

  const togliModificato = useCallback((chiave: string) => {
    dirtyVersionRef.current.delete(chiave);
    setHeaderDirty(new Set(dirtyVersionRef.current.keys()));
  }, []);

  // Invia al server le modifiche in coda. Le chiamate si mettono in fila:
  // chi arriva mentre un salvataggio è in corso lo aspetta e poi invia il resto.
  const salvaModifiche = useCallback(async (): Promise<void> => {
    while (salvataggioInCorsoRef.current) {
      await salvataggioInCorsoRef.current;
    }
    const b = bozzeRef.current;
    if (!b.prev || b.prev.stato !== 'BOZZA' || dirtyVersionRef.current.size === 0) return;
    // L'id viene dal preventivo caricato, non dall'URL: se la pagina sta
    // passando a un altro preventivo le modifiche vanno comunque a quello giusto.
    const prevId = b.prev.id;
    const conNominativo = !!b.prev.nominativoId;

    const body: Record<string, unknown> = {};
    const inviate = new Map<string, number>();
    dirtyVersionRef.current.forEach((versione, chiave) => {
      if (chiave === 'note') {
        body.note = b.note;
      } else if (chiave === 'condizioni.testoHtml') {
        body.condizioniStampa = b.condizioni || null;
      } else if (chiave === 'scontoGlobale') {
        // Tipo scelto ma valore ancora vuoto o incompleto: si aspetta il valore.
        if (b.sconto.tipo) {
          const valore = parseFloat(b.sconto.valore);
          if (Number.isNaN(valore)) return;
          body.scontoGlobaleTipo = b.sconto.tipo;
          body.scontoGlobaleValore = valore;
        } else {
          body.scontoGlobaleTipo = null;
          body.scontoGlobaleValore = null;
        }
      } else if (chiave.startsWith('cliente.')) {
        // Con nominativo collegato i campi cliente non sono editabili.
        if (!conNominativo) {
          const campo = chiave.slice('cliente.'.length) as keyof Preventivo;
          body[campo] = b.cliente[campo];
        }
      } else {
        const campo = chiave.slice(chiave.indexOf('.') + 1) as keyof Preventivo;
        // La data emissione è obbligatoria: finché il campo è vuoto non si salva.
        if (campo === 'dataEmissione' && !b.testata.dataEmissione) return;
        body[campo] = b.testata[campo];
      }
      inviate.set(chiave, versione);
    });
    if (inviate.size === 0) return;

    const esegui = (async () => {
      setStatoSalvataggio('salvando');
      try {
        if (Object.keys(body).length > 0) {
          // @api PUT /api/preventivi/:id {"oggetto":"string?","dataEmissione":"string?","dataValidita":"string?","indirizzoLavori":"string?","codiceIvaDefaultId":"number?","condizionePagamentoId":"number?","condizionePagamentoAccontoId":"number?","percentualeAcconto":"number?","condizionePagamentoExtraAccontoId":"number?","condizionePagamentoExtraSaldoId":"number?","percentualeAccontoExtraAcquirente":"number|null","codiceIvaExtraAcquirenteId":"number|null","commessaId":"number?","clienteRagioneSociale":"string?","clienteNome":"string?","clienteCognome":"string?","clientePartitaIva":"string?","clienteCodiceFiscale":"string?","clienteIndirizzo":"string?","clienteCap":"string?","clienteCitta":"string?","clienteProvincia":"string?","clienteEmail":"string?","clienteTelefono":"string?","note":"string?","condizioniStampa":"string?","scontoGlobaleTipo":"PERCENTUALE|IMPORTO|null","scontoGlobaleValore":"number|null"} -> {"success":true}
          await apiFetch(`/api/preventivi/${prevId}`, {
            method: 'PUT',
            body: JSON.stringify(body),
          });
        }
        inviate.forEach((versione, chiave) => {
          if (dirtyVersionRef.current.get(chiave) === versione) dirtyVersionRef.current.delete(chiave);
        });
        setHeaderDirty(new Set(dirtyVersionRef.current.keys()));
        setStatoSalvataggio('salvato');
        // La PUT ricalcola già i totali: si rilegge il preventivo senza toccare
        // le bozze, così i campi che l'utente sta scrivendo non vengono sovrascritti.
        // @api GET /api/preventivi/:id -> {"success":true,"data":{...}}
        const r = await apiFetch<{ success: boolean; data: Preventivo }>(`/api/preventivi/${prevId}`);
        if (r.success) {
          setPrev(p => {
            // Pagina passata nel frattempo a un altro preventivo: non si tocca.
            if (!p || p.id !== prevId) return p;
            // Gli allegati (con URL firmati) arrivano da chiamate dedicate: si tengono.
            const acquirente = r.data.acquirente && p.acquirente
              ? { ...r.data.acquirente, allegati: p.acquirente.allegati }
              : r.data.acquirente;
            return { ...r.data, allegati: p.allegati, acquirente };
          });
        }
      } catch (e) {
        setStatoSalvataggio('errore');
        showToast((e as Error).message || 'Errore nel salvataggio automatico', 'error');
      }
    })();
    salvataggioInCorsoRef.current = esegui;
    try {
      await esegui;
    } finally {
      if (salvataggioInCorsoRef.current === esegui) salvataggioInCorsoRef.current = null;
    }
  }, []);
  salvaModificheRef.current = salvaModifiche;

  // Salva poco dopo l'ultima modifica. Dipende solo dalla revisione: dopo un
  // errore non riprova da solo, riprova alla modifica successiva o con "Riprova".
  useEffect(() => {
    if (dirtyVersionRef.current.size === 0) return;
    const t = setTimeout(() => { void salvaModifiche(); }, 800);
    return () => clearTimeout(t);
  }, [revisioneModifiche, salvaModifiche]);

  // Uscendo da un campo si salva subito, senza aspettare il ritardo.
  useEffect(() => {
    // La mappa non viene mai sostituita, solo modificata: si può leggere nel cleanup.
    const versioni = dirtyVersionRef.current;
    const onFocusOut = () => {
      if (versioni.size > 0) void salvaModificheRef.current?.();
    };
    document.addEventListener('focusout', onFocusOut);
    return () => {
      document.removeEventListener('focusout', onFocusOut);
      // Lasciando la pagina si inviano le modifiche ancora in coda.
      if (versioni.size > 0) void salvaModificheRef.current?.();
    };
  }, []);

  // State collassamento card commessa e testata (persistito in localStorage)
  const [cardsCommessaCollassate, setCardsCommessaCollassate] = useState<boolean>(() => {
    try {
      return localStorage.getItem('bop.preventivo.cardsCommessaCollassate') === 'true';
    } catch {
      return false;
    }
  });
  const [cardsTestataCollassate, setCardsTestataCollassate] = useState<boolean>(() => {
    try {
      return localStorage.getItem('bop.preventivo.cardsTestataCollassate') === 'true';
    } catch {
      return false;
    }
  });
  const [cardsRigheCollassate, setCardsRigheCollassate] = useState<boolean>(() => {
    try {
      return localStorage.getItem('bop.preventivo.cardsRigheCollassate') === 'true';
    } catch {
      return false;
    }
  });
  const [cardsFooterCollassate, setCardsFooterCollassate] = useState<boolean>(() => {
    try {
      return localStorage.getItem('bop.preventivo.cardsFooterCollassate') === 'true';
    } catch {
      return false;
    }
  });
  const [cardsCondizioniCollassate, setCardsCondizioniCollassate] = useState<boolean>(() => {
    try {
      return localStorage.getItem('bop.preventivo.cardsCondizioniCollassate') === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('bop.preventivo.cardsCommessaCollassate', String(cardsCommessaCollassate));
    } catch {
      // ignore persist errors
    }
  }, [cardsCommessaCollassate]);

  useEffect(() => {
    try {
      localStorage.setItem('bop.preventivo.cardsTestataCollassate', String(cardsTestataCollassate));
    } catch {
      // ignore persist errors
    }
  }, [cardsTestataCollassate]);

  useEffect(() => {
    try {
      localStorage.setItem('bop.preventivo.cardsRigheCollassate', String(cardsRigheCollassate));
    } catch {
      // ignore persist errors
    }
  }, [cardsRigheCollassate]);

  useEffect(() => {
    try {
      localStorage.setItem('bop.preventivo.cardsFooterCollassate', String(cardsFooterCollassate));
    } catch {
      // ignore persist errors
    }
  }, [cardsFooterCollassate]);

  useEffect(() => {
    try {
      localStorage.setItem('bop.preventivo.cardsCondizioniCollassate', String(cardsCondizioniCollassate));
    } catch {
      // ignore persist errors
    }
  }, [cardsCondizioniCollassate]);

  // Protezione: chiedi conferma prima di chiudere la tab/uscire se ci sono modifiche non salvate
  useEffect(() => {
    if (headerDirty.size === 0) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [headerDirty]);

  // Scorciatoia Ctrl/Cmd+S → salva subito le modifiche in coda (e blocca il
  // "Salva pagina" del browser).
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isSaveShortcut = (e.metaKey || e.ctrlKey) && e.key === 's';
      if (!isSaveShortcut) return;
      e.preventDefault();
      e.stopPropagation();
      void salvaModificheRef.current?.();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const canEdit = can('commerciale.preventivi', 'edit');

  useBloccoRecordContesto({
    recordSocietaId: prev?.societaId,
    label: prev ? `Preventivo ${prev.numero}` : 'Preventivo',
  });

  // Ricarica solo gli allegati del preventivo (con signed URL GCS)
  const ricaricaAllegatiPreventivo = useCallback(async () => {
    if (!id) return;
    setLoadingAllegatiPrev(true);
    try {
      // @api GET /api/preventivi/:id/allegati -> {"success":true,"data":[{"id":"number","nome":"string","nomeFile":"string","mimeType":"string|null","dimensione":"number|null","visibileUfficioTecnico":"boolean","stampaConPreventivo":"boolean","uploadedAt":"string","uploadedBy":{"id":"number","nome":"string"}|null}]}
      const r = await apiFetch<{ success: boolean; data: Allegato[] }>(
        `/api/preventivi/${id}/allegati`
      );
      if (r.success) {
        setPrev(p => p ? { ...p, allegati: r.data } : p);
      }
    } catch (e) {
      console.error('[Preventivo] ricaricaAllegatiPreventivo', e);
    } finally {
      setLoadingAllegatiPrev(false);
    }
  }, [id]);

  // Ricarica solo gli allegati dell'acquirente (con signed URL GCS)
  const ricaricaAllegatiAcquirente = useCallback(async (acquirenteId: number) => {
    if (!acquirenteId) return;
    setLoadingAllegatiAcq(true);
    try {
      // @api GET /api/acquirenti/:acquirenteId/allegati -> {"success":true,"data":[{"id":"number","nome":"string","nomeFile":"string","mimeType":"string|null","dimensione":"number|null","ivaAgevolata":"boolean","uploadedAt":"string","uploadedBy":{"id":"number","nome":"string"}|null}]}
      const r = await apiFetch<{ success: boolean; data: AllegatoAcquirente[] }>(
        `/api/acquirenti/${acquirenteId}/allegati`
      );
      if (r.success) {
        setPrev(p => {
          if (!p || !p.acquirente) return p;
          return { ...p, acquirente: { ...p.acquirente, allegati: r.data } };
        });
      }
    } catch (e) {
      console.error('[Preventivo] ricaricaAllegatiAcquirente', e);
    } finally {
      setLoadingAllegatiAcq(false);
    }
  }, []);

  // Ricarica preventivo
  const ricarica = useCallback(async () => {
    try {
      // Prima si inviano le modifiche in coda, altrimenti le bozze qui sotto
      // verrebbero sovrascritte con i valori vecchi del server.
      await salvaModificheRef.current?.();
      // @api GET /api/preventivi/:id -> {"success":true,"data":{...}}
      const r = await apiFetch<{ success: boolean; data: Preventivo; error?: string }>(`/api/preventivi/${id}`);
      if (r.success) {
        setPrev(r.data);
        // Carica i signed URL degli allegati in parallelo, non bloccante.
        // La pagina è già renderizzata, gli allegati arrivano dopo.
        ricaricaAllegatiPreventivo();
        if (r.data.acquirenteId) {
          ricaricaAllegatiAcquirente(r.data.acquirenteId);
        }
        // Campi modificati durante la rilettura (stesso preventivo): restano
        // quelli scritti dall'utente e rimangono in coda di salvataggio.
        const inCoda = bozzeRef.current.prev?.id === r.data.id
          ? new Set(dirtyVersionRef.current.keys())
          : new Set<string>();
        if (inCoda.size === 0) dirtyVersionRef.current.clear();
        const bozzeCorrenti = bozzeRef.current;
        const tieni = <T,>(chiave: string, server: T, corrente: T): T => (inCoda.has(chiave) ? corrente : server);
        const campoTestata = (campo: keyof Preventivo, server: unknown) =>
          inCoda.has(`testata.${campo}`) || inCoda.has(`condizioni.${campo}`) ? bozzeCorrenti.testata[campo] : server;
        const testataServer: Partial<Preventivo> = {
          oggetto: r.data.oggetto,
          dataEmissione: r.data.dataEmissione,
          dataValidita: r.data.dataValidita,
          indirizzoLavori: r.data.indirizzoLavori,
          codiceIvaDefaultId: r.data.codiceIvaDefaultId,
          condizionePagamentoId: r.data.condizionePagamentoId,
          condizionePagamentoAccontoId: r.data.condizionePagamentoAccontoId,
          condizionePagamentoExtraAccontoId: r.data.condizionePagamentoExtraAccontoId,
          condizionePagamentoExtraSaldoId: r.data.condizionePagamentoExtraSaldoId,
          codiceIvaExtraAcquirenteId: r.data.codiceIvaExtraAcquirenteId,
          percentualeAcconto: r.data.percentualeAcconto != null ? String(r.data.percentualeAcconto) : '',
          percentualeAccontoExtraAcquirente: r.data.percentualeAccontoExtraAcquirente != null ? String(r.data.percentualeAccontoExtraAcquirente) : '',
          commessaId: r.data.commessaId ?? null,
        };
        const testata: Record<string, unknown> = {};
        (Object.keys(testataServer) as (keyof Preventivo)[]).forEach(k => {
          testata[k] = campoTestata(k, testataServer[k]);
        });
        setTestataDraft(testata as Partial<Preventivo>);
        const clienteServer: Partial<Preventivo> = {
          clienteRagioneSociale: r.data.clienteRagioneSociale,
          clienteNome: r.data.clienteNome,
          clienteCognome: r.data.clienteCognome,
          clientePartitaIva: r.data.clientePartitaIva,
          clienteCodiceFiscale: r.data.clienteCodiceFiscale,
          clienteIndirizzo: r.data.clienteIndirizzo,
          clienteCap: r.data.clienteCap,
          clienteCitta: r.data.clienteCitta,
          clienteProvincia: r.data.clienteProvincia,
          clienteEmail: r.data.clienteEmail,
          clienteTelefono: r.data.clienteTelefono,
        };
        const cliente: Record<string, unknown> = {};
        (Object.keys(clienteServer) as (keyof Preventivo)[]).forEach(k => {
          cliente[k] = tieni(`cliente.${k}`, clienteServer[k], bozzeCorrenti.cliente[k]);
        });
        setClienteDraft(cliente as Partial<Preventivo>);
        setScontoDraft(tieni('scontoGlobale', {
          tipo: r.data.scontoGlobaleTipo || '',
          valore: r.data.scontoGlobaleValore || '',
        }, bozzeCorrenti.sconto));
        setNoteDraft(tieni('note', r.data.note ?? '', bozzeCorrenti.note));
        setCondizioniDraft(tieni('condizioni.testoHtml', r.data.condizioniStampa ?? '', bozzeCorrenti.condizioni));
        setHeaderDirty(new Set(dirtyVersionRef.current.keys()));
      } else {
        showToast(r.error || 'Errore caricamento preventivo', 'error');
      }
    } catch (e) {
      showToast((e as Error).message || 'Errore caricamento preventivo', 'error');
    } finally {
      setLoading(false);
    }
  }, [id, ricaricaAllegatiPreventivo, ricaricaAllegatiAcquirente]);

  useEffect(() => {
    ricarica();
  }, [ricarica]);

  // Re-sync manuale dello stato firma digitale da Zoho Sign (fallback al webhook)
  const handleSyncFirma = async () => {
    setSyncFirmaInCorso(true);
    try {
      // @api POST /api/preventivi/:id/sync-firma {} -> {"success":true,"data":{"stato":"string","aggiornato":"boolean","messaggio":"string"}}
      const res = await apiFetch<{
        success: boolean;
        error?: string;
        data?: { stato: string; aggiornato: boolean; messaggio: string };
      }>(`/api/preventivi/${id}/sync-firma`, { method: 'POST' });
      if (!res.success || !res.data) {
        showToast(res.error || 'Errore nella sincronizzazione della firma', 'error');
        return;
      }
      showToast(res.data.messaggio, res.data.aggiornato ? 'success' : 'info');
      if (res.data.aggiornato) await ricarica();
    } catch (e) {
      showToast((e as Error).message || 'Errore nella sincronizzazione della firma', 'error');
    } finally {
      setSyncFirmaInCorso(false);
    }
  };

  // Carica anagrafiche (codici iva, pagamenti) quando conosco la società
  useEffect(() => {
    if (!prev?.societaId) return;
    const carica = async () => {
      try {
        // @api GET /api/codici-iva?societaId={id}&attivo=true -> {"codici":[{"id":"number","codice":"string","descrizione":"string","aliquota":"string","attivo":"boolean"}]}
        const rIva = await apiFetch<{ codici: CodiceIvaOption[] }>(
          `/api/codici-iva?societaId=${prev.societaId}&attivo=true`
        );
        setCodiciIva(rIva.codici || []);
      } catch {
        setCodiciIva([]);
      }
      try {
        // @api GET /api/pagamenti?societaId={id} -> {"success":true,"data":[{"id":"number","nome":"string"}],"condizioni":[{"id":"number","nome":"string"}]}
        const rPag = await apiFetch<{ success?: boolean; data?: PagamentoOption[]; condizioni?: PagamentoOption[] }>(
          `/api/pagamenti?societaId=${prev.societaId}`
        );
        setPagamenti(rPag.data || rPag.condizioni || []);
      } catch {
        setPagamenti([]);
      }
    };
    carica();
  }, [prev?.societaId]);

  // Carica acquirenti della commessa (per dropdown in testata CONFIGURATO)
  useEffect(() => {
    if (!prev?.commessaId || prev?.tipo !== 'CONFIGURATO_COMMESSA') {
      setAcquirentiCommessa([]);
      return;
    }
    (async () => {
      try {
        // @api GET /api/commesse/:commessaId/acquirenti -> {"success":true,"data":[{"id":"number","commessaId":"number","unitaCodice":"string","unitaDescrizione":"string|null","nome":"string|null","cognome":"string|null","email":"string|null","telefono":"string|null"}]}
        const r = await apiFetch<{ success: boolean; data: AcquirenteCommessa[] }>(
          `/api/commesse/${prev.commessaId}/acquirenti`
        );
        setAcquirentiCommessa(r.data || []);
      } catch {
        setAcquirentiCommessa([]);
      }
    })();
  }, [prev?.commessaId, prev?.tipo]);

  // Carica commesse del cliente (per combo commessa in testata)
  useEffect(() => {
    if (!prev?.nominativoId || !prev?.id) { setCommesseCliente([]); return; }
    // @api GET /api/preventivi/:id/commesse-cliente -> {"success":true,"data":[{"id":"number","numero":"string","descrizione":"string","clienteRagioneSociale":"string"}]}
    apiFetch<{ success: boolean; data: typeof commesseCliente }>(
      `/api/preventivi/${prev.id}/commesse-cliente`
    )
      .then(r => setCommesseCliente(r.data || []))
      .catch(() => setCommesseCliente([]));
  }, [prev?.nominativoId, prev?.id]);

  // Carica log on demand
  const caricaLog = useCallback(async () => {
    if (!id) return;
    try {
      // @api GET /api/preventivi/:id/log -> {"success":true,"data":[{"id":"number","azione":"string","dettaglio":"string|null","utenteName":"string","createdAt":"string"}]}
      const r = await apiFetch<{ success: boolean; data: LogEntry[] }>(`/api/preventivi/${id}/log`);
      if (r.success) setLogs(r.data);
    } catch (e) {
      showToast((e as Error).message || 'Errore log', 'error');
    }
  }, [id]);

  useEffect(() => {
    if (logsOpen && logs.length === 0) caricaLog();
  }, [logsOpen, logs.length, caricaLog]);

  // Apri di default tutti i raggruppamenti al primo caricamento del preventivo
  useEffect(() => {
    if (!prev) return;
    const l1 = prev.raggruppamenti.filter(r => r.livello === 1).map(r => r.id);
    const l2 = prev.raggruppamenti.filter(r => r.livello === 2).map(r => r.id);
    const l3 = prev.raggruppamenti.filter(r => r.livello === 3).map(r => r.id);
    setOpenL1(new Set(l1));
    setOpenL2(new Set(l2));
    setOpenL3(new Set(l3));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prev?.id]);

  // ── Listener BroadcastChannel: invio email da TemplateEditorPage (altra finestra)
  // Il modulo runtime di stampa apre una nuova tab; quando l'email è stata
  // effettivamente spedita pubblica un evento su 'bop-email-inviata'. Qui lo
  // intercettiamo e, se il preventivo è in BOZZA, proponiamo di marcarlo come
  // INVIATO con nota auto-precompilata.
  useEffect(() => {
    if (!prev || typeof BroadcastChannel === 'undefined') return;
    const ch = new BroadcastChannel('bop-email-inviata');
    const handler = (ev: MessageEvent) => {
      const msg = ev.data as {
        entitaType?: string;
        entitaId?: number;
        destinatari?: string[];
        inviatoIl?: string;
      } | null;
      if (!msg) return;
      if (msg.entitaType !== 'preventivo') return;
      if (msg.entitaId !== prev.id) return;
      if (prev.stato !== 'BOZZA') return;
      const dest = (msg.destinatari || []).filter(Boolean).join(', ') || 'destinatario';
      const inviatoIl = msg.inviatoIl ? new Date(msg.inviatoIl) : new Date();
      setEmailInviataInfo({ destinatario: dest, inviatoIl });
    };
    ch.addEventListener('message', handler);
    return () => {
      ch.removeEventListener('message', handler);
      ch.close();
    };
  }, [prev?.id, prev?.stato]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── DnD: sensori (devono stare PRIMA di qualsiasi early return per rispettare i Rules of Hooks)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  if (loading) {
    return <Layout><div className="bop-empty-state">Caricamento...</div></Layout>;
  }

  if (!prev) {
    return <Layout><div className="bop-empty-state">Preventivo non trovato</div></Layout>;
  }

  const readonly = prev.stato !== 'BOZZA' || !canEdit;

  // ── Handlers testata ──────────────────────────────────────────────────────
  // Aggiorna un campo di testata e marca il set dirty.
  const aggiornaTestata = (patch: Partial<Preventivo>) => {
    // Un valore identico a quello già presente non è una modifica.
    const cambiati = (Object.keys(patch) as (keyof Preventivo)[])
      .filter(k => patch[k] !== bozzeRef.current.testata[k]);
    if (cambiati.length === 0) return;
    setTestataDraft(d => ({ ...d, ...patch }));
    marcaModificato(...cambiati.map(k => `testata.${k}`));
  };

  // Aggiorna un campo di cliente e marca il set dirty.
  const aggiornaCliente = (patch: Partial<Preventivo>) => {
    // Un valore identico a quello già presente non è una modifica.
    const cambiati = (Object.keys(patch) as (keyof Preventivo)[])
      .filter(k => patch[k] !== bozzeRef.current.cliente[k]);
    if (cambiati.length === 0) return;
    setClienteDraft(d => ({ ...d, ...patch }));
    marcaModificato(...cambiati.map(k => `cliente.${k}`));
  };

  // Aggiorna un campo delle condizioni (alias semantico di aggiornaTestata
  // con prefisso diverso, utile per distinguere i campi nella barra di stato).
  const aggiornaCondizioni = (patch: Partial<Preventivo>) => {
    // Un valore identico a quello già presente non è una modifica.
    const cambiati = (Object.keys(patch) as (keyof Preventivo)[])
      .filter(k => patch[k] !== bozzeRef.current.testata[k]);
    if (cambiati.length === 0) return;
    setTestataDraft(d => ({ ...d, ...patch }));
    marcaModificato(...cambiati.map(k => `condizioni.${k}`));
  };

  const ricomponiOggetto = () => {
    if (!prev) return;
    const numero = prev.numero || '';
    let descrizioneCliente: string;
    if (prev.acquirente) {
      const nome = (prev.acquirente.nome || '').trim();
      const cognome = (prev.acquirente.cognome || '').trim();
      const unita = (prev.acquirente.unitaCodice || '').trim();
      descrizioneCliente = [nome, cognome, unita].filter(Boolean).join(' ');
    } else {
      descrizioneCliente = (prev.clienteRagioneSociale || '').trim() ||
        `${prev.clienteNome || ''} ${prev.clienteCognome || ''}`.trim();
    }
    const nuovo = ['Preventivo', numero, descrizioneCliente].filter(Boolean).join(' ');
    aggiornaTestata({ oggetto: nuovo });
  };

  const aggiornaDaNominativo = async () => {
    if (!id || !prev?.nominativoId) return;
    if (!window.confirm('Copiare i dati del cliente dall\'anagrafica? I dati attuali nel preventivo verranno sovrascritti.')) return;
    setAggiornandoDaNominativo(true);
    try {
      // @api POST /api/preventivi/:id/aggiorna-da-nominativo {} -> {"success":true}
      await apiFetch<{ success: boolean }>(`/api/preventivi/${id}/aggiorna-da-nominativo`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      showToast('Dati cliente aggiornati dal nominativo', 'success');
      await ricarica();
    } catch (e) {
      const err = e as Error;
      showToast(err?.message || 'Errore aggiornamento', 'error');
    } finally {
      setAggiornandoDaNominativo(false);
    }
  };

  const ricaricaCondizioniDaSede = async () => {
    if (readonly) return;
    if (!window.confirm(
      'Le condizioni attualmente sul preventivo verranno sovrascritte con quelle di default della sede. Procedere?'
    )) return;
    try {
      // @api POST /api/preventivi/:id/ricarica-condizioni-da-sede {} -> {"success":true,"condizioniStampa":"string|null","info":"string|null"}
      const res = await apiFetch<{ success: boolean; condizioniStampa: string | null; info: string | null }>(
        `/api/preventivi/${id}/ricarica-condizioni-da-sede`,
        { method: 'POST', body: JSON.stringify({}) }
      );
      setCondizioniDraft(res.condizioniStampa ?? '');
      togliModificato('condizioni.testoHtml');
      if (res.info) {
        showToast(res.info, 'info');
      } else {
        showToast('Condizioni aggiornate dalla sede', 'success');
      }
      await ricarica();
    } catch (e) {
      showToast((e as Error).message || 'Errore ricarica condizioni', 'error');
    }
  };

  // ── Transizione stato ─────────────────────────────────────────────────────
  const cambiaStato = async (nuovoStato: StatoPreventivo, messaggioConferma?: string) => {
    if (!window.confirm(
      messaggioConferma
      || `Confermi cambio stato: ${STATO_LABEL[prev.stato]} → ${STATO_LABEL[nuovoStato]}?`
    )) return;
    try {
      // Le modifiche in coda vanno scritte finché il preventivo è ancora in BOZZA.
      await salvaModifiche();
      // @api PUT /api/preventivi/:id/stato {"stato":"INVIATO|ACCETTATO|RIFIUTATO|SCADUTO|ANNULLATO"} -> {"success":true}
      await apiFetch(`/api/preventivi/${id}/stato`, {
        method: 'PUT',
        body: JSON.stringify({ stato: nuovoStato }),
      });
      showToast(`Stato aggiornato a ${STATO_LABEL[nuovoStato]}`, 'success');
      await ricarica();
    } catch (e) {
      showToast((e as Error).message || 'Errore cambio stato', 'error');
    }
  };

  // ── Ricalcolo totali manuale ──────────────────────────────────────────────
  const ricalcolaTotali = async () => {
    try {
      // @api POST /api/preventivi/:id/ricalcola-totali -> {"success":true,"data":{"totaleImponibileCliente":"string","totaleIvaCliente":"string","totaleCliente":"string"}}
      await apiFetch(`/api/preventivi/${id}/ricalcola-totali`, { method: 'POST' });
      showToast('Totali ricalcolati', 'success');
      await ricarica();
    } catch (e) {
      showToast((e as Error).message || 'Errore ricalcolo', 'error');
    }
  };

  // ── Raggruppamenti ────────────────────────────────────────────────────────
  const creaRaggruppamento = async (
    etichetta: string,
    livello: 1 | 2 | 3 = 1,
    parentId: number | null = null,
  ) => {
    const v = etichetta.trim();
    if (!v) return;
    try {
      // @api POST /api/preventivi/:id/raggruppamenti {"etichetta":"string","livello":"1|2|3","parentId":"number|null","descrizione":"string?"} -> {"success":true,"data":{"id":"number"}}
      await apiFetch(`/api/preventivi/${id}/raggruppamenti`, {
        method: 'POST',
        body: JSON.stringify({ etichetta: v, livello, parentId }),
      });
      showToast('Raggruppamento creato', 'success');
      await ricarica();
    } catch (e) {
      showToast((e as Error).message || 'Errore creazione raggruppamento', 'error');
    }
  };

  const riordinaRaggruppamenti = async (
    ordini: Array<{ id: number; ordine: number; parentId?: number | null }>,
  ) => {
    if (!id || ordini.length === 0) return;
    try {
      // @api POST /api/preventivi/:id/raggruppamenti/riordina {"ordini":"[{id,ordine,parentId?}]"} -> {"success":true}
      await apiFetch(`/api/preventivi/${id}/raggruppamenti/riordina`, {
        method: 'POST',
        body: JSON.stringify({ ordini }),
      });
      await ricarica();
    } catch (e) {
      showToast((e as Error).message || 'Errore riordino raggruppamenti', 'error');
      await ricarica();
    }
  };

  const riordinaRighe = async (
    ordini: Array<{ id: number; ordine: number; raggruppamentoId?: number | null }>,
  ) => {
    if (!id || ordini.length === 0) return;
    try {
      // @api POST /api/preventivi/:id/righe/riordina {"ordini":"[{id,ordine,raggruppamentoId?}]"} -> {"success":true}
      await apiFetch(`/api/preventivi/${id}/righe/riordina`, {
        method: 'POST',
        body: JSON.stringify({ ordini }),
      });
      await ricarica();
    } catch (e) {
      showToast((e as Error).message || 'Errore riordino righe', 'error');
      await ricarica();
    }
  };

  const salvaRinominaRaggr = async (rId: number) => {
    const etichetta = renameValue.trim();
    if (!etichetta) return;
    try {
      // @api PUT /api/preventivi/:id/raggruppamenti/:rId {"etichetta":"string"} -> {"success":true}
      await apiFetch(`/api/preventivi/${id}/raggruppamenti/${rId}`, {
        method: 'PUT',
        body: JSON.stringify({ etichetta }),
      });
      setRenameRaggrId(null);
      setRenameValue('');
      await ricarica();
    } catch (e) {
      showToast((e as Error).message || 'Errore rinomina', 'error');
    }
  };

  const eliminaRaggr = async (rId: number, etichetta: string) => {
    if (!window.confirm(`Eliminare il raggruppamento "${etichetta}"? Le righe associate torneranno in radice.`)) return;
    try {
      // @api DELETE /api/preventivi/:id/raggruppamenti/:rId -> {"success":true}
      await apiFetch(`/api/preventivi/${id}/raggruppamenti/${rId}`, { method: 'DELETE' });
      showToast('Raggruppamento eliminato', 'success');
      await ricarica();
    } catch (e) {
      showToast((e as Error).message || 'Errore eliminazione', 'error');
    }
  };

  // ── Righe ─────────────────────────────────────────────────────────────────
  const creaRiga = async (d: NuovaRigaDraft) => {
    // Capitolato "effettivo": un preventivo fuori-capitolato (CLIENTE_PAGA_DIRETT.
    // senza acquirente) va trattato come SENZA capitolato → riga NON_CAPITOLATO.
    const fuoriCapitolato =
      prev?.tipoFatturazioneFreeze === 'CLIENTE_PAGA_DIRETTAMENTE' && !prev?.acquirenteId;
    const hasCapitolatoEff = !!prev?.offertaCapitolatoId && !fuoriCapitolato;
    const body: Record<string, unknown> = {
      tipoRiga: d.tipoRiga,
      raggruppamentoId: d.raggruppamentoId,
      descrizione: d.descrizione,
      descrizioneEstesa: d.descrizioneEstesa || null,
      umVendita: d.umVendita || null,
      tipoRigaCapitolato: hasCapitolatoEff ? 'EXTRA_CAPITOLATO_AGGIUNTIVO' : 'NON_CAPITOLATO',
    };
    if (d.tipoRiga !== 'DESCRITTIVA') {
      body.quantita = d.quantita ? parseFloat(d.quantita) : null;
      body.prezzoUnitario = d.prezzoUnitario ? parseFloat(d.prezzoUnitario) : null;
      body.sconto = d.sconto ? parseFloat(d.sconto) : null;
    }
    // @api POST /api/preventivi/:id/righe {"tipoRiga":"ARTICOLO|DESCRITTIVA","raggruppamentoId":"number?","descrizione":"string","quantita":"number?","prezzoUnitario":"number?","sconto":"number?"} -> {"success":true,"data":{"id":"number"}}
    const resp = await apiFetch<{ success: boolean; data: { id: number } }>(
      `/api/preventivi/${id}/righe`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      }
    );
    const nuovaRigaId = resp.data?.id;

    if (d.alternativaTargetRigaId != null && nuovaRigaId) {
      let rigaTargetId: number | null = null;
      if (d.alternativaTargetRigaId === -1) {
        // Nuova alternativa: cerca una riga candidata nello stesso raggruppamento
        const candidate = (prev?.righe || []).find(r =>
          r.raggruppamentoId === d.raggruppamentoId &&
          r.tipoRiga !== 'DESCRITTIVA' &&
          r.id !== nuovaRigaId
        );
        if (!candidate) {
          showToast('Nessuna riga disponibile per creare il cluster di alternative', 'error');
          await ricarica();
          return;
        }
        rigaTargetId = candidate.id;
      } else {
        rigaTargetId = d.alternativaTargetRigaId;
      }
      // @api POST /api/preventivi/:id/righe/:rigaId/imposta-alternativa {"rigaTargetId":"number","etichettaAlternativa":"string|null"} -> {"success":true,"data":{"alternativaGruppo":"string|null","alternativaScelta":"boolean","etichettaAlternativa":"string|null"}}
      await apiFetch(`/api/preventivi/${id}/righe/${nuovaRigaId}/imposta-alternativa`, {
        method: 'POST',
        body: JSON.stringify({
          rigaTargetId,
          etichettaAlternativa: d.etichettaAlternativa.trim() || null,
        }),
      });
    }
    showToast('Riga aggiunta', 'success');
    await ricarica();
  };

  const eliminaRiga = async (rigaId: number, descr: string) => {
    if (!window.confirm(`Eliminare la riga "${descr}"?`)) return;
    try {
      // @api DELETE /api/preventivi/:id/righe/:rigaId -> {"success":true}
      await apiFetch(`/api/preventivi/${id}/righe/${rigaId}`, { method: 'DELETE' });
      showToast('Riga eliminata', 'success');
      await ricarica();
    } catch (e) {
      showToast((e as Error).message || 'Errore eliminazione riga', 'error');
    }
  };

  const duplicaRiga = async (rigaId: number) => {
    if (!prev) return;
    const riga = prev.righe.find(r => r.id === rigaId);
    if (!riga) return;

    // Se in cluster di alternative, conferma scioglimento
    let sciogli = false;
    if (riga.alternativaGruppo && riga.raggruppamentoId) {
      const nCluster = prev.righe.filter(
        r => r.raggruppamentoId === riga.raggruppamentoId && r.alternativaGruppo === riga.alternativaGruppo
      ).length;
      const ok = window.confirm(
        `Questa riga fa parte di un cluster di alternative (${nCluster} righe).\n` +
        `Duplicandola il cluster verrà sciolto: tutte le righe alternative torneranno righe normali.\n\n` +
        `Vuoi procedere?`
      );
      if (!ok) return;
      sciogli = true;
    }

    try {
      // @api POST /api/preventivi/:id/righe/:rigaId/duplica {"sciogliClusterSeNecessario":"boolean?"} -> {"success":true,"data":{"id":"number"}}
      const r = await apiFetch<{ success: boolean; data?: { id: number }; error?: string }>(
        `/api/preventivi/${id}/righe/${rigaId}/duplica`,
        { method: 'POST', body: JSON.stringify({ sciogliClusterSeNecessario: sciogli }) }
      );
      if (r.success) {
        showToast('Riga duplicata', 'success');
        await ricarica();
      } else {
        showToast(r.error || 'Errore duplicazione riga', 'error');
      }
    } catch (e) {
      showToast((e as Error).message || 'Errore duplicazione riga', 'error');
    }
  };

  const duplicaRaggruppamento = async (raggrId: number) => {
    try {
      // @api POST /api/preventivi/:id/raggruppamenti/:rId/duplica {} -> {"success":true,"data":{"id":"number","nRighe":"number","nGruppi":"number"}}
      const r = await apiFetch<{ success: boolean; data?: { id: number; nRighe: number; nGruppi: number }; error?: string }>(
        `/api/preventivi/${id}/raggruppamenti/${raggrId}/duplica`,
        { method: 'POST', body: JSON.stringify({}) }
      );
      if (r.success && r.data) {
        showToast(`Sezione duplicata (${r.data.nGruppi} gruppi, ${r.data.nRighe} righe)`, 'success');
        await ricarica();
      } else {
        showToast(r.error || 'Errore duplicazione sezione', 'error');
      }
    } catch (e) {
      showToast((e as Error).message || 'Errore duplicazione sezione', 'error');
    }
  };

  const scegliAlternativa = async (rigaId: number) => {
    try {
      // @api POST /api/preventivi/:id/righe/:rigaId/scegli-alternativa -> {"success":true}
      await apiFetch(`/api/preventivi/${id}/righe/${rigaId}/scegli-alternativa`, { method: 'POST' });
      await ricarica();
    } catch (e) {
      showToast((e as Error).message || 'Errore scelta alternativa', 'error');
    }
  };

  const apriRendiAlternativa = (r: Riga) => {
    if (!r.raggruppamentoId) {
      showToast('La riga deve essere dentro un raggruppamento', 'error');
      return;
    }
    setRendiAltRiga(r);
    setRendiAltOpen(true);
  };

  const slegaAlternativa = async (r: Riga) => {
    try {
      // @api POST /api/preventivi/:id/righe/:rigaId/imposta-alternativa {"rigaTargetId":"null"} -> {"success":true}
      await apiFetch(`/api/preventivi/${id}/righe/${r.id}/imposta-alternativa`, {
        method: 'POST',
        body: JSON.stringify({ rigaTargetId: null }),
      });
      showToast('Riga slegata dalle alternative', 'success');
      await ricarica();
    } catch (e) {
      showToast((e as Error).message || 'Errore', 'error');
    }
  };

  const confermaRendiAlternativa = async (rigaTargetId: number, etichetta: string) => {
    if (!rendiAltRiga) return;
    // @api POST /api/preventivi/:id/righe/:rigaId/imposta-alternativa {"rigaTargetId":"number","etichettaAlternativa":"string|null"} -> {"success":true}
    await apiFetch(`/api/preventivi/${id}/righe/${rendiAltRiga.id}/imposta-alternativa`, {
      method: 'POST',
      body: JSON.stringify({ rigaTargetId, etichettaAlternativa: etichetta || null }),
    });
    showToast('Alternativa impostata', 'success');
    await ricarica();
  };

  // ── Acquirente (CONFIGURATO_COMMESSA) ─────────────────────────────────────
  const aggiornaAcquirente = async (acquirenteId: number | null) => {
    try {
      // @api PUT /api/preventivi/:id {"acquirenteId":"number|null"} -> {"success":true}
      await apiFetch(`/api/preventivi/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ acquirenteId }),
      });
      showToast('Acquirente aggiornato', 'success');
      await ricarica();
    } catch (e) {
      showToast((e as Error).message || 'Errore aggiornamento acquirente', 'error');
    }
  };

  // ── Allegati acquirente capitolato ─────────────────────────────────────
  const uploadAllegatoAcquirente = async () => {
    if (!prev?.acquirenteId || !fileDaCaricare || !nomeAllegatoNuovo.trim()) {
      showToast('Seleziona un file e inserisci il nome', 'error');
      return;
    }
    setSavingAllegatoAcq(true);
    try {
      const fd = new FormData();
      fd.append('file', fileDaCaricare);
      fd.append('nome', nomeAllegatoNuovo.trim());
      fd.append('ivaAgevolata', String(ivaAgevolataNuovo));
      // @api POST /api/acquirenti/:acquirenteId/allegati {"file":"File","nome":"string","ivaAgevolata":"boolean"} -> {"success":true,"data":{"id":"number"}}
      await apiFetch(`/api/acquirenti/${prev.acquirenteId}/allegati`, {
        method: 'POST',
        body: fd,
      });
      setFileDaCaricare(null);
      setNomeAllegatoNuovo('');
      setIvaAgevolataNuovo(false);
      if (fileInputAllegatoRef.current) fileInputAllegatoRef.current.value = '';
      showToast('Allegato caricato', 'success');
      await ricaricaAllegatiAcquirente(prev.acquirenteId);
    } catch (e) {
      showToast((e as Error).message || 'Errore caricamento allegato', 'error');
    } finally {
      setSavingAllegatoAcq(false);
    }
  };

  const aggiornaAllegatoAcquirente = async (
    allegatoId: number,
    patch: { nome?: string; ivaAgevolata?: boolean }
  ) => {
    if (!prev?.acquirenteId) return;
    try {
      // @api PUT /api/acquirenti/:acquirenteId/allegati/:allegatoId {"nome":"string?","ivaAgevolata":"boolean?"} -> {"success":true}
      await apiFetch(`/api/acquirenti/${prev.acquirenteId}/allegati/${allegatoId}`, {
        method: 'PUT',
        body: JSON.stringify(patch),
      });
      await ricaricaAllegatiAcquirente(prev.acquirenteId);
    } catch (e) {
      showToast((e as Error).message || 'Errore aggiornamento allegato', 'error');
    }
  };

  const eliminaAllegatoAcquirente = async (allegatoId: number, nome: string) => {
    if (!prev?.acquirenteId) return;
    if (!window.confirm(`Eliminare l'allegato "${nome}"?`)) return;
    try {
      // @api DELETE /api/acquirenti/:acquirenteId/allegati/:allegatoId -> {"success":true}
      await apiFetch(`/api/acquirenti/${prev.acquirenteId}/allegati/${allegatoId}`, {
        method: 'DELETE',
      });
      showToast('Allegato eliminato', 'success');
      await ricaricaAllegatiAcquirente(prev.acquirenteId);
    } catch (e) {
      showToast((e as Error).message || 'Errore eliminazione allegato', 'error');
    }
  };

  // ── Allegati preventivo ─────────────────────────────────────────────────
  const uploadAllegatoPreventivoHandler = async () => {
    if (!prev?.id || !fileDaCaricarePrev || !nomeAllegatoNuovoPrev.trim()) {
      showToast('Seleziona un file e inserisci il nome', 'error');
      return;
    }
    setSavingAllegatoPrev(true);
    try {
      const fd = new FormData();
      fd.append('file', fileDaCaricarePrev);
      fd.append('nome', nomeAllegatoNuovoPrev.trim());
      fd.append('visibileUfficioTecnico', String(visibileUtNuovoPrev));
      // @api POST /api/preventivi/:id/allegati {"file":"File","nome":"string","visibileUfficioTecnico":"boolean"} -> {"success":true,"data":{"id":"number"}}
      await apiFetch(`/api/preventivi/${prev.id}/allegati`, {
        method: 'POST',
        body: fd,
      });
      setFileDaCaricarePrev(null);
      setNomeAllegatoNuovoPrev('');
      setVisibileUtNuovoPrev(false);
      if (fileInputAllegatoPrevRef.current) fileInputAllegatoPrevRef.current.value = '';
      showToast('Allegato caricato', 'success');
      await ricaricaAllegatiPreventivo();
    } catch (e) {
      showToast((e as Error).message || 'Errore caricamento allegato', 'error');
    } finally {
      setSavingAllegatoPrev(false);
    }
  };

  const aggiornaAllegatoPreventivoHandler = async (
    allegatoId: number,
    patch: { nome?: string; visibileUfficioTecnico?: boolean; stampaConPreventivo?: boolean }
  ) => {
    if (!prev?.id) return;
    try {
      // @api PUT /api/preventivi/:id/allegati/:allegatoId {"nome":"string?","visibileUfficioTecnico":"boolean?","stampaConPreventivo":"boolean?"} -> {"success":true}
      await apiFetch(`/api/preventivi/${prev.id}/allegati/${allegatoId}`, {
        method: 'PUT',
        body: JSON.stringify(patch),
      });
      await ricaricaAllegatiPreventivo();
    } catch (e) {
      showToast((e as Error).message || 'Errore aggiornamento allegato', 'error');
    }
  };

  const eliminaAllegatoPreventivoHandler = async (allegatoId: number, nome: string) => {
    if (!prev?.id) return;
    if (!window.confirm(`Eliminare l'allegato "${nome}"?`)) return;
    try {
      // @api DELETE /api/preventivi/:id/allegati/:allegatoId -> {"success":true}
      await apiFetch(`/api/preventivi/${prev.id}/allegati/${allegatoId}`, {
        method: 'DELETE',
      });
      showToast('Allegato eliminato', 'success');
      await ricaricaAllegatiPreventivo();
    } catch (e) {
      showToast((e as Error).message || 'Errore eliminazione allegato', 'error');
    }
  };

  // ── Rendering righe con raggruppamento ────────────────────────────────────
  const righePerGruppo = (groupId: number | null) =>
    prev.righe
      .filter(r => (r.raggruppamentoId ?? null) === groupId)
      .sort((a, b) => a.ordine - b.ordine);

  const righeFree = righePerGruppo(null);

  // ── Helpers gerarchia 3 livelli ───────────────────────────────────────────
  const figliDiGruppo = (parentId: number | null, livello: 1 | 2 | 3): Raggruppamento[] =>
    prev!.raggruppamenti
      .filter(g => g.parentId === parentId && g.livello === livello)
      .sort((a, b) => a.ordine - b.ordine);

  const totaleRigheRicorsivo = (gruppoId: number): number => {
    let n = prev!.righe.filter(r => r.raggruppamentoId === gruppoId).length;
    for (const f of prev!.raggruppamenti.filter(g => g.parentId === gruppoId)) {
      n += totaleRigheRicorsivo(f.id);
    }
    return n;
  };

  const importoRicorsivo = (gruppoId: number): number => {
    let tot = prev!.righe
      .filter(r => r.raggruppamentoId === gruppoId && (!r.alternativaGruppo || r.alternativaScelta))
      .reduce((s, r) => s + parseFloat(r.importoNetto || '0'), 0);
    for (const f of prev!.raggruppamenti.filter(g => g.parentId === gruppoId)) {
      tot += importoRicorsivo(f.id);
    }
    return tot;
  };

  const focusPath: Raggruppamento[] = (() => {
    if (!focusGruppoId || !prev) return [];
    const out: Raggruppamento[] = [];
    let cur: Raggruppamento | undefined = prev.raggruppamenti.find(g => g.id === focusGruppoId);
    while (cur) {
      out.unshift(cur);
      const parentId: number | null = cur.parentId;
      cur = parentId != null ? prev.raggruppamenti.find(x => x.id === parentId) : undefined;
    }
    return out;
  })();

  // Toggle open per livello
  const toggleOpenLivello = (livello: 1 | 2 | 3, gruppoId: number) => {
    if (livello === 1) {
      setOpenL1((s) => {
        const next = new Set(s);
        if (next.has(gruppoId)) next.delete(gruppoId);
        else next.add(gruppoId);
        return next;
      });
    } else if (livello === 2) {
      setOpenL2((s) => {
        const next = new Set(s);
        if (next.has(gruppoId)) next.delete(gruppoId);
        else next.add(gruppoId);
        return next;
      });
    } else {
      setOpenL3((s) => {
        const next = new Set(s);
        if (next.has(gruppoId)) next.delete(gruppoId);
        else next.add(gruppoId);
        return next;
      });
    }
  };

  // Apri modale nuova riga (preimpostazione del raggruppamento gestita lato modale)
  const apriNuovaRigaInGruppo = (_raggruppamentoId: number | null) => {
    setConfigRigaEditing(null);
    setConfigRigaOpen(true);
  };

  // Gruppi di partenza (root della vista) — dipende dal focus
  const rootGruppi: Raggruppamento[] = focusGruppoId
    ? (() => {
        const f = prev.raggruppamenti.find(g => g.id === focusGruppoId);
        return f ? [f] : [];
      })()
    : figliDiGruppo(null, 1);

  // ── DnD: parser degli ID convenzionali (G:<id> / R:<id> / GE:<id|root>) ──
  const parseDndId = (s: string | number): { kind: 'G' | 'R' | 'GE'; id: number | 'root' } | null => {
    const str = String(s);
    if (str === 'GE:root') return { kind: 'GE', id: 'root' };
    const m = str.match(/^(G|R|GE):(\d+)$/);
    if (!m) return null;
    return { kind: m[1] as 'G' | 'R' | 'GE', id: parseInt(m[2], 10) };
  };

  // ── DnD: collision detection ──────────────────────────────────────────────
  const collisionDetection: CollisionDetection = (args) => {
    const pw = pointerWithin(args);
    if (pw.length > 0) return pw;
    const ri = rectIntersection(args);
    if (ri.length > 0) return ri;
    return closestCenter(args);
  };

  // Ricava il gruppo target a partire da un over.id qualsiasi
  const findTargetGruppoIdFromOver = (overId: string): number | 'root' | null => {
    const parsed = parseDndId(overId);
    if (!parsed) return null;
    if (parsed.kind === 'GE') return parsed.id;          // empty-zone (gruppo o root)
    if (parsed.kind === 'G') return parsed.id as number; // header gruppo
    if (parsed.kind === 'R') {
      const r = prev!.righe.find(x => x.id === parsed.id);
      if (!r) return null;
      return r.raggruppamentoId ?? 'root';
    }
    return null;
  };

  const onDragStart = (e: DragStartEvent) => {
    if (readonly) return;
    const id = String(e.active.id);
    setActiveDragId(id);
  };

  // Durante il drag non modifichiamo lo stato del preventivo (il server è
  // l'unica fonte di verità). Lasciamo un onDragOver vuoto per estensioni
  // future (es. preview del target).
  const onDragOver = (_e: DragOverEvent) => {
    // no-op
  };

  const onDragEnd = (e: DragEndEvent) => {
    setActiveDragId(null);
    if (readonly) return;

    const { active, over } = e;
    if (!over) return;
    if (active.id === over.id) return;

    const aP = parseDndId(String(active.id));
    const oP = parseDndId(String(over.id));
    if (!aP || !oP) return;

    // ── Caso 1: drag RIGA ──────────────────────────────────────────────────
    if (aP.kind === 'R') {
      const aRiga = prev!.righe.find(r => r.id === aP.id);
      if (!aRiga) return;

      const targetGruppo = findTargetGruppoIdFromOver(String(over.id));
      if (targetGruppo === null) return;
      const targetGruppoId: number | null = targetGruppo === 'root' ? null : targetGruppo;

      // Drop su altra RIGA
      if (oP.kind === 'R') {
        const oRiga = prev!.righe.find(r => r.id === oP.id);
        if (!oRiga) return;

        // Caso 1a: stessa raggruppamentoId → riordino interno
        if ((aRiga.raggruppamentoId ?? null) === (oRiga.raggruppamentoId ?? null)) {
          const inGruppo = prev!.righe
            .filter(r => (r.raggruppamentoId ?? null) === (oRiga.raggruppamentoId ?? null))
            .sort((x, y) => x.ordine - y.ordine);
          const oldIdx = inGruppo.findIndex(r => r.id === aRiga.id);
          const newIdx = inGruppo.findIndex(r => r.id === oRiga.id);
          if (oldIdx === -1 || newIdx === -1) return;
          const reord = arrayMove(inGruppo, oldIdx, newIdx);
          const ordini = reord.map((r, i) => ({ id: r.id, ordine: i + 1 }));
          void riordinaRighe(ordini);
          return;
        }

        // Caso 1b: gruppo diverso → sposta nella posizione del target
        const inDest = prev!.righe
          .filter(r => (r.raggruppamentoId ?? null) === (oRiga.raggruppamentoId ?? null) && r.id !== aRiga.id)
          .sort((x, y) => x.ordine - y.ordine);
        const oIdx = inDest.findIndex(r => r.id === oRiga.id);
        const nuovaLista = [
          ...inDest.slice(0, oIdx),
          aRiga,
          ...inDest.slice(oIdx),
        ];
        const ordini = nuovaLista.map((r, i) => ({
          id: r.id,
          ordine: i + 1,
          raggruppamentoId: r.id === aRiga.id ? (oRiga.raggruppamentoId ?? null) : undefined,
        }));
        void riordinaRighe(ordini);
        return;
      }

      // Drop su gruppo / empty-zone → metti in coda
      if ((aRiga.raggruppamentoId ?? null) === targetGruppoId) return;
      const inDest = prev!.righe
        .filter(r => (r.raggruppamentoId ?? null) === targetGruppoId)
        .sort((x, y) => x.ordine - y.ordine);
      const ultimoOrdine = inDest.reduce((m, r) => Math.max(m, r.ordine), 0);
      void riordinaRighe([{ id: aRiga.id, ordine: ultimoOrdine + 1, raggruppamentoId: targetGruppoId }]);
      return;
    }

    // ── Caso 2: drag GRUPPO ────────────────────────────────────────────────
    if (aP.kind === 'G') {
      const aG = prev!.raggruppamenti.find(g => g.id === aP.id);
      if (!aG) return;

      // Solo drop su gruppi o empty-zone di gruppi (non root)
      if (oP.kind !== 'G' && oP.kind !== 'GE') return;
      if (oP.kind === 'GE' && oP.id === 'root') return;
      const targetGruppoId = oP.id as number;
      const oG = prev!.raggruppamenti.find(g => g.id === targetGruppoId);
      if (!oG) return;

      // Sotto-caso 2a: stesso livello → riordino tra fratelli (eventuale cambio parent)
      if (oG.livello === aG.livello) {
        const newParent = oG.parentId;
        const fratelli = prev!.raggruppamenti
          .filter(g => g.parentId === newParent && g.livello === aG.livello && g.id !== aG.id)
          .sort((a, b) => a.ordine - b.ordine);
        const oIdx = fratelli.findIndex(g => g.id === oG.id);
        const nuovaLista = [
          ...fratelli.slice(0, oIdx),
          aG,
          ...fratelli.slice(oIdx),
        ];
        const ordini = nuovaLista.map((g, i) => ({
          id: g.id,
          ordine: i + 1,
          parentId: g.id === aG.id ? newParent : undefined,
        }));
        void riordinaRaggruppamenti(ordini);
        return;
      }

      // Sotto-caso 2b: drop su gruppo del livello-padre → cambia parent
      if (oG.livello === aG.livello - 1) {
        const fratelliNuovi = prev!.raggruppamenti
          .filter(g => g.parentId === oG.id && g.livello === aG.livello)
          .sort((a, b) => a.ordine - b.ordine);
        const nuovoOrdine = fratelliNuovi.reduce((m, g) => Math.max(m, g.ordine), 0) + 1;
        void riordinaRaggruppamenti([{ id: aG.id, ordine: nuovoOrdine, parentId: oG.id }]);
        return;
      }

      // Tutti gli altri drop sono ignorati (vincolo livelli)
      return;
    }
  };

  const dragPreviewContent = (() => {
    if (!activeDragId) return null;
    const p = parseDndId(activeDragId);
    if (!p || p.id === 'root') return null;
    if (p.kind === 'R') {
      const r = prev!.righe.find(x => x.id === p.id);
      if (!r) return null;
      return (
        <div className="prev-drag-preview prev-drag-preview--riga">
          <span className="prev-drag-preview__grip"><GripIcon /></span>
          <span className="prev-drag-preview__codice">{r.codicePrecopy || '—'}</span>
          <span className="prev-drag-preview__desc">{r.descrizione}</span>
          {r.importoNetto && (
            <span className="prev-drag-preview__imp">{formatCurrency(parseFloat(r.importoNetto))}</span>
          )}
        </div>
      );
    }
    if (p.kind === 'G') {
      const g = prev!.raggruppamenti.find(x => x.id === p.id);
      if (!g) return null;
      return (
        <div className={`prev-drag-preview prev-drag-preview--gruppo prev-drag-preview--l${g.livello}`}>
          <span className="prev-drag-preview__grip"><GripIcon /></span>
          <span className="prev-drag-preview__livello">L{g.livello}</span>
          <span className="prev-drag-preview__desc"><strong>{g.etichetta}</strong></span>
          <span className="prev-drag-preview__imp">
            {totaleRigheRicorsivo(g.id)} righe · {formatCurrency(importoRicorsivo(g.id))}
          </span>
        </div>
      );
    }
    return null;
  })();
  const totaleRighe = prev.righe.length;

  const isConfig = prev.tipo === 'CONFIGURATO_COMMESSA';
  const hasCapitolato = isConfig && !!prev.offertaCapitolatoId;
  // colCount include la colonna grip (per il drag) in più: grip + # + tipo + ... + azioni
  const colCount = (!isConfig ? 10 : vista === 'cliente' ? 10 : 13) + 1;
  const descrittivaColSpan = colCount - 4; // grip + # + tipo + azioni

  // Righe valide per il calcolo dei totali (no DESCRITTIVA, no alternative non scelte)
  const righeValide = prev.righe.filter(
    r => r.tipoRiga !== 'DESCRITTIVA' && (!r.alternativaGruppo || r.alternativaScelta)
  );

  // Imponibile lordo cliente: somma delle righe prima dello sconto globale.
  const imponibileLordoNum = righeValide.reduce((s, r) => {
    const q = parseFloat(r.quantita || '0');
    const sc = parseFloat(r.sconto || '0');
    const p = parseFloat(r.prezzoUnitario || '0');
    return s + p * q * (1 - sc / 100);
  }, 0);

  // Righe acconto/saldo di un ramo (riusate dalle card Totali).
  const renderAccontoRows = (ramo: TotaliRamoPreventivo) => {
    if (!ramo.acconto) return null;
    return (
      <>
        <div className="prev-totali-row prev-totali-row--acconto">
          <span className="prev-totali-row__label">
            Acconto <span className="bop-text-muted">({formatNumber(ramo.acconto.percentuale)}%)</span>
          </span>
          <span className="prev-totali-row__value">€ {formatCurrency(ramo.acconto.totale)}</span>
        </div>
        <div className="prev-totali-row">
          <span className="prev-totali-row__label bop-text-muted">Imponibile acconto + IVA</span>
          <span className="prev-totali-row__value bop-text-muted">
            € {formatCurrency(ramo.acconto.imponibile)} + € {formatCurrency(ramo.acconto.iva)}
          </span>
        </div>
        {ramo.acconto.condizionePagamento && (
          <div className="prev-totali-row">
            <span className="prev-totali-row__label bop-text-muted">Pagamento acconto</span>
            <span className="prev-totali-row__value bop-text-muted">{ramo.acconto.condizionePagamento.nome}</span>
          </div>
        )}
        <div className="prev-totali-row">
          <span className="prev-totali-row__label">Saldo</span>
          <span className="prev-totali-row__value">€ {formatCurrency(ramo.saldoTotale)}</span>
        </div>
      </>
    );
  };

  const firmaMancante = !prev.firmatoAt;
  const convertiDisabled = !canEdit || firmaMancante;
  const fuoriCapitolatoDettaglio =
    isConfig &&
    prev.tipoFatturazioneFreeze === 'CLIENTE_PAGA_DIRETTAMENTE' &&
    !prev.acquirenteId;
  const convertiTooltip = firmaMancante
    ? 'Conversione non disponibile: la firma del cliente non è stata registrata. Carica il preventivo firmato dalla sezione "Firma cliente".'
    : fuoriCapitolatoDettaglio
      ? 'Verrà creato un solo ordine all\'impresa (preventivo fuori capitolato).'
      : undefined;

  // ── Azioni di stato ───────────────────────────────────────────────────────
  // Una sola definizione alimenta sia i bottoni della testata sia l'helper che
  // li spiega: così la spiegazione non può divergere da quello che si vede.
  const firmaDigitalePendente =
    prev.firmaModalita === 'FIRMA_DIGITALE' && prev.zohoStato === 'INVIATA';
  const statoCorrente = STATO_LABEL[prev.stato];

  const azioniDef: AzioneStato[] = [
    {
      key: 'marca-inviato',
      label: 'Marca come inviato',
      variante: 'primary',
      descrizione: 'Registra che il preventivo è stato trasmesso al cliente (email, PEC, consegna a mano…) con una nota obbligatoria sulla modalità. Il preventivo passa a Inviato e non è più modificabile.',
      disponibile: prev.stato === 'BOZZA',
      motivoNonDisponibile:
        prev.stato === 'INVIATO'
          ? 'Il preventivo risulta già inviato al cliente.'
          : `Si può registrare l'invio solo da un preventivo in bozza (stato attuale: ${statoCorrente}).`,
      onClick: () => setMarcaInviatoOpen(true),
    },
    {
      key: 'firma-digitale',
      label: firmaDigitaleInCorso ? 'Invio in corso…' : 'Genera doc per firma digitale',
      variante: 'primary',
      descrizione: 'Genera il PDF del preventivo e lo invia al cliente su Zoho Sign per la firma. Se il preventivo è in bozza passa anche a Inviato. Quando il cliente firma diventa Accettato; se non firma entro la data di validità diventa Scaduto.',
      disponibile:
        (prev.stato === 'BOZZA' || prev.stato === 'INVIATO')
        && !prev.firmatoAt
        && !firmaDigitalePendente,
      motivoNonDisponibile:
        prev.firmatoAt
          ? 'Il preventivo è già firmato dal cliente: la firma è registrata nella sezione "Firma cliente".'
          : firmaDigitalePendente
            ? 'C\'è già una richiesta di firma digitale in attesa. Attendi l\'esito, oppure duplica il preventivo come nuova versione (la richiesta in corso viene annullata).'
            : `Si può inviare per firma solo un preventivo in bozza o inviato (stato attuale: ${statoCorrente}).`,
      onClick: () => setFirmaDigitaleSceltaOpen(true),
    },
    {
      key: 'rifiuta',
      label: 'Rifiuta',
      variante: 'secondary',
      descrizione: 'Registra il rifiuto del cliente. Il preventivo passa a Rifiutato ed è definitivo: per riproporlo va duplicato come nuova versione.',
      disponibile: prev.stato === 'INVIATO',
      motivoNonDisponibile:
        prev.stato === 'BOZZA'
          ? 'Il preventivo non è ancora stato inviato al cliente.'
          : `Si può registrare il rifiuto solo su un preventivo inviato (stato attuale: ${statoCorrente}).`,
      onClick: () => cambiaStato('RIFIUTATO'),
    },
    {
      key: 'invalida-prev',
      label: 'Invalida preventivo',
      variante: 'danger',
      descrizione: 'Dichiara il preventivo non più valido. Non cancella nulla: il documento resta in archivio con tutte le sue righe e la sua storia, ma non potrà più essere inviato, firmato o convertito in ordine. L\'operazione è definitiva.'
        + (firmaDigitalePendente ? ' L\'eventuale richiesta di firma su Zoho Sign viene annullata.' : ''),
      disponibile: prev.stato === 'BOZZA' || prev.stato === 'INVIATO',
      motivoNonDisponibile:
        `Si può invalidare solo un preventivo in bozza o inviato (stato attuale: ${statoCorrente}).`,
      onClick: () => cambiaStato(
        'ANNULLATO',
        `Invalidare il preventivo ${prev.numero}?\n\n`
        + 'Il preventivo NON viene cancellato: resta in archivio ma passa a "Annullato" '
        + 'e non potrà più essere inviato, firmato o convertito in ordine.'
        + (firmaDigitalePendente ? '\nLa richiesta di firma digitale in corso verrà annullata.' : '')
        + '\n\nL\'operazione è definitiva.'
      ),
    },
    {
      key: 'converti',
      label: 'Converti in ordine',
      variante: 'primary',
      descrizione: 'Crea l\'ordine cliente (e la commessa, se prevista) a partire dal preventivo firmato. Il preventivo passa a Convertito.',
      disponibile: prev.stato === 'ACCETTATO',
      disabilitato: convertiDisabled,
      titolo: convertiTooltip,
      motivoNonDisponibile:
        `Il cliente deve prima accettare il preventivo: la conversione si attiva a preventivo Accettato (stato attuale: ${statoCorrente}).`,
      onClick: () => { if (!convertiDisabled) setConvertiOpen(true); },
    },
  ];

  const azioniStato = azioniDef
    .filter(a => a.disponibile)
    .map(a => (
      <button
        key={a.key}
        type="button"
        className={`bop-btn bop-btn-${a.variante}`}
        onClick={a.onClick}
        disabled={!canEdit || firmaDigitaleInCorso || a.disabilitato}
        title={a.titolo || a.descrizione}
      >
        {a.label}
      </button>
    ));

  return (
    <Layout>
      <div className="prev-page">
        {/* Header */}
        <div className="prev-header">
          <BopBackLink to="/commerciale/attivo/preventivi" label="Torna ai preventivi" />

          <div className="prev-header__top">
                <div className="prev-header__titlewrap">
                  <h1 className="prev-header__title">
                    <span>
                      Preventivo {prev.numero}
                      {prev.versione > 1 && (
                        <span className="prev-versione-badge" title={`Versione ${prev.versione}`}>
                          v{prev.versione}
                        </span>
                      )}
                    </span>
                  </h1>
                  {prev.oggetto && (
                    <div className="prev-header__oggetto">{prev.oggetto}</div>
                  )}
                  <div className="prev-header__meta">
                    <span className={`prev-badge-stato ${STATO_CLS[prev.stato]}`}>{STATO_LABEL[prev.stato]}</span>
                    <BadgeFlussoCommerciale flusso={prev.flussoCommerciale} />
                    {/* La classe '--configurato' è il nome tecnico e NON cambia;
                        la label visibile passa da 'Configurato/Commessa' a 'Capitolato commessa'. */}
                    <span className={`prev-tipo-pill ${prev.tipo === 'SEMPLICE' ? 'prev-tipo-pill--semplice' : 'prev-tipo-pill--configurato'}`}>
                      {prev.tipo === 'SEMPLICE' ? 'Semplice' : 'Capitolato commessa'}
                    </span>
                    {prev.commessa && (
                      <Link to={`/commerciale/commesse/${prev.commessa.id}`} className="prev-link-inline" target="_blank" rel="noopener noreferrer">
                        Commessa {prev.commessa.numero}
                      </Link>
                    )}
                  </div>
                  {prev.preventivoOrigine && (
                    <div className="prev-origine-link">
                      Copia di <Link to={`/commerciale/attivo/preventivi/${prev.preventivoOrigine.id}`}>{prev.preventivoOrigine.numero}</Link>
                    </div>
                  )}
                </div>
                <div className="prev-header__actions-wrap">
                <HelperAzioni azioni={azioniDef} canEdit={canEdit} />
                <div className="prev-header__actions">
                  {!readonly && (statoSalvataggio !== 'idle' || headerDirty.size > 0) && (
                    <span
                      className={`prev-autosave prev-autosave--${statoSalvataggio === 'errore' ? 'errore' : statoSalvataggio === 'salvando' || headerDirty.size > 0 ? 'in-corso' : 'salvato'}`}
                      role="status"
                      aria-live="polite"
                    >
                      {statoSalvataggio === 'errore' ? (
                        <>
                          Modifiche non salvate
                          <button
                            type="button"
                            className="bop-btn bop-btn-secondary prev-autosave__riprova"
                            onClick={() => { void salvaModifiche(); }}
                          >
                            Riprova
                          </button>
                        </>
                      ) : statoSalvataggio === 'salvando' ? (
                        'Salvataggio…'
                      ) : headerDirty.size > 0 ? (
                        // In attesa del ritardo, o di un valore da completare (es. sconto senza importo).
                        'Modifiche da salvare'
                      ) : (
                        'Modifiche salvate'
                      )}
                    </span>
                  )}
                  <button
                    className="bop-btn bop-btn-secondary"
                    onClick={() => {
                      // Apre sempre la modale opzioni di stampa.
                      // Per CONFIGURATO_COMMESSA con acquirente mostra anche la scelta vista.
                      setVistaSceltaOpen(true);
                    }}
                    title="Stampa, anteprima HTML o invio email del preventivo (non cambia stato)"
                  >
                    Stampa / Email
                  </button>
                  <button className="bop-btn bop-btn-secondary" onClick={() => setVistaJsonOpen(true)}>Vista JSON</button>
                  <button
                    className="bop-btn bop-btn-secondary"
                    onClick={() => setDuplicaOpen(true)}
                    disabled={!canEdit}
                  >
                    Duplica
                  </button>
                  {azioniStato}
                </div>
                </div>
              </div>

              {prev.supersededBy && (
                <div className="prev-banner-warn">
                  Questo preventivo è stato superato da{' '}
                  <Link to={`/commerciale/attivo/preventivi/${prev.supersededBy.id}`} className="prev-link-inline">
                    {prev.supersededBy.numero}
                  </Link>
                </div>
              )}
              {prev.firmaConflittoVersione && (
                <div className="prev-banner-warn">
                  <strong>Attenzione: conflitto di versione.</strong>{' '}
                  Questo preventivo era stato superato o era scaduto, ma è stato
                  firmato dal cliente tramite Zoho Sign ed è quindi tornato ACCETTATO.
                  Verifica quale versione del preventivo è quella valida prima di
                  convertirlo in ordine.
                </div>
              )}
              {(prev.commessaGenerata || (prev.ordiniGenerati && prev.ordiniGenerati.length > 0)) && (
                <div className="prev-banner-row">
                  {prev.commessaGenerata && (
                    <div className="prev-banner-info prev-banner-info--compact">
                      Commessa generata:{' '}
                      <Link to={`/commerciale/commesse/${prev.commessaGenerata.id}`} className="prev-link-inline">
                        {prev.commessaGenerata.numero}
                      </Link>
                    </div>
                  )}
                  {prev.ordiniGenerati && prev.ordiniGenerati.length > 0 && (
                    <div className="prev-banner-info prev-banner-info--compact">
                      {prev.ordiniGenerati.length === 1 ? 'Ordine generato: ' : 'Ordini generati: '}
                      {prev.ordiniGenerati.map((o, i) => (
                        <React.Fragment key={o.id}>
                          {i > 0 && ' · '}
                          <Link
                            to={`/commerciale/attivo/ordini/${o.id}`}
                            className="prev-link-inline"
                          >
                            {o.numero}
                          </Link>
                          {o.destinatarioOrdine && (
                            <span className="bop-text-muted">
                              {' '}({o.destinatarioOrdine}{o.isSoloImporto ? ' · solo importo' : ''})
                            </span>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                </div>
              )}
        </div>

        {/* Card Firma cliente (Fase C) — visibile quando il preventivo è in
            attesa di firma (INVIATO) oppure quando è già stato firmato: in
            quest'ultimo caso resta visibile per mostrare l'esito della firma. */}
        {(prev.stato === 'INVIATO' || !!prev.firmatoAt) && (
        <div className="prev-firma-card-wrap">
          <div className="bop-card prev-card prev-card--firma">
            <div
              className="prev-card__header prev-card__header--clickable"
              onClick={() => setFirmaCollassata(!firmaCollassata)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setFirmaCollassata(!firmaCollassata);
                }
              }}
            >
              <h2 className="prev-card__title">
                <ChevronDownIcon collapsed={firmaCollassata} />
                Firma cliente
              </h2>
              {prev.firmatoAt && (
                <span className="prev-badge prev-badge--ok">✓ Firmato</span>
              )}
            </div>
            {!firmaCollassata && (
              <div className="prev-card__body">
                {prev.firmatoAt ? (
                  <FirmaInfoCorrente
                    firmatoAt={prev.firmatoAt}
                    firmatoBy={prev.firmatoBy}
                    firmaAllegatoId={prev.firmaAllegatoId}
                    firmaModalita={prev.firmaModalita}
                    preventivoId={prev.id}
                    onMostraStorico={() => setStoricoFirmeOpen(true)}
                  />
                ) : prev.firmaModalita === 'FIRMA_DIGITALE' && prev.zohoStato === 'INVIATA' ? (
                  <div className="prev-firma__in-attesa">
                    <span className="prev-badge prev-badge--info">In attesa di firma digitale</span>
                    <p className="bop-text-muted">
                      Il documento è stato inviato al firmatario tramite Zoho Sign.
                      Il preventivo passerà automaticamente ad ACCETTATO alla firma,
                      oppure a SCADUTO se non firmato entro la data di validità
                      {prev.dataValidita ? ` (${formatDateTime(prev.dataValidita)})` : ''}.
                    </p>
                    <p className="bop-text-muted">
                      Per correggere il preventivo è necessario duplicarlo come nuova
                      versione (la richiesta di firma corrente verrà annullata).
                    </p>
                    <p className="bop-text-muted">
                      Se il documento risulta già firmato ma lo stato qui non è
                      aggiornato, sincronizzalo manualmente da Zoho Sign.
                    </p>
                    <button
                      className="bop-btn-secondary"
                      onClick={handleSyncFirma}
                      disabled={syncFirmaInCorso}
                      title="Controlla su Zoho Sign se il documento è già stato firmato e aggiorna lo stato del preventivo"
                    >
                      {syncFirmaInCorso ? 'Sincronizzazione…' : 'Sincronizza stato firma'}
                    </button>
                  </div>
                ) : (
                  <FirmaNonRegistrata
                    stato={prev.stato}
                    onApriUpload={() => setUploadFirmaOpen(true)}
                  />
                )}
              </div>
            )}
          </div>
        </div>
        )}

        {/* Card superiore CONFIGURATO_COMMESSA: Commessa, Capitolato, Acquirente */}
        {isConfig && (
          <button
            type="button"
            className="prev-section-toggle"
            onClick={() => setCardsCommessaCollassate(!cardsCommessaCollassate)}
          >
            <span className="prev-section-toggle__line" />
            <span className="prev-section-toggle__label">
              <ChevronDownIcon collapsed={cardsCommessaCollassate} />
              Commessa · Capitolato · Acquirente
            </span>
            <span className="prev-section-toggle__line" />
          </button>
        )}
        {isConfig && !cardsCommessaCollassate && (
          <div className="prev-header-commessa-cards">
            {/* Card Commessa */}
            <div className="bop-card prev-card">
              <div className="prev-card__header">
                <h2 className="prev-card__title">Commessa</h2>
                {prev.commessa && (
                  <a
                    href={`/commerciale/commesse/${prev.commessa.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bop-btn bop-btn-secondary"
                  >
                    Apri commessa
                  </a>
                )}
              </div>
              <div className="prev-card__body">
                {prev.commessa ? (
                  <>
                    <div className="bop-form-group">
                      <label className="bop-label">Numero</label>
                      <input className="bop-input" value={prev.commessa.numero} disabled />
                    </div>
                    <div className="bop-form-group">
                      <label className="bop-label">Descrizione</label>
                      <input className="bop-input" value={prev.commessa.descrizione} disabled />
                    </div>
                  </>
                ) : (
                  <div className="bop-text-muted">Nessuna commessa collegata</div>
                )}
              </div>
            </div>

            {/* Card Capitolato */}
            <div className="bop-card prev-card">
              <div className="prev-card__header">
                <h2 className="prev-card__title">Capitolato</h2>
                {prev.offertaCapitolato && (
                  <a
                    href={`/commerciale/capitolati/offerte/${prev.offertaCapitolato.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bop-btn bop-btn-secondary"
                  >
                    Apri offerta
                  </a>
                )}
              </div>
              <div className="prev-card__body">
                {prev.offertaCapitolato ? (
                  <>
                    <div className="bop-form-group">
                      <label className="bop-label">Codice offerta</label>
                      <input className="bop-input" value={prev.offertaCapitolato.codice} disabled />
                    </div>
                    <div className="bop-form-group">
                      <label className="bop-label">Oggetto</label>
                      <input className="bop-input" value={prev.offertaCapitolato.oggetto} disabled />
                    </div>
                    <div className="bop-form-group">
                      <label className="bop-label">Tipo fatturazione</label>
                      <div>
                        {prev.tipoFatturazioneFreeze ? (
                          <span
                            className={`prev-badge-fatturazione prev-badge-fatturazione--${
                              prev.tipoFatturazioneFreeze === 'IMPRESA_PAGA_TUTTO' ? 'impresa' : 'cliente'
                            }`}
                          >
                            {TIPO_FATTURAZIONE_LABEL[prev.tipoFatturazioneFreeze]}
                          </span>
                        ) : (
                          <span className="bop-text-muted">—</span>
                        )}
                      </div>
                    </div>
                    <div className="bop-form-group">
                      <label className="bop-label">Ricarico impresa</label>
                      <input
                        className="bop-input"
                        value={(prev.ricaricoImpresaFreeze ?? prev.offertaCapitolato?.ricarico) ? `${prev.ricaricoImpresaFreeze ?? prev.offertaCapitolato?.ricarico}%` : '—'}
                        disabled
                      />
                    </div>
                  </>
                ) : (
                  <div className="bop-text-muted">Nessun capitolato collegato</div>
                )}
              </div>
            </div>

            {/* Card Acquirente */}
            <div className="bop-card prev-card">
              <div className="prev-card__header">
                <h2 className="prev-card__title">Acquirente</h2>
              </div>
              <div className="prev-card__body">
                {!readonly && acquirentiCommessa.length > 0 && (
                  <div className="bop-form-group">
                    <label className="bop-label">Seleziona acquirente</label>
                    <select
                      className="bop-select"
                      value={prev.acquirenteId ?? ''}
                      onChange={e =>
                        aggiornaAcquirente(e.target.value ? parseInt(e.target.value, 10) : null)
                      }
                    >
                      <option value="">— Nessuno —</option>
                      {acquirentiCommessa.map(a => (
                        <option key={a.id} value={a.id}>
                          {a.unitaCodice} — {[a.nome, a.cognome].filter(Boolean).join(' ') || a.unitaDescrizione || ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {prev.acquirente ? (
                  <>
                    <div className="bop-form-group">
                      <label className="bop-label">Nominativo</label>
                      <input
                        className="bop-input"
                        value={`${prev.acquirente.nome || ''} ${prev.acquirente.cognome || ''}`.trim() || '—'}
                        disabled
                      />
                    </div>
                    <div className="bop-form-group">
                      <label className="bop-label">Unità</label>
                      <div>
                        <span className="prev-badge-unita">{prev.acquirente.unitaCodice}</span>
                      </div>
                    </div>
                    <div className="bop-form-row">
                      <div className="bop-form-group">
                        <label className="bop-label">Email</label>
                        <input className="bop-input" value={prev.acquirente.email || ''} disabled />
                      </div>
                      <div className="bop-form-group">
                        <label className="bop-label">Telefono</label>
                        <input className="bop-input" value={prev.acquirente.telefono || ''} disabled />
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="bop-text-muted">Nessun acquirente impostato</div>
                )}

                {prev.acquirente && (
                  <>
                    <hr className="prev-card__divider" />
                    <button
                      className="prev-allegati-acq-header"
                      onClick={() => setAllegatoAcqOpen(v => !v)}
                      type="button"
                    >
                      <span className="bop-label prev-label-section prev-allegati-acq-header__label">Allegati acquirente</span>
                      {prev.acquirente.allegati.length > 0 && (
                        <span className="prev-allegati-acq-count">{prev.acquirente.allegati.length}</span>
                      )}
                      {loadingAllegatiAcq && (
                        <span className="prev-allegati-loader" aria-label="Caricamento allegati">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                               stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                          </svg>
                        </span>
                      )}
                      <svg
                        className={`prev-allegati-acq-chevron${allegatoAcqOpen ? ' prev-allegati-acq-chevron--open' : ''}`}
                        width="14" height="14" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                      >
                        <polyline points="6 9 12 15 18 9"/>
                      </svg>
                    </button>
                    <small className="bop-text-muted">
                      Dichiarazioni IVA agevolata, documenti firmati, altri allegati relativi all'acquirente.
                      Sempre modificabili anche con preventivo inviato.
                    </small>

                    {allegatoAcqOpen && (
                    <div className="prev-allegati-acq-body">
                    {prev.acquirente.allegati.length === 0 ? (
                      <div className="bop-empty-state">Nessun allegato caricato</div>
                    ) : (
                      <div className="prev-allegati-acq-lista">
                        {prev.acquirente.allegati.map(a => (
                          <div key={a.id} className="prev-allegato-acq-row">
                            <div className="prev-allegato-acq-row__top">
                              <svg className="prev-allegato-acq-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                <polyline points="14 2 14 8 20 8"/>
                              </svg>
                              <span className="prev-allegato-acq-nome">{a.nome}</span>
                              {a.ivaAgevolata && (
                                <span className="bop-badge bop-badge-info prev-allegato-acq-iva">IVA agevolata</span>
                              )}
                            </div>
                            <div className="prev-allegato-acq-row__bottom">
                              <span className="bop-text-muted">
                                {a.nomeFile}{a.dimensione ? ` · ${Math.round(a.dimensione / 1024)} KB` : ''}
                              </span>
                              <div className="prev-allegato-acq-row__btns">
                                {prev.acquirenteId && (
                                  // @api GET /api/acquirenti/:acquirenteId/allegati/:allegatoId/download -> apre file in nuova tab
                                  <button
                                    className="bop-table__action-btn bop-table__action-btn--primary"
                                    title="Apri in nuova tab"
                                    onClick={async () => {
                                      try {
                                        await apiOpenBlob(`/api/acquirenti/${prev.acquirenteId}/allegati/${a.id}/download`);
                                      } catch {
                                        showToast('Errore nell\'apertura del file', 'error');
                                      }
                                    }}
                                  >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                      <circle cx="12" cy="12" r="3"/>
                                    </svg>
                                  </button>
                                )}
                                {prev.acquirenteId && (
                                  <button
                                    className="bop-table__action-btn bop-table__action-btn--primary"
                                    title="Scarica"
                                    onClick={async () => {
                                      try {
                                        // @api GET /api/acquirenti/:acquirenteId/allegati/:allegatoId/download -> file stream (attachment)
                                        await apiDownload(`/api/acquirenti/${prev.acquirenteId}/allegati/${a.id}/download`, a.nomeFile || 'allegato');
                                      } catch {
                                        showToast('Errore nel download', 'error');
                                      }
                                    }}
                                  >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                      <polyline points="7 10 12 15 17 10"/>
                                      <line x1="12" y1="15" x2="12" y2="3"/>
                                    </svg>
                                  </button>
                                )}
                                <button
                                  className="bop-table__action-btn bop-table__action-btn--danger"
                                  title="Elimina allegato"
                                  onClick={() => eliminaAllegatoAcquirente(a.id, a.nome)}
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="3 6 5 6 21 6"/>
                                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                                    <path d="M10 11v6M14 11v6"/>
                                    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                                  </svg>
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="prev-allegato-acq-upload">
                      <div className="prev-allegato-acq-upload__header">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                          <polyline points="17 8 12 3 7 8"/>
                          <line x1="12" y1="3" x2="12" y2="15"/>
                        </svg>
                        <span>Carica nuovo allegato</span>
                      </div>
                      <div className="prev-allegato-acq-upload__fields">
                        <div className="bop-form-group">
                          <label className="bop-label">File</label>
                          <input
                            ref={fileInputAllegatoRef}
                            type="file"
                            className="bop-input"
                            accept="application/pdf,image/jpeg,image/png,image/gif,image/webp,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            onChange={e => setFileDaCaricare(e.target.files?.[0] || null)}
                          />
                        </div>
                        <div className="bop-form-group">
                          <label className="bop-label">Nome allegato *</label>
                          <input
                            className="bop-input"
                            value={nomeAllegatoNuovo}
                            onChange={e => setNomeAllegatoNuovo(e.target.value)}
                            placeholder="es. Dichiarazione IVA agevolata"
                          />
                        </div>
                      </div>
                      <div className="prev-allegato-acq-upload__footer">
                        <label className="prev-allegato-acq-upload__iva-check">
                          <input
                            type="checkbox"
                            checked={ivaAgevolataNuovo}
                            onChange={e => setIvaAgevolataNuovo(e.target.checked)}
                          />
                          <span>IVA agevolata</span>
                        </label>
                        <button
                          className="bop-btn bop-btn-primary"
                          onClick={uploadAllegatoAcquirente}
                          disabled={savingAllegatoAcq || !fileDaCaricare || !nomeAllegatoNuovo.trim()}
                        >
                          {savingAllegatoAcq ? (
                            <>
                              <svg className="prev-allegato-spinner" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                              </svg>
                              Carico…
                            </>
                          ) : (
                            <>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="17 8 12 3 7 8"/>
                                <line x1="12" y1="3" x2="12" y2="15"/>
                                <path d="M5 20h14"/>
                              </svg>
                              Carica
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                    </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Toggle collapse Gruppo Testata ordine */}
        <button
          type="button"
          className="prev-section-toggle"
          onClick={() => setCardsTestataCollassate(!cardsTestataCollassate)}
        >
          <span className="prev-section-toggle__line" />
          <span className="prev-section-toggle__label">
            <ChevronDownIcon collapsed={cardsTestataCollassate} />
            Testata · Cliente · Condizioni pagamento
          </span>
          <span className="prev-section-toggle__line" />
        </button>

        {/* 3 card testata */}
        {!cardsTestataCollassate && (
        <div className="prev-layout-cards">
          {/* Card 1 — Testata */}
          <div className="bop-card prev-card">
            <div className="prev-card__header">
              <h2 className="prev-card__title">Testata</h2>
            </div>
            <div className="prev-card__body">
              <div className="bop-form-group">
                <label className="bop-label">Oggetto</label>
                <div className="prev-oggetto-row">
                  <input
                    className="bop-input"
                    value={testataDraft.oggetto ?? ''}
                    onChange={e => aggiornaTestata({ oggetto: e.target.value })}
                    disabled={readonly}
                  />
                  {!readonly && (
                    <button
                      type="button"
                      className="bop-btn bop-btn-secondary"
                      onClick={ricomponiOggetto}
                      title="Rigenera l'oggetto dalla formula standard"
                    >
                      Aggiorna
                    </button>
                  )}
                </div>
              </div>
              <div className="bop-form-row">
                <div className="bop-form-group">
                  <label className="bop-label">Data emissione</label>
                  <input
                    type="date"
                    className="bop-input"
                    value={toDateInput(testataDraft.dataEmissione)}
                    onChange={e => aggiornaTestata({ dataEmissione: e.target.value })}
                    disabled={readonly}
                  />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Data validità</label>
                  <input
                    type="date"
                    className="bop-input"
                    value={toDateInput(testataDraft.dataValidita)}
                    onChange={e => aggiornaTestata({ dataValidita: e.target.value || null })}
                    disabled={readonly}
                  />
                </div>
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Indirizzo lavori</label>
                <textarea
                  className="bop-input"
                  rows={2}
                  value={testataDraft.indirizzoLavori ?? ''}
                  onChange={e => aggiornaTestata({ indirizzoLavori: e.target.value })}
                  disabled={readonly}
                />
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Sede</label>
                <input className="bop-input" value={prev.sede?.nome || '—'} disabled />
              </div>
            </div>
          </div>

          {/* Card 2 — Cliente */}
          <div className="bop-card prev-card">
            <div className="prev-card__header">
              <h2 className="prev-card__title">Cliente</h2>
              <div className="prev-card__header-actions">
                {prev.nominativoId && (
                  <a
                    href={`/anagrafiche/nominativi/${prev.nominativoId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bop-btn bop-btn-secondary"
                    title="Apri la scheda del cliente in un nuovo tab"
                  >
                    Apri Cliente
                  </a>
                )}
                {prev.nominativoId && canEdit && !readonly && (
                  <button
                    type="button"
                    className="bop-btn bop-btn-secondary"
                    onClick={aggiornaDaNominativo}
                    disabled={aggiornandoDaNominativo}
                    title="Copia i dati freschi del cliente dall'anagrafica nel preventivo"
                  >
                    {aggiornandoDaNominativo ? 'Aggiornamento...' : 'Aggiorna'}
                  </button>
                )}
              </div>
            </div>
            <div className="prev-card__body">
              {prev.nominativoId ? (
                <>
                  <div className="bop-form-group">
                    <label className="bop-label">Nominativo</label>
                    <a
                      href={`/anagrafiche/nominativi/${prev.nominativoId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="prev-link-inline"
                    >
                      {prev.clienteRagioneSociale || `${prev.clienteNome || ''} ${prev.clienteCognome || ''}`.trim() || 'Vai alla scheda'}
                    </a>
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">Commessa</label>
                    <select
                      className="bop-select"
                      value={testataDraft.commessaId ?? ''}
                      onChange={e => aggiornaTestata({
                        commessaId: e.target.value ? parseInt(e.target.value, 10) : null,
                      })}
                      disabled={readonly || prev.tipo === 'CONFIGURATO_COMMESSA'}
                      title={prev.tipo === 'CONFIGURATO_COMMESSA'
                        ? 'La commessa di un preventivo Capitolato commessa non è modificabile'
                        : undefined}
                    >
                      <option value="">— Nessuna commessa —</option>
                      {commesseCliente.map(c => (
                        <option key={c.id} value={c.id}>
                          {c.numero} — {c.descrizione}
                        </option>
                      ))}
                    </select>
                    {prev.commessa && !readonly && prev.tipo === 'SEMPLICE' && (
                      <small className="bop-text-muted">
                        Il preventivo SEMPLICE non gestisce il capitolato della commessa.
                      </small>
                    )}
                  </div>
                  <div className="bop-form-row">
                    <div className="bop-form-group">
                      <label className="bop-label">P.IVA</label>
                      <input className="bop-input" value={prev.clientePartitaIva || ''} disabled />
                    </div>
                    <div className="bop-form-group">
                      <label className="bop-label">C.F.</label>
                      <input className="bop-input" value={prev.clienteCodiceFiscale || ''} disabled />
                    </div>
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">Indirizzo</label>
                    <input className="bop-input" value={prev.clienteIndirizzo || ''} disabled />
                  </div>
                  <div className="bop-form-row">
                    <div className="bop-form-group">
                      <label className="bop-label">CAP</label>
                      <input className="bop-input" value={prev.clienteCap || ''} disabled />
                    </div>
                    <div className="bop-form-group">
                      <label className="bop-label">Città</label>
                      <input className="bop-input" value={prev.clienteCitta || ''} disabled />
                    </div>
                    <div className="bop-form-group">
                      <label className="bop-label">Prov.</label>
                      <input className="bop-input" value={prev.clienteProvincia || ''} disabled />
                    </div>
                  </div>
                  <div className="bop-form-row">
                    <div className="bop-form-group">
                      <label className="bop-label">Email</label>
                      <input className="bop-input" value={prev.clienteEmail || ''} disabled />
                    </div>
                    <div className="bop-form-group">
                      <label className="bop-label">Telefono</label>
                      <input className="bop-input" value={prev.clienteTelefono || ''} disabled />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="bop-form-group">
                    <label className="bop-label">Ragione sociale</label>
                    <input
                      className="bop-input"
                      value={clienteDraft.clienteRagioneSociale ?? ''}
                      onChange={e => aggiornaCliente({ clienteRagioneSociale: e.target.value })}
                      disabled={readonly}
                    />
                  </div>
                  <div className="bop-form-row">
                    <div className="bop-form-group">
                      <label className="bop-label">Nome</label>
                      <input
                        className="bop-input"
                        value={clienteDraft.clienteNome ?? ''}
                        onChange={e => aggiornaCliente({ clienteNome: e.target.value })}
                        disabled={readonly}
                      />
                    </div>
                    <div className="bop-form-group">
                      <label className="bop-label">Cognome</label>
                      <input
                        className="bop-input"
                        value={clienteDraft.clienteCognome ?? ''}
                        onChange={e => aggiornaCliente({ clienteCognome: e.target.value })}
                        disabled={readonly}
                      />
                    </div>
                  </div>
                  <div className="bop-form-row">
                    <div className="bop-form-group">
                      <label className="bop-label">P.IVA</label>
                      <input
                        className="bop-input"
                        value={clienteDraft.clientePartitaIva ?? ''}
                        onChange={e => aggiornaCliente({ clientePartitaIva: e.target.value })}
                        disabled={readonly}
                      />
                    </div>
                    <div className="bop-form-group">
                      <label className="bop-label">C.F.</label>
                      <input
                        className="bop-input"
                        value={clienteDraft.clienteCodiceFiscale ?? ''}
                        onChange={e => aggiornaCliente({ clienteCodiceFiscale: e.target.value })}
                        disabled={readonly}
                      />
                    </div>
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">Indirizzo</label>
                    <input
                      className="bop-input"
                      value={clienteDraft.clienteIndirizzo ?? ''}
                      onChange={e => aggiornaCliente({ clienteIndirizzo: e.target.value })}
                      disabled={readonly}
                    />
                  </div>
                  <div className="bop-form-row">
                    <div className="bop-form-group">
                      <label className="bop-label">CAP</label>
                      <input
                        className="bop-input"
                        value={clienteDraft.clienteCap ?? ''}
                        onChange={e => aggiornaCliente({ clienteCap: e.target.value })}
                        disabled={readonly}
                      />
                    </div>
                    <div className="bop-form-group">
                      <label className="bop-label">Città</label>
                      <input
                        className="bop-input"
                        value={clienteDraft.clienteCitta ?? ''}
                        onChange={e => aggiornaCliente({ clienteCitta: e.target.value })}
                        disabled={readonly}
                      />
                    </div>
                    <div className="bop-form-group">
                      <label className="bop-label">Prov.</label>
                      <input
                        className="bop-input"
                        value={clienteDraft.clienteProvincia ?? ''}
                        onChange={e => aggiornaCliente({ clienteProvincia: e.target.value })}
                        disabled={readonly}
                      />
                    </div>
                  </div>
                  <div className="bop-form-row">
                    <div className="bop-form-group">
                      <label className="bop-label">Email</label>
                      <input
                        className="bop-input"
                        value={clienteDraft.clienteEmail ?? ''}
                        onChange={e => aggiornaCliente({ clienteEmail: e.target.value })}
                        disabled={readonly}
                      />
                    </div>
                    <div className="bop-form-group">
                      <label className="bop-label">Telefono</label>
                      <input
                        className="bop-input"
                        value={clienteDraft.clienteTelefono ?? ''}
                        onChange={e => aggiornaCliente({ clienteTelefono: e.target.value })}
                        disabled={readonly}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Card 3 — Condizioni */}
          <div className="bop-card prev-card">
            <div className="prev-card__header">
              <h2 className="prev-card__title">Condizioni</h2>
            </div>
            <div className="prev-card__body">
              <div className="bop-form-group">
                <label className="bop-label">Codice IVA default</label>
                <select
                  className="bop-select"
                  value={testataDraft.codiceIvaDefaultId ?? ''}
                  onChange={e => aggiornaCondizioni({ codiceIvaDefaultId: parseInt(e.target.value, 10) })}
                  disabled={readonly}
                >
                  {codiciIva.map(iva => (
                    <option key={iva.id} value={iva.id}>
                      {iva.codice} — {iva.descrizione} ({formatNumber(parseFloat(iva.aliquota))}%)
                    </option>
                  ))}
                </select>
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Condizione pagamento</label>
                <select
                  className="bop-select"
                  value={testataDraft.condizionePagamentoId ?? ''}
                  onChange={e => aggiornaCondizioni({ condizionePagamentoId: e.target.value ? parseInt(e.target.value, 10) : null })}
                  disabled={readonly}
                >
                  <option value="">— Nessuna —</option>
                  {pagamenti.map(p => (
                    <option key={p.id} value={p.id}>{p.nome}</option>
                  ))}
                </select>
              </div>
              <div className="bop-form-group">
                <label className="bop-label">
                  {prev.totaliDettaglio.isImpresaAcquirente ? '% Acconto impresa' : '% Acconto'}
                </label>
                <input
                  className="bop-input"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={typeof testataDraft.percentualeAcconto === 'string' ? testataDraft.percentualeAcconto : (testataDraft.percentualeAcconto ?? '')}
                  onChange={e => aggiornaCondizioni({ percentualeAcconto: e.target.value })}
                  disabled={readonly}
                  placeholder="es. 30"
                />
              </div>
              {(() => {
                const accRaw = testataDraft.percentualeAcconto;
                const accNum =
                  accRaw === null || accRaw === undefined || accRaw === ''
                    ? 0
                    : typeof accRaw === 'number'
                      ? accRaw
                      : parseFloat(String(accRaw)) || 0;
                const accontoDisabled = readonly || accNum <= 0;
                return (
                  <div className="bop-form-group">
                    <label className="bop-label">
                      {prev.totaliDettaglio.isImpresaAcquirente
                        ? 'Condizione pagamento acconto impresa'
                        : 'Condizione pagamento acconto'}
                    </label>
                    <select
                      className="bop-select"
                      value={testataDraft.condizionePagamentoAccontoId ?? ''}
                      onChange={e => aggiornaCondizioni({ condizionePagamentoAccontoId: e.target.value ? parseInt(e.target.value, 10) : null })}
                      disabled={accontoDisabled}
                      title={accNum <= 0 ? 'Impostare una % Acconto > 0 per scegliere la condizione di pagamento acconto' : undefined}
                    >
                      <option value="">— Nessuna —</option>
                      {pagamenti.map(p => (
                        <option key={p.id} value={p.id}>{p.nome}</option>
                      ))}
                    </select>
                  </div>
                );
              })()}
              {(prev.dataInvio || prev.noteInvio) && (
                <div className="prev-card__sub-section">
                  <div className="prev-card__sub-title">Invio</div>
                  <div className="bop-form-group">
                    <label className="bop-label">Data invio</label>
                    <div className="prev-field__readonly">
                      {prev.dataInvio ? formatDateTime(prev.dataInvio) : '—'}
                    </div>
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">Note invio</label>
                    <div className="prev-field__readonly">
                      {prev.noteInvio || '—'}
                    </div>
                  </div>
                </div>
              )}
              {prev.importoAcconto != null && prev.importoSaldo != null && (
                <div className="bop-form-group prev-acconto-riepilogo">
                  <label className="bop-label">Riepilogo acconto</label>
                  <div className="prev-acconto-riepilogo__box">
                    <div className="prev-acconto-riepilogo__row">
                      <span>Acconto:</span>
                      <strong>€ {formatCurrency(prev.importoAcconto)}</strong>
                    </div>
                    <div className="prev-acconto-riepilogo__row">
                      <span>Saldo:</span>
                      <strong>€ {formatCurrency(prev.importoSaldo)}</strong>
                    </div>
                  </div>
                </div>
              )}
              {/* Sottosezione: pagamento extra-capitolato acquirente */}
              {isConfig && prev.offertaCapitolato && prev.tipoFatturazioneFreeze === 'CLIENTE_PAGA_DIRETTAMENTE' && (
                <>
                  <hr className="prev-card__divider" />
                  <div className="bop-form-group">
                    <label className="bop-label prev-label-section">Pagamento extra-capitolato acquirente</label>
                    <small className="bop-text-muted">
                      Condizioni di pagamento per gli importi extra a carico dell'acquirente finale
                      (valido solo se il capitolato prevede pagamento diretto cliente).
                    </small>
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">% Acconto acquirente</label>
                    <input
                      className="bop-input"
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={typeof testataDraft.percentualeAccontoExtraAcquirente === 'string'
                        ? testataDraft.percentualeAccontoExtraAcquirente
                        : (testataDraft.percentualeAccontoExtraAcquirente ?? '')}
                      onChange={e => aggiornaCondizioni({ percentualeAccontoExtraAcquirente: e.target.value })}
                      disabled={readonly}
                      placeholder="es. 100"
                    />
                  </div>
                  {(() => {
                    const accRaw = testataDraft.percentualeAccontoExtraAcquirente;
                    const accNum =
                      accRaw === null || accRaw === undefined || accRaw === ''
                        ? 0
                        : typeof accRaw === 'number'
                          ? accRaw
                          : parseFloat(String(accRaw)) || 0;
                    const accontoAcqDisabled = readonly || accNum <= 0;
                    return (
                      <div className="bop-form-group">
                        <label className="bop-label">Pagamento acconto extra-capitolato</label>
                        <select
                          className="bop-select"
                          value={testataDraft.condizionePagamentoExtraAccontoId ?? ''}
                          onChange={e => aggiornaCondizioni({
                            condizionePagamentoExtraAccontoId: e.target.value ? parseInt(e.target.value, 10) : null
                          })}
                          disabled={accontoAcqDisabled}
                          title={accNum <= 0 ? 'Impostare una % Acconto acquirente > 0 per scegliere la condizione di pagamento acconto' : undefined}
                        >
                          <option value="">— Nessuna —</option>
                          {pagamenti.map(p => (
                            <option key={p.id} value={p.id}>{p.nome}</option>
                          ))}
                        </select>
                      </div>
                    );
                  })()}
                  <div className="bop-form-group">
                    <label className="bop-label">Pagamento saldo extra-capitolato</label>
                    <select
                      className="bop-select"
                      value={testataDraft.condizionePagamentoExtraSaldoId ?? ''}
                      onChange={e => aggiornaCondizioni({
                        condizionePagamentoExtraSaldoId: e.target.value ? parseInt(e.target.value, 10) : null
                      })}
                      disabled={readonly}
                    >
                      <option value="">— Nessuna —</option>
                      {pagamenti.map(p => (
                        <option key={p.id} value={p.id}>{p.nome}</option>
                      ))}
                    </select>
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">Codice IVA fattura extra-acquirente</label>
                    <div className="bop-flex prev-iva-extra-row">
                      <select
                        className="bop-select"
                        value={testataDraft.codiceIvaExtraAcquirenteId ?? ''}
                        onChange={e => {
                          const val = e.target.value ? parseInt(e.target.value, 10) : null;
                          aggiornaCondizioni({ codiceIvaExtraAcquirenteId: val });
                          if (val != null) setModalReminderIva(true);
                        }}
                        disabled={readonly}
                      >
                        <option value="">— Nessuna —</option>
                        {codiciIva.map(c => (
                          <option key={c.id} value={c.id}>
                            {c.codice} — {c.descrizione} ({formatNumber(parseFloat(c.aliquota))}%)
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="bop-btn bop-btn-secondary"
                        onClick={() => setModalReminderIva(true)}
                        title="Apri il reminder sulla dichiarazione IVA agevolata"
                      >
                        Ricorda dichiarazione
                      </button>
                    </div>
                    <small className="bop-text-muted">
                      Codice IVA applicato in fattura per gli importi extra a carico dell'acquirente finale.
                    </small>
                  </div>
                </>
              )}
            </div>
          </div>

        </div>
        )}

        {/* Toggle collapse Sezione Righe */}
        <button
          type="button"
          className="prev-section-toggle"
          onClick={() => setCardsRigheCollassate(!cardsRigheCollassate)}
        >
          <span className="prev-section-toggle__line" />
          <span className="prev-section-toggle__label">
            <ChevronDownIcon collapsed={cardsRigheCollassate} />
            Righe preventivo
          </span>
          <span className="prev-section-toggle__line" />
        </button>

        {/* Sezione Righe */}
        {!cardsRigheCollassate && (() => {
          const renderCtx: GruppoRenderCtx = {
            raggruppamenti: prev.raggruppamenti,
            righe: prev.righe,
            readonly,
            isConfig,
            hasCapitolato,
            vista,
            colCount,
            descrittivaColSpan,
            expandedRighe,
            toggleExpanded,
            openL1, openL2, openL3,
            toggleOpen: toggleOpenLivello,
            focusGruppoId,
            setFocusGruppoId,
            righePerGruppo,
            totaleRigheRicorsivo,
            importoRicorsivo,
            renameRaggrId,
            renameValue,
            setRenameRaggrId,
            setRenameValue,
            salvaRinominaRaggr,
            eliminaRaggr,
            duplicaRaggr: duplicaRaggruppamento,
            creaRaggruppamento,
            apriNuovaRiga: apriNuovaRigaInGruppo,
            onDelete: (r) => eliminaRiga(r.id, r.descrizione),
            onEdit: (r) => { setConfigRigaEditing(r); setConfigRigaOpen(true); },
            onDuplicate: (r) => duplicaRiga(r.id),
            onScegliAlternativa: (r) => scegliAlternativa(r.id),
            onAlternativaAction: (r) => { if (r.alternativaGruppo) slegaAlternativa(r); else apriRendiAlternativa(r); },
            clusterHeaderInfo: (g, idx) => clusterHeaderInfo(g, idx),
          };
          return (
        <div className="bop-card prev-righe-wrap">
          <div className="prev-righe-toolbar">
            <h2 className="prev-card__title">Righe preventivo ({totaleRighe})</h2>
            <div className="prev-righe-toolbar__left">
              {isConfig && (
                <div className="prev-vista-toggle" role="radiogroup" aria-label="Vista righe">
                  <button
                    className={
                      vista === 'cliente'
                        ? 'prev-vista-toggle__btn prev-vista-toggle__btn--active'
                        : 'prev-vista-toggle__btn'
                    }
                    onClick={() => setVista('cliente')}
                    role="radio"
                    aria-checked={vista === 'cliente'}
                  >
                    Vista cliente
                  </button>
                  <button
                    className={
                      vista === 'interna'
                        ? 'prev-vista-toggle__btn prev-vista-toggle__btn--active'
                        : 'prev-vista-toggle__btn'
                    }
                    onClick={() => setVista('interna')}
                    role="radio"
                    aria-checked={vista === 'interna'}
                  >
                    Vista interna
                  </button>
                </div>
              )}
              <button
                className="bop-btn bop-btn-primary"
                onClick={() => {
                  setConfigRigaEditing(null);
                  setConfigRigaOpen(true);
                }}
                disabled={readonly}
              >
                <PlusIcon /> Aggiungi riga
              </button>
            </div>
          </div>

          {/* Breadcrumb focus drill-down */}
          {focusGruppoId && (
            <div className="prev-focus-breadcrumb">
              <button
                className="prev-focus-breadcrumb__back"
                onClick={() => setFocusGruppoId(null)}
                title="Esci dalla vista focus"
                type="button"
              >
                ← Torna a tutto il preventivo
              </button>
              <div className="prev-focus-breadcrumb__path">
                {focusPath.map((g, i) => (
                  <React.Fragment key={g.id}>
                    {i > 0 && <span className="prev-focus-breadcrumb__sep">›</span>}
                    <button
                      className="prev-focus-breadcrumb__crumb"
                      onClick={() => setFocusGruppoId(g.id)}
                      type="button"
                    >
                      <span className="prev-gp__livello prev-gp__livello--inline">L{g.livello}</span>
                      {g.etichetta}
                    </button>
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}

          {/* Tree dei raggruppamenti con DnD */}
          <DndContext
            sensors={sensors}
            collisionDetection={collisionDetection}
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDragEnd={onDragEnd}
          >
            <SortableContext
              items={rootGruppi.map(g => `G:${g.id}`)}
              strategy={verticalListSortingStrategy}
            >
              <div className="prev-tree">
                {rootGruppi.map(g => (
                  <SortableGruppoNode key={g.id} g={g} ctx={renderCtx} />
                ))}
              </div>
            </SortableContext>

            {/* Aggiungi sezione di livello 1 — solo quando non si è in focus */}
            {!focusGruppoId && !readonly && (
              <div className="prev-tree__add-root">
                <AggiungiGruppoBox
                  livello={1}
                  parentId={null}
                  onAdd={(et, lv, pid) => creaRaggruppamento(et, lv, pid)}
                />
              </div>
            )}

            {/* Righe senza raggruppamento (root) — solo quando non si è in focus */}
            {!focusGruppoId && (righeFree.length > 0 || prev.raggruppamenti.length === 0) && (
              <div className="prev-root-rows">
                <div className="prev-root-rows__header">
                  <span className="prev-root-rows__label">Righe senza raggruppamento</span>
                  <span className="prev-root-rows__count">{righeFree.length} righe</span>
                </div>
                {righeFree.length > 0 ? (
                  <GruppoRigheTable righe={righeFree} ctx={renderCtx} />
                ) : (
                  <EmptyDropZone gruppoId="root" label="senza raggruppamento" />
                )}
              </div>
            )}

            <DragOverlay dropAnimation={null}>
              {dragPreviewContent}
            </DragOverlay>
          </DndContext>

          {totaleRighe === 0 && prev.raggruppamenti.length === 0 && (
            <div className="bop-empty-state">Nessuna riga. Aggiungi una sezione o una riga.</div>
          )}
        </div>
          );
        })()}

        {/* Toggle collapse Footer */}
        <button
          type="button"
          className="prev-section-toggle"
          onClick={() => setCardsFooterCollassate(!cardsFooterCollassate)}
        >
          <span className="prev-section-toggle__line" />
          <span className="prev-section-toggle__label">
            <ChevronDownIcon collapsed={cardsFooterCollassate} />
            Totali · Note · Allegati
          </span>
          <span className="prev-section-toggle__line" />
        </button>

        {/* Footer: note+allegati / totali */}
        {!cardsFooterCollassate && <div className="prev-footer-layout">
          <div className="bop-card prev-note-card">
            <h3 className="prev-card__title">Note</h3>
            <textarea
              className="bop-input"
              rows={5}
              value={noteDraft}
              onChange={e => {
                setNoteDraft(e.target.value);
                marcaModificato('note');
              }}
              disabled={readonly}
              placeholder="Note per il cliente..."
            />

            <button
              className="prev-allegati-prev-header"
              onClick={() => setAllegatoPrevOpen(v => !v)}
              type="button"
            >
              <span className="bop-label prev-label-section prev-allegati-prev-header__label">Allegati</span>
              {prev.allegati.length > 0 && (
                <span className="prev-allegati-prev-count">{prev.allegati.length}</span>
              )}
              {loadingAllegatiPrev && (
                <span className="prev-allegati-loader" aria-label="Caricamento allegati">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                       stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                </span>
              )}
              <svg
                className={`prev-allegati-prev-chevron${allegatoPrevOpen ? ' prev-allegati-prev-chevron--open' : ''}`}
                width="14" height="14" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
              >
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>
            <small className="bop-text-muted">
              Documenti allegati al preventivo (schede tecniche, disegni, foto, capitolati firmati...).
              Sempre modificabili anche con preventivo inviato. Il flag "visibile ufficio tecnico" indica
              gli allegati condivisi anche con la produzione.
            </small>

            {allegatoPrevOpen && (
            <div className="prev-allegati-prev-body">
              {prev.allegati.length === 0 ? (
                <div className="bop-empty-state">Nessun allegato caricato</div>
              ) : (
                <div className="prev-allegati-prev-lista">
                  {prev.allegati.map(a => (
                    <div key={a.id} className="prev-allegato-prev-row">
                      <div className="prev-allegato-prev-row__top">
                        <svg className="prev-allegato-prev-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                          <polyline points="14 2 14 8 20 8"/>
                        </svg>
                        <span className="prev-allegato-prev-nome">{a.nome}</span>
                        {a.visibileUfficioTecnico && (
                          <span className="bop-badge bop-badge-info prev-allegato-prev-ut">Uff. tecnico</span>
                        )}
                      </div>
                      <div className="prev-allegato-prev-row__bottom">
                        <span className="bop-text-muted">
                          {a.nomeFile}{a.dimensione ? ` · ${Math.round(a.dimensione / 1024)} KB` : ''}
                        </span>
                        {a.mimeType === 'application/pdf' ? (
                          <label
                            className="bop-flex"
                            title="Includi questo allegato in coda al PDF del preventivo"
                            style={{ gap: 'var(--bop-space-xs)', cursor: readonly ? 'default' : 'pointer' }}
                          >
                            <input
                              type="checkbox"
                              checked={!!a.stampaConPreventivo}
                              disabled={readonly}
                              onChange={(e) =>
                                aggiornaAllegatoPreventivoHandler(a.id, { stampaConPreventivo: e.target.checked })
                              }
                            />
                            <span className="bop-text-muted" style={{ fontSize: 'var(--bop-font-sm)' }}>
                              Stampa col preventivo
                            </span>
                          </label>
                        ) : (
                          <span
                            className="bop-text-muted"
                            title="Solo gli allegati PDF possono essere inclusi nella stampa del preventivo"
                            style={{ fontSize: 'var(--bop-font-sm)' }}
                          >
                            Stampa: solo PDF
                          </span>
                        )}
                        <div className="prev-allegato-prev-row__btns">
                          <label
                            className="bop-table__action-btn"
                            title={a.visibileUfficioTecnico
                              ? "Nascondi all'ufficio tecnico"
                              : "Rendi visibile all'ufficio tecnico"}
                          >
                            <input
                              type="checkbox"
                              className="prev-allegato-prev-checkbox"
                              checked={a.visibileUfficioTecnico}
                              onChange={(e) => aggiornaAllegatoPreventivoHandler(a.id, { visibileUfficioTecnico: e.target.checked })}
                            />
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M3 7l5-3 5 3 5-3v13l-5 3-5-3-5 3z"/>
                              <path d="M8 4v13M13 7v13"/>
                            </svg>
                          </label>
                          {/* @api GET /api/preventivi/:id/allegati/:allegatoId/download -> apre file in nuova tab */}
                          <button
                            className="bop-table__action-btn bop-table__action-btn--primary"
                            title="Apri in nuova tab"
                            onClick={async () => {
                              try {
                                await apiOpenBlob(`/api/preventivi/${prev.id}/allegati/${a.id}/download`);
                              } catch {
                                showToast('Errore nell\'apertura del file', 'error');
                              }
                            }}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                              <circle cx="12" cy="12" r="3"/>
                            </svg>
                          </button>
                          <button
                            className="bop-table__action-btn bop-table__action-btn--primary"
                            title="Scarica"
                            onClick={async () => {
                              try {
                                // @api GET /api/preventivi/:id/allegati/:allegatoId/download -> file stream (attachment)
                                await apiDownload(`/api/preventivi/${prev.id}/allegati/${a.id}/download`, a.nomeFile || 'allegato');
                              } catch {
                                showToast('Errore nel download', 'error');
                              }
                            }}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                              <polyline points="7 10 12 15 17 10"/>
                              <line x1="12" y1="15" x2="12" y2="3"/>
                            </svg>
                          </button>
                          <button
                            className="bop-table__action-btn bop-table__action-btn--danger"
                            title="Elimina allegato"
                            onClick={() => eliminaAllegatoPreventivoHandler(a.id, a.nome)}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6"/>
                              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                              <path d="M10 11v6M14 11v6"/>
                              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="prev-allegato-prev-upload">
                <div className="prev-allegato-prev-upload__header">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="17 8 12 3 7 8"/>
                    <line x1="12" y1="3" x2="12" y2="15"/>
                  </svg>
                  <span>Carica nuovo allegato</span>
                </div>
                <div className="prev-allegato-prev-upload__fields">
                  <div className="bop-form-group">
                    <label className="bop-label">File</label>
                    <input
                      ref={fileInputAllegatoPrevRef}
                      type="file"
                      className="bop-input"
                      onChange={e => setFileDaCaricarePrev(e.target.files?.[0] || null)}
                    />
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">Nome allegato *</label>
                    <input
                      className="bop-input"
                      value={nomeAllegatoNuovoPrev}
                      onChange={e => setNomeAllegatoNuovoPrev(e.target.value)}
                      placeholder="es. Scheda tecnica pavimento"
                    />
                  </div>
                </div>
                <div className="prev-allegato-prev-upload__footer">
                  <label className="prev-allegato-prev-upload__ut-check">
                    <input
                      type="checkbox"
                      checked={visibileUtNuovoPrev}
                      onChange={e => setVisibileUtNuovoPrev(e.target.checked)}
                    />
                    <span>Visibile ufficio tecnico</span>
                  </label>
                  <button
                    className="bop-btn bop-btn-primary"
                    onClick={uploadAllegatoPreventivoHandler}
                    disabled={savingAllegatoPrev || !fileDaCaricarePrev || !nomeAllegatoNuovoPrev.trim()}
                  >
                    {savingAllegatoPrev ? (
                      <>
                        <svg className="prev-allegato-spinner" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                        </svg>
                        Carico…
                      </>
                    ) : (
                      <>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="17 8 12 3 7 8"/>
                          <line x1="12" y1="3" x2="12" y2="15"/>
                          <path d="M5 20h14"/>
                        </svg>
                        Carica
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
            )}
          </div>

          <div className={`prev-totali-stack${prev.totaliDettaglio.isImpresaAcquirente ? ' prev-totali-stack--dual' : ''}`}>
          {/* Ramo impresa (o ramo unico per i preventivi non impresa+acquirente) */}
          <div className="prev-totali-panel">
            <div className="prev-totali-panel__title">
              {prev.totaliDettaglio.isImpresaAcquirente ? 'Totali impresa' : 'Totali'}
            </div>
            {!prev.totaliDettaglio.isImpresaAcquirente && (
              <div className="prev-totali-row">
                <span className="prev-totali-row__label">Imponibile lordo</span>
                <span className="prev-totali-row__value">€ {formatCurrency(imponibileLordoNum)}</span>
              </div>
            )}
            <div className="prev-totali-row">
              <span className="prev-totali-row__label">Sconto globale</span>
              <div className="prev-sconto-inputs">
                <select
                  className="bop-select"
                  value={scontoDraft.tipo}
                  onChange={e => {
                    const newTipo = e.target.value;
                    setScontoDraft({ tipo: newTipo, valore: newTipo ? scontoDraft.valore : '' });
                    marcaModificato('scontoGlobale');
                  }}
                  disabled={readonly}
                >
                  <option value="">Nessuno</option>
                  <option value="PERCENTUALE">%</option>
                  <option value="IMPORTO">€</option>
                </select>
                <input
                  className="bop-input"
                  type="number"
                  step="0.01"
                  value={scontoDraft.valore}
                  onChange={e => {
                    setScontoDraft(s => ({ ...s, valore: e.target.value }));
                    marcaModificato('scontoGlobale');
                  }}
                  disabled={readonly || !scontoDraft.tipo}
                />
              </div>
            </div>
            <div className="prev-totali-row">
              <span className="prev-totali-row__label">
                {prev.totaliDettaglio.isImpresaAcquirente ? 'Imponibile' : 'Imponibile netto'}
              </span>
              <span className="prev-totali-row__value">€ {formatCurrency(prev.totaliDettaglio.impresa.imponibile)}</span>
            </div>
            <div className="prev-totali-row">
              <span className="prev-totali-row__label">IVA <span className="bop-text-muted">(al {formatNumber(prev.totaliDettaglio.impresa.aliquotaIva)}%)</span></span>
              <span className="prev-totali-row__value">€ {formatCurrency(prev.totaliDettaglio.impresa.iva)}</span>
            </div>
            <div className="prev-totali-row prev-totali-row--totale">
              <span className="prev-totali-row__label">Totale</span>
              <span className="prev-totali-row__value">€ {formatCurrency(prev.totaliDettaglio.impresa.totale)}</span>
            </div>
            {renderAccontoRows(prev.totaliDettaglio.impresa)}
            {!readonly && headerDirty.has('scontoGlobale') && (
              <div className="prev-totali-warning" role="status">
                {scontoDraft.tipo && scontoDraft.valore === ''
                  ? 'Inserisci il valore dello sconto per applicarlo.'
                  : 'Sconto in salvataggio, i totali si aggiornano tra un istante.'}
              </div>
            )}
            {!readonly && (
              <button
                className="bop-btn bop-btn-secondary"
                onClick={ricalcolaTotali}
                disabled={headerDirty.has('scontoGlobale')}
                title={headerDirty.has('scontoGlobale')
                  ? 'Attendi il salvataggio dello sconto'
                  : undefined}
              >
                Ricalcola totali
              </button>
            )}
          </div>

          {/* Ramo extra-capitolato acquirente */}
          {prev.totaliDettaglio.isImpresaAcquirente && prev.totaliDettaglio.acquirente && (
            <div className="prev-totali-panel">
              <div className="prev-totali-panel__title">Totali acquirente</div>
              <div className="prev-totali-row">
                <span className="prev-totali-row__label">Imponibile</span>
                <span className="prev-totali-row__value">€ {formatCurrency(prev.totaliDettaglio.acquirente.imponibile)}</span>
              </div>
              <div className="prev-totali-row">
                <span className="prev-totali-row__label">IVA <span className="bop-text-muted">(al {formatNumber(prev.totaliDettaglio.acquirente.aliquotaIva)}%)</span></span>
                <span className="prev-totali-row__value">€ {formatCurrency(prev.totaliDettaglio.acquirente.iva)}</span>
              </div>
              <div className="prev-totali-row prev-totali-row--totale">
                <span className="prev-totali-row__label">Totale</span>
                <span className="prev-totali-row__value">€ {formatCurrency(prev.totaliDettaglio.acquirente.totale)}</span>
              </div>
              {renderAccontoRows(prev.totaliDettaglio.acquirente)}
            </div>
          )}
          </div>
        </div>}

        {/* Toggle collapse Condizioni generali */}
        <button
          type="button"
          className="prev-section-toggle"
          onClick={() => setCardsCondizioniCollassate(!cardsCondizioniCollassate)}
        >
          <span className="prev-section-toggle__line" />
          <span className="prev-section-toggle__label">
            <ChevronDownIcon collapsed={cardsCondizioniCollassate} />
            Condizioni generali
          </span>
          <span className="prev-section-toggle__line" />
        </button>

        {!cardsCondizioniCollassate && (
          <div className="bop-card prev-condizioni-card">
            <div className="prev-condizioni-card__header">
              <h3 className="prev-card__title">Condizioni generali</h3>
              <button
                type="button"
                className="bop-btn bop-btn-secondary"
                onClick={ricaricaCondizioniDaSede}
                disabled={readonly}
                title={readonly ? 'Preventivo non modificabile' : 'Sovrascrive con il testo configurato sulla sede'}
              >
                ↻ Aggiorna da default sede
              </button>
            </div>
            <small className="bop-text-muted prev-condizioni-card__hint">
              Testo che verrà inserito in coda al preventivo stampato. Modificabile solo se il preventivo è in stato BOZZA.
              Le modifiche vengono salvate insieme al resto del preventivo con il bottone Salva.
            </small>
            <CondizioniHtmlEditor
              value={condizioniDraft}
              onChange={(html) => {
                // L'editor può notificare lo stesso HTML (normalizzazione al caricamento).
                if (html === bozzeRef.current.condizioni) return;
                setCondizioniDraft(html);
                marcaModificato('condizioni.testoHtml');
              }}
              disabled={readonly}
              minHeight={300}
            />
          </div>
        )}

        {/* Log */}
        <div className="bop-card prev-log-section">
          <button className="prev-log-toggle" onClick={() => setLogsOpen(o => !o)}>
            {logsOpen ? '▼' : '▶'} Log azioni ({logs.length})
          </button>
          {logsOpen && (
            <div className="prev-log-timeline">
              {logs.length === 0 ? (
                <div className="bop-empty-state">Nessun log registrato</div>
              ) : (
                logs.map(l => (
                  <div key={l.id} className="prev-log-entry">
                    <div>
                      <div className="prev-log-entry__time">{formatDateTime(l.createdAt)}</div>
                      <div className="bop-text-muted">{l.utenteName || '—'}</div>
                    </div>
                    <div>
                      <div className="prev-log-entry__azione">{l.azione}</div>
                      {l.dettaglio && <div className="prev-log-entry__dettaglio">{l.dettaglio}</div>}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {modalReminderIva && (
          <ModalReminderIvaAgevolata onClose={() => setModalReminderIva(false)} />
        )}
        {uploadFirmaOpen && (
          <ModalCaricaPreventivoFirmato
            preventivoId={prev.id}
            onClose={() => setUploadFirmaOpen(false)}
            onFirmaRegistrata={() => {
              setUploadFirmaOpen(false);
              ricarica();
            }}
          />
        )}
        {storicoFirmeOpen && (
          <ModalStoricoFirme
            preventivoId={prev.id}
            onClose={() => setStoricoFirmeOpen(false)}
          />
        )}
        {marcaInviatoOpen && (
          <ModalMarcaComeInviato
            preventivoId={prev.id}
            isOpen={marcaInviatoOpen}
            onClose={() => setMarcaInviatoOpen(false)}
            onInviato={() => {
              setMarcaInviatoOpen(false);
              showToast('Preventivo marcato come INVIATO', 'success');
              ricarica();
            }}
          />
        )}
        {emailInviataInfo && (
          <ModalConfermaInvioPostMail
            preventivoId={prev.id}
            destinatario={emailInviataInfo.destinatario}
            inviatoIl={emailInviataInfo.inviatoIl}
            isOpen={true}
            onClose={() => setEmailInviataInfo(null)}
            onConfermato={() => {
              setEmailInviataInfo(null);
              showToast('Preventivo marcato come INVIATO', 'success');
              ricarica();
            }}
          />
        )}
        {vistaJsonOpen && <ModalJson prev={prev} onClose={() => setVistaJsonOpen(false)} />}
        {duplicaOpen && (
          <ModalDuplicaPreventivo
            preventivo={prev}
            onClose={() => setDuplicaOpen(false)}
            onDuplicated={(newId) => {
              setDuplicaOpen(false);
              navigate(`/commerciale/attivo/preventivi/${newId}`);
            }}
          />
        )}
        {vistaSceltaOpen && (
          <ModalSceltaVistaStampa
            preventivoNumero={prev.numero}
            labelAppaltatore={
              prev.clienteRagioneSociale
              || [prev.clienteNome, prev.clienteCognome].filter(Boolean).join(' ')
              || null
            }
            labelAcquirente={
              prev.acquirente
                ? [prev.acquirente.nome, prev.acquirente.cognome].filter(Boolean).join(' ')
                  + (prev.acquirente.unitaCodice ? ` (${prev.acquirente.unitaCodice})` : '')
                : null
            }
            mostraSceltaVista={prev.tipo === 'CONFIGURATO_COMMESSA' && !!prev.acquirenteId}
            onSelected={(v, totali, allegati) => {
              setVistaStampa(v);
              setStampaTotali(totali);
              setStampaAllegati(allegati);
              setVistaSceltaOpen(false);
              setStampaOpen(true);
            }}
            onClose={() => setVistaSceltaOpen(false)}
          />
        )}
        {firmaDigitaleSceltaOpen && (
          <ModalSceltaVistaStampa
            preventivoNumero={prev.numero}
            labelAppaltatore={
              prev.clienteRagioneSociale
              || [prev.clienteNome, prev.clienteCognome].filter(Boolean).join(' ')
              || null
            }
            labelAcquirente={
              prev.acquirente
                ? [prev.acquirente.nome, prev.acquirente.cognome].filter(Boolean).join(' ')
                  + (prev.acquirente.unitaCodice ? ` (${prev.acquirente.unitaCodice})` : '')
                : null
            }
            mostraSceltaVista={prev.tipo === 'CONFIGURATO_COMMESSA' && !!prev.acquirenteId}
            onSelected={async (v, totali, allegati) => {
              setFirmaDigitaleSceltaOpen(false);
              setFirmaDigitaleInCorso(true);
              try {
                // @api POST /api/preventivi/:id/genera-firma-digitale {"vista":"APPALTATORE|ACQUIRENTE|undefined","conTotali":"boolean","conAllegati":"boolean"} -> {"success":true}
                const res = await apiFetch<{ success: boolean; error?: string }>(
                  `/api/preventivi/${prev.id}/genera-firma-digitale`,
                  {
                    method: 'POST',
                    body: JSON.stringify({
                      vista: v || undefined,
                      conTotali: totali === 'CON_TOTALI',
                      conAllegati: allegati === 'CON_ALLEGATI',
                    }),
                  }
                );
                if (!res.success) {
                  showToast(res.error || 'Errore durante la generazione del documento per firma', 'error');
                  return;
                }
                showToast('Documento inviato per firma digitale. Il preventivo è ora in stato INVIATO.', 'success');
                await ricarica();
              } catch (e) {
                showToast((e as Error)?.message || 'Errore imprevisto', 'error');
              } finally {
                setFirmaDigitaleInCorso(false);
              }
            }}
            onClose={() => setFirmaDigitaleSceltaOpen(false)}
          />
        )}
        {stampaOpen && (
          <PrintModal
            societaId={prev.societaId}
            entitaType="preventivo"
            entitaId={prev.id}
            entitaLabel={`Preventivo ${prev.numero}`}
            emailDestinatarioDefault={prev.clienteEmail || undefined}
            vista={vistaStampa || undefined}
            conTotali={stampaTotali === 'CON_TOTALI'}
            conAllegati={stampaAllegati === 'CON_ALLEGATI'}
            onClose={() => {
              setStampaOpen(false);
              setVistaStampa(null);
              setStampaTotali('CON_TOTALI');
              setStampaAllegati('CON_ALLEGATI');
            }}
          />
        )}
        {convertiOpen && (
          <ModalConvertiPreventivo
            preventivo={prev}
            onClose={() => setConvertiOpen(false)}
            onConverted={(commessaId) => {
              setConvertiOpen(false);
              ricarica();
              navigate(`/commerciale/commesse/${commessaId}`);
            }}
          />
        )}
        {nuovaRigaOpen && (
          <ModalNuovaRiga
            raggruppamenti={prev.raggruppamenti}
            defaultRaggruppamentoId={prev.raggruppamenti[0]?.id ?? null}
            righeEsistenti={prev.righe}
            onClose={() => setNuovaRigaOpen(false)}
            onSave={creaRiga}
          />
        )}
        {rendiAltOpen && rendiAltRiga && (
          <ModalRendiAlternativa
            riga={rendiAltRiga}
            righeRaggruppamento={(prev?.righe || []).filter(r => r.raggruppamentoId === rendiAltRiga.raggruppamentoId)}
            onClose={() => { setRendiAltOpen(false); setRendiAltRiga(null); }}
            onSave={confermaRendiAlternativa}
          />
        )}
        {configRigaOpen && (
          <ModalConfigurazioneRiga
            preventivo={
              {
                id: prev.id,
                societaId: prev.societaId,
                offertaCapitolatoId: prev.offertaCapitolatoId,
                ricaricoImpresaFreeze: prev.ricaricoImpresaFreeze,
                codiceIvaDefaultId: prev.codiceIvaDefaultId,
                tipoFatturazioneFreeze: prev.tipoFatturazioneFreeze,
                acquirenteId: prev.acquirenteId,
                flussoCommerciale: prev.flussoCommerciale,
                offertaCapitolato: prev.offertaCapitolato
                  ? {
                      id: prev.offertaCapitolato.id,
                      codice: prev.offertaCapitolato.codice,
                      oggetto: prev.offertaCapitolato.oggetto,
                      ricarico: prev.offertaCapitolato.ricarico,
                    }
                  : null,
              } as PreventivoLite
            }
            rigaEsistente={configRigaEditing as unknown as RigaLite | null}
            righeDelPreventivo={prev.righe as unknown as RigaLite[]}
            raggruppamenti={prev.raggruppamenti}
            codiciIva={codiciIva}
            onClose={() => {
              setConfigRigaOpen(false);
              setConfigRigaEditing(null);
            }}
            onSaved={() => {
              ricarica();
            }}
          />
        )}
      </div>

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}

// ── Modale reminder IVA agevolata ─────────────────────────────────────────
function ModalReminderIvaAgevolata({ onClose }: { onClose: () => void }) {
  return (
    <div className="bop-modal-overlay">
      <div className="bop-modal bop-modal--sm">
        <div className="bop-modal__header">
          <h3 className="bop-modal__title">Promemoria: dichiarazione IVA agevolata</h3>
          <button className="bop-modal__close" onClick={onClose}>×</button>
        </div>
        <div className="bop-modal__body">
          <p>
            Stai impostando un codice IVA per la fatturazione degli importi extra all'acquirente finale.
          </p>
          <p>
            Se si tratta di un'aliquota agevolata, ricorda di <strong>richiedere all'acquirente
            la dichiarazione scritta</strong> che attesti il diritto all'applicazione dell'aliquota scelta,
            e di <strong>allegarla tra gli Allegati dell'acquirente</strong> nella sezione qui sopra
            (flaggando l'opzione "IVA agevolata" sul documento caricato).
          </p>
        </div>
        <div className="bop-modal__footer">
          <button className="bop-btn bop-btn-primary" onClick={onClose}>Ho capito</button>
        </div>
      </div>
    </div>
  );
}

// ── Helper cluster header ──────────────────────────────────────────────────
function clusterHeaderInfo(righe: Riga[], idx: number): {
  isFirstOfCluster: boolean;
  gruppo: string | null;
  descrizioneScelta: string | null;
  numAlternative: number;
} {
  const r = righe[idx];
  if (!r.alternativaGruppo) {
    return { isFirstOfCluster: false, gruppo: null, descrizioneScelta: null, numAlternative: 0 };
  }
  const isFirstOfCluster = idx === 0
    || righe[idx - 1].alternativaGruppo !== r.alternativaGruppo
    || righe[idx - 1].raggruppamentoId !== r.raggruppamentoId;
  if (!isFirstOfCluster) {
    return { isFirstOfCluster: false, gruppo: null, descrizioneScelta: null, numAlternative: 0 };
  }
  const cluster = righe.filter(x =>
    x.raggruppamentoId === r.raggruppamentoId &&
    x.alternativaGruppo === r.alternativaGruppo
  );
  const scelta = cluster.find(x => x.alternativaScelta);
  return {
    isFirstOfCluster: true,
    gruppo: r.alternativaGruppo,
    descrizioneScelta: scelta?.descrizione || null,
    numAlternative: cluster.length - 1,
  };
}

// ── Sotto-componente riga ──────────────────────────────────────────────────
function RigaRow({
  r,
  idx,
  readonly,
  isConfig,
  hasCapitolato,
  vista,
  descrittivaColSpan,
  totalColCount,
  expanded,
  onToggleExpand,
  onDelete,
  onEdit,
  onDuplicate,
  onScegliAlternativa,
  onAlternativaAction,
  dnd,
}: {
  r: Riga;
  idx: number;
  readonly: boolean;
  isConfig: boolean;
  hasCapitolato: boolean;
  vista: VistaRighe;
  descrittivaColSpan: number;
  totalColCount: number;
  expanded: boolean;
  onToggleExpand: () => void;
  onDelete: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onScegliAlternativa: () => void;
  onAlternativaAction: () => void;
  dnd?: {
    setNodeRef: (el: HTMLElement | null) => void;
    style: React.CSSProperties;
    attributes: Record<string, any>;
    listeners: Record<string, any> | undefined;
  };
}) {
  const gripCell = (
    <td className="prev-col-grip" {...(dnd?.attributes || {})} {...(dnd?.listeners || {})} title={dnd ? 'Trascina per spostare' : undefined}>
      {dnd && !readonly ? <GripIcon /> : null}
    </td>
  );
  const isDescrittiva = r.tipoRiga === 'DESCRITTIVA';
  const isAlternativa = !!r.alternativaGruppo;
  const nonScelta = isAlternativa && !r.alternativaScelta;

  const classes = [
    isDescrittiva ? 'prev-riga-descrittiva' : '',
    isAlternativa ? 'prev-riga-cluster' : '',
    nonScelta ? 'prev-riga-alternativa--non-scelta' : '',
  ].filter(Boolean).join(' ');

  const badgeCap = hasCapitolato ? TIPO_CAPITOLATO_BADGE[r.tipoRigaCapitolato] : null;
  const capCell = hasCapitolato;

  // Calcoli Vista cliente
  const q = parseFloat(r.quantita || '0');
  const sc = parseFloat(r.sconto || '0');
  const prezzoCliente = capCell && r.extraAcquirenteCalcolato != null
    ? parseFloat(r.extraAcquirenteCalcolato)
    : parseFloat(r.prezzoUnitario || '0');
  const totaleCliente = capCell
    ? prezzoCliente * q * (1 - sc / 100)
    : (r.importoNetto != null ? parseFloat(r.importoNetto) : prezzoCliente * q * (1 - sc / 100));

  const descrizioneCell = (
    <>
      {isAlternativa && (
        <input
          type="radio"
          className="prev-riga-alternativa-radio"
          checked={r.alternativaScelta}
          onChange={onScegliAlternativa}
          disabled={readonly}
          title={r.etichettaAlternativa || r.alternativaGruppo || 'Alternativa'}
        />
      )}
      {r.descrizione}
      {badgeCap && (
        <span className={`prev-badge-capitolato ${badgeCap.cls}`}>{badgeCap.label}</span>
      )}
      {r.descrizioneEstesa && (
        <div className="bop-text-muted">{r.descrizioneEstesa}</div>
      )}
      {r.tipoRiga === 'RICORRENTE' && (
        <div className="prev-riga-meta">
          {r.sottotipoRicorrenza && (
            <span className="prev-riga-meta__chip">
              {SOTTOTIPO_RICORRENZA_LABEL[r.sottotipoRicorrenza]}
            </span>
          )}
          {r.periodicita && (
            <span className="prev-riga-meta__chip">
              {PERIODICITA_RICORRENZA_LABEL[r.periodicita]}
            </span>
          )}
          {r.durataMesi ? (
            <span className="prev-riga-meta__chip">{r.durataMesi} mesi</span>
          ) : (
            <span className="prev-riga-meta__chip prev-riga-meta__chip--muted">perpetuo</span>
          )}
          {r.modalitaPagamento && (
            <span className="prev-riga-meta__chip">
              {MODALITA_PAGAMENTO_LABEL[r.modalitaPagamento]}
            </span>
          )}
        </div>
      )}
      {r.tipoRiga === 'A_CORPO' && (
        <div className="prev-riga-meta">
          <span className="prev-riga-meta__chip prev-riga-meta__chip--muted">
            Figli da definire in validazione tecnica
          </span>
        </div>
      )}
    </>
  );

  const isDifferita = r.tipoRiga === 'ARTICOLO_CONFIGURATO' && r.modalitaConfigurazione === 'DIFFERITA_IN_ORDINE';
  const tipoBadge = (
    <span className={`prev-tipo-riga-label ${isDifferita ? 'prev-tipo-riga-label--differita' : TIPO_RIGA_CSS[r.tipoRiga]}`}>
      {isDifferita ? 'Config. differita' : TIPO_RIGA_LABEL[r.tipoRiga]}
    </span>
  );

  const isConfigurataRow = r.tipoRiga === 'ARTICOLO_CONFIGURATO';
  const hasComponenti = isConfigurataRow && r.componenti && r.componenti.length > 0;

  if (isDescrittiva) {
    return (
      <tr className={classes} ref={dnd?.setNodeRef as any} style={dnd?.style}>
        {gripCell}
        <td className="prev-col-num">{idx}</td>
        <td className="prev-col-tipo">{tipoBadge}</td>
        <td colSpan={descrittivaColSpan}>
          {r.descrizione}
          {r.descrizioneEstesa && (
            <div className="bop-text-muted">{r.descrizioneEstesa}</div>
          )}
        </td>
        <td className="prev-col-azioni">
          <div className="prev-azioni-wrap">
            {!readonly && (
              <>
                <button className="bop-btn-icon" onClick={onEdit} title="Modifica"><EditIcon /></button>
                <button className="bop-btn-icon" onClick={onDuplicate} title="Duplica riga"><CopyIcon /></button>
                <button className="bop-btn-icon" onClick={onDelete} title="Elimina"><TrashIcon /></button>
              </>
            )}
          </div>
        </td>
      </tr>
    );
  }

  return (
    <>
      <tr className={classes} ref={dnd?.setNodeRef as any} style={dnd?.style}>
        {gripCell}
        {isConfigurataRow ? (
          <td className="prev-col-num prev-col-num--expandable">
            <button
              className="prev-num-expand-btn"
              onClick={onToggleExpand}
              title={expanded ? 'Nascondi componenti' : 'Mostra componenti'}
            >
              <span className="prev-num-expand-btn__idx">{idx}</span>
              <ChevronDownIcon collapsed={!expanded} />
            </button>
          </td>
        ) : (
          <td className="prev-col-num">{idx}</td>
        )}
        <td className="prev-col-tipo">{tipoBadge}</td>
        <td className="prev-col-codice">{r.codicePrecopy || '—'}</td>
        <td>{descrizioneCell}</td>
        <td className="prev-col-um">{r.umVendita || '—'}</td>
        <td className="prev-col-num-val">{r.quantita ? formatQuantity(num(r.quantita)) : '—'}</td>

        {!isConfig && (
          <>
            <td className="prev-col-num-val">{r.prezzoUnitario ? formatCurrency(num(r.prezzoUnitario)) : '—'}</td>
            <td className="prev-col-num-val">{r.sconto ? formatQuantity(num(r.sconto)) : '—'}</td>
            <td className="prev-col-num-val">{r.importoNetto ? formatCurrency(num(r.importoNetto)) : '—'}</td>
          </>
        )}

        {isConfig && vista === 'cliente' && (
          <>
            <td className="prev-col-num-val">{formatCurrency(prezzoCliente)}</td>
            <td className="prev-col-num-val">{r.sconto ? formatQuantity(num(r.sconto)) : '—'}</td>
            <td className="prev-col-num-val">{formatCurrency(totaleCliente)}</td>
          </>
        )}

        {isConfig && vista === 'interna' && (
          <>
            <td className="prev-col-num-val">{r.prezzoUnitario ? formatCurrency(num(r.prezzoUnitario)) : '—'}</td>
            <td className="prev-col-num-val">{capCell && r.scorporoCapitolato != null ? formatCurrency(num(r.scorporoCapitolato)) : '—'}</td>
            <td className="prev-col-num-val">{capCell && r.deltaCalcolato != null ? formatCurrency(num(r.deltaCalcolato)) : '—'}</td>
            <td className="prev-col-num-val">{capCell && r.extraAcquirenteCalcolato != null ? formatCurrency(num(r.extraAcquirenteCalcolato)) : '—'}</td>
            <td className="prev-col-num-val">{capCell && r.importoImpresaCalcolato != null ? formatCurrency(num(r.importoImpresaCalcolato)) : '—'}</td>
            <td className="prev-col-num-val">{r.sconto ? formatQuantity(num(r.sconto)) : '—'}</td>
          </>
        )}

        <td className="prev-col-azioni">
          <div className="prev-azioni-wrap">
            {!readonly && (
              <>
                <button className="bop-btn-icon" onClick={onEdit} title="Modifica"><EditIcon /></button>
                {r.raggruppamentoId && (
                  <button
                    className="bop-btn-icon"
                    onClick={onAlternativaAction}
                    title={r.alternativaGruppo ? 'Slega da alternative' : 'Rendi alternativa di…'}
                  ><BranchIcon /></button>
                )}
                <button className="bop-btn-icon" onClick={onDuplicate} title="Duplica riga"><CopyIcon /></button>
                <button className="bop-btn-icon" onClick={onDelete} title="Elimina"><TrashIcon /></button>
              </>
            )}
          </div>
        </td>
      </tr>

      {isConfigurataRow && expanded && (
        <tr className="prev-riga-accordion">
          <td className="prev-col-grip"></td>
          <td colSpan={totalColCount - 1} className="prev-riga-accordion__cell">
            {hasComponenti ? (
              <table className="prev-componenti-table">
                <thead>
                  <tr>
                    <th className="prev-comp-col-principale">P.</th>
                    <th>Descrizione</th>
                    <th className="prev-comp-col-codice">Codice</th>
                    <th className="prev-comp-col-num">Qta</th>
                    <th className="prev-comp-col-um">U.M.</th>
                    <th className="prev-comp-col-num">Costo unit.</th>
                    <th className="prev-comp-col-num">Prezzo vend.</th>
                    <th className="prev-comp-col-sconti">Sc. acq.</th>
                    <th className="prev-comp-col-sconti">Sc. vend.</th>
                  </tr>
                </thead>
                <tbody>
                  {r.componenti.map(c => (
                    <tr key={c.id} className={c.isPrincipale ? 'prev-comp-row--principale' : ''}>
                      <td className="prev-comp-col-principale">
                        {c.isPrincipale && <span className="prev-comp-badge-principale" title="Componente principale">•</span>}
                      </td>
                      <td>{c.descrizione}</td>
                      <td className="prev-comp-col-codice">{c.codicePrecopy || '—'}</td>
                      <td className="prev-comp-col-num">{c.quantita ? formatQuantity(num(c.quantita)) : '—'}</td>
                      <td className="prev-comp-col-um">{c.umVendita || '—'}</td>
                      <td className="prev-comp-col-num">{c.costoUnitario ? formatCurrency(num(c.costoUnitario)) : '—'}</td>
                      <td className="prev-comp-col-num">{c.prezzoVenditaUnitario ? formatCurrency(num(c.prezzoVenditaUnitario)) : '—'}</td>
                      <td className="prev-comp-col-sconti">{c.scontoAcquistoCodice || '—'}</td>
                      <td className="prev-comp-col-sconti">{c.scontoVenditaCodice || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <span className="bop-text-muted prev-comp-vuoti">Nessun componente configurato</span>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// ── Wrapper sortable della riga ─────────────────────────────────────────────
function SortableRigaRow(props: {
  r: Riga;
  idx: number;
  readonly: boolean;
  isConfig: boolean;
  hasCapitolato: boolean;
  vista: VistaRighe;
  descrittivaColSpan: number;
  totalColCount: number;
  expanded: boolean;
  onToggleExpand: () => void;
  onDelete: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onScegliAlternativa: () => void;
  onAlternativaAction: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `R:${props.r.id}`,
    data: { type: 'riga', raggruppamentoId: props.r.raggruppamentoId },
    disabled: props.readonly,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  return <RigaRow {...props} dnd={{ setNodeRef, style, attributes, listeners }} />;
}

// ── Empty zone droppable per gruppi senza righe dirette ─────────────────────
function EmptyDropZone({ gruppoId, label }: { gruppoId: number | 'root'; label: string }) {
  const { setNodeRef, isOver } = useSortable({
    id: `GE:${gruppoId}`,
    data: { type: 'empty-zone', gruppoId },
  });
  return (
    <div
      ref={setNodeRef}
      className={`prev-empty-rows-zone${isOver ? ' prev-empty-rows-zone--over' : ''}`}
    >
      Nessuna riga in <em>{label}</em> · trascina qui una riga
    </div>
  );
}

// ── Box contestuale "+ Aggiungi gruppo" ─────────────────────────────────────
function AggiungiGruppoBox({
  livello,
  parentId,
  parentLabel,
  onAdd,
}: {
  livello: 1 | 2 | 3;
  parentId: number | null;
  parentLabel?: string;
  onAdd: (etichetta: string, livello: 1 | 2 | 3, parentId: number | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');

  const tipoNome =
    livello === 1 ? 'sezione di livello 1' :
    livello === 2 ? 'sezione di livello 2' :
                    'sezione di livello 3';
  const placeholder =
    livello === 1
      ? 'Etichetta sezione di livello 1'
      : livello === 2
      ? `Etichetta sezione di livello 2${parentLabel ? ` (in ${parentLabel})` : ''}`
      : `Etichetta sezione di livello 3${parentLabel ? ` (in ${parentLabel})` : ''}`;

  const conferma = () => {
    const v = value.trim();
    if (!v) return;
    onAdd(v, livello, parentId);
    setValue('');
    setOpen(false);
  };

  if (!open) {
    return (
      <button className="prev-add-gruppo-trigger" onClick={() => setOpen(true)} type="button">
        + Aggiungi {tipoNome}
        {parentLabel && <span className="prev-add-gruppo-trigger__ctx"> in "{parentLabel}"</span>}
      </button>
    );
  }

  return (
    <div className="prev-add-gruppo-box">
      <input
        className="bop-input"
        autoFocus
        placeholder={placeholder}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') conferma();
          if (e.key === 'Escape') {
            setOpen(false);
            setValue('');
          }
        }}
      />
      <button className="bop-btn bop-btn-primary" onClick={conferma} disabled={!value.trim()} type="button">
        Aggiungi
      </button>
      <button
        className="bop-btn bop-btn-secondary"
        onClick={() => {
          setOpen(false);
          setValue('');
        }}
        type="button"
      >
        Annulla
      </button>
    </div>
  );
}

// ── Contesto di rendering per i nodi gruppo ─────────────────────────────────
interface GruppoRenderCtx {
  raggruppamenti: Raggruppamento[];
  righe: Riga[];
  readonly: boolean;
  isConfig: boolean;
  hasCapitolato: boolean;
  vista: VistaRighe;
  colCount: number;
  descrittivaColSpan: number;
  expandedRighe: Set<number>;
  toggleExpanded: (id: number) => void;
  openL1: Set<number>;
  openL2: Set<number>;
  openL3: Set<number>;
  toggleOpen: (livello: 1 | 2 | 3, id: number) => void;
  focusGruppoId: number | null;
  setFocusGruppoId: (id: number | null) => void;
  righePerGruppo: (gruppoId: number | null) => Riga[];
  totaleRigheRicorsivo: (gruppoId: number) => number;
  importoRicorsivo: (gruppoId: number) => number;
  renameRaggrId: number | null;
  renameValue: string;
  setRenameRaggrId: (n: number | null) => void;
  setRenameValue: (s: string) => void;
  salvaRinominaRaggr: (rId: number) => void;
  eliminaRaggr: (rId: number, etichetta: string) => void;
  duplicaRaggr: (rId: number) => void;
  creaRaggruppamento: (etichetta: string, livello: 1 | 2 | 3, parentId: number | null) => void;
  apriNuovaRiga: (raggruppamentoId: number | null) => void;
  onDelete: (r: Riga) => void;
  onEdit: (r: Riga) => void;
  onDuplicate: (r: Riga) => void;
  onScegliAlternativa: (r: Riga) => void;
  onAlternativaAction: (r: Riga) => void;
  clusterHeaderInfo: (gruppo: Riga[], idx: number) => {
    isFirstOfCluster: boolean;
    gruppo: string | null;
    descrizioneScelta: string | null;
    numAlternative: number;
  };
}

// ── Tabella delle righe dirette di un gruppo (sortable) ─────────────────────
function GruppoRigheTable({ righe, ctx }: { righe: Riga[]; ctx: GruppoRenderCtx }) {
  if (righe.length === 0) return null;
  return (
    <SortableContext items={righe.map((r) => `R:${r.id}`)} strategy={verticalListSortingStrategy}>
      <table className="bop-table prev-righe-table prev-righe-table--gruppo">
        <thead>
          <tr>
            <th className="prev-col-grip"></th>
            <th className="prev-col-num">#</th>
            <th className="prev-col-tipo">Tipo</th>
            <th className="prev-col-codice">Codice</th>
            <th>Descrizione</th>
            <th className="prev-col-um">U.M.</th>
            <th className="prev-col-num-val">Qta</th>
            {!ctx.isConfig && (
              <>
                <th className="prev-col-num-val">Prezzo unit.</th>
                <th className="prev-col-num-val">Sconto %</th>
                <th className="prev-col-num-val">Importo</th>
              </>
            )}
            {ctx.isConfig && ctx.vista === 'cliente' && (
              <>
                <th className="prev-col-num-val">Prezzo cliente</th>
                <th className="prev-col-num-val">Sconto %</th>
                <th className="prev-col-num-val">Totale cliente</th>
              </>
            )}
            {ctx.isConfig && ctx.vista === 'interna' && (
              <>
                <th className="prev-col-num-val">Prezzo pieno</th>
                <th className="prev-col-num-val">Scorporo cap.</th>
                <th className="prev-col-num-val">Delta</th>
                <th className="prev-col-num-val">Extra acquirente</th>
                <th className="prev-col-num-val">Quota impresa</th>
                <th className="prev-col-num-val">Sconto %</th>
              </>
            )}
            <th className="prev-col-azioni">Azioni</th>
          </tr>
        </thead>
        <tbody>
          {righe.map((r, idx) => {
            const ch = ctx.clusterHeaderInfo(righe, idx);
            return (
              <React.Fragment key={r.id}>
                {ch.isFirstOfCluster && (
                  <tr className="prev-cluster-header">
                    <td colSpan={ctx.colCount}>
                      <span className="prev-badge-gruppo">Gruppo {ch.gruppo}</span>
                      {ch.descrizioneScelta && (
                        <>
                          <span className="bop-text-muted"> · scelta corrente: </span>
                          <strong>{ch.descrizioneScelta}</strong>
                        </>
                      )}
                      <span className="bop-text-muted">
                        {' '}· {ch.numAlternative} alternativ{ch.numAlternative === 1 ? 'a' : 'e'}
                      </span>
                    </td>
                  </tr>
                )}
                <SortableRigaRow
                  r={r}
                  idx={idx + 1}
                  readonly={ctx.readonly}
                  isConfig={ctx.isConfig}
                  hasCapitolato={ctx.hasCapitolato}
                  vista={ctx.vista}
                  descrittivaColSpan={ctx.descrittivaColSpan}
                  totalColCount={ctx.colCount}
                  expanded={ctx.expandedRighe.has(r.id)}
                  onToggleExpand={() => ctx.toggleExpanded(r.id)}
                  onDelete={() => ctx.onDelete(r)}
                  onEdit={() => ctx.onEdit(r)}
                  onDuplicate={() => ctx.onDuplicate(r)}
                  onScegliAlternativa={() => ctx.onScegliAlternativa(r)}
                  onAlternativaAction={() => ctx.onAlternativaAction(r)}
                />
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </SortableContext>
  );
}

// ── Singolo nodo gruppo (sortable, ricorsivo) ───────────────────────────────
function SortableGruppoNode({ g, ctx }: { g: Raggruppamento; ctx: GruppoRenderCtx }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `G:${g.id}`,
    data: { type: 'gruppo', livello: g.livello, parentId: g.parentId },
    disabled: ctx.readonly,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const isOpen =
    g.livello === 1 ? ctx.openL1.has(g.id) : g.livello === 2 ? ctx.openL2.has(g.id) : ctx.openL3.has(g.id);
  const figli = ctx.raggruppamenti
    .filter((x) => x.parentId === g.id && x.livello === ((g.livello + 1) as 1 | 2 | 3))
    .sort((a, b) => a.ordine - b.ordine);
  const righeDirette = ctx.righePerGruppo(g.id);
  const canHaveChildren = g.livello < 3;
  const figliL = (g.livello + 1) as 1 | 2 | 3;

  return (
    <div ref={setNodeRef} style={style} className={`prev-gp prev-gp--l${g.livello}`}>
      <div className="prev-gp__header" onClick={() => ctx.toggleOpen(g.livello, g.id)}>
        {!ctx.readonly && (
          <span
            className="prev-gp__grip"
            onClick={(e) => e.stopPropagation()}
            {...attributes}
            {...(listeners || {})}
            title="Trascina per riordinare"
          >
            <GripIcon />
          </span>
        )}
        <span className="prev-gp__chevron">
          {isOpen ? <ChevronDownSimpleIcon /> : <ChevronRightIcon />}
        </span>
        <span className="prev-gp__livello">L{g.livello}</span>
        {ctx.renameRaggrId === g.id ? (
          <input
            className="bop-input prev-gp__rename"
            value={ctx.renameValue}
            autoFocus
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => ctx.setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') ctx.salvaRinominaRaggr(g.id);
              if (e.key === 'Escape') {
                ctx.setRenameRaggrId(null);
                ctx.setRenameValue('');
              }
            }}
            onBlur={() => ctx.salvaRinominaRaggr(g.id)}
          />
        ) : (
          <span
            className="prev-gp__label"
            onDoubleClick={(e) => {
              e.stopPropagation();
              if (ctx.readonly) return;
              ctx.setRenameRaggrId(g.id);
              ctx.setRenameValue(g.etichetta);
            }}
            title={ctx.readonly ? undefined : 'Doppio click per rinominare'}
          >
            {g.etichetta}
          </span>
        )}
        <span className="prev-gp__meta">
          {ctx.totaleRigheRicorsivo(g.id)} righe · {formatCurrency(ctx.importoRicorsivo(g.id))}
        </span>
        {!ctx.readonly && (
          <span className="prev-gp__actions" onClick={(e) => e.stopPropagation()}>
            <button
              className="bop-btn-icon"
              title="Focalizza su questo gruppo"
              onClick={() => ctx.setFocusGruppoId(g.id)}
              type="button"
            >
              <FocusIcon />
            </button>
            <button
              className="bop-btn-icon"
              title="Rinomina"
              onClick={() => {
                ctx.setRenameRaggrId(g.id);
                ctx.setRenameValue(g.etichetta);
              }}
              type="button"
            >
              <EditIcon />
            </button>
            <button
              className="bop-btn-icon"
              title="Duplica sezione"
              onClick={() => ctx.duplicaRaggr(g.id)}
              type="button"
            >
              <CopyIcon />
            </button>
            <button
              className="bop-btn-icon"
              title="Elimina raggruppamento"
              onClick={() => ctx.eliminaRaggr(g.id, g.etichetta)}
              type="button"
            >
              <TrashIcon />
            </button>
          </span>
        )}
      </div>

      {isOpen && (
        <div className="prev-gp__body">
          {righeDirette.length > 0 ? (
            <GruppoRigheTable righe={righeDirette} ctx={ctx} />
          ) : (
            <EmptyDropZone gruppoId={g.id} label={g.etichetta} />
          )}

          {canHaveChildren && figli.length > 0 && (
            <SortableContext
              items={figli.map((f) => `G:${f.id}`)}
              strategy={verticalListSortingStrategy}
            >
              <div className="prev-gp__children">
                {figli.map((f) => (
                  <SortableGruppoNode key={f.id} g={f} ctx={ctx} />
                ))}
              </div>
            </SortableContext>
          )}

          {canHaveChildren && !ctx.readonly && (
            <div className="prev-gp__add-child">
              <AggiungiGruppoBox
                livello={figliL}
                parentId={g.id}
                parentLabel={g.etichetta}
                onAdd={(et, lv, pid) => ctx.creaRaggruppamento(et, lv, pid)}
              />
            </div>
          )}

          {!ctx.readonly && (
            <button
              className="prev-link-add prev-link-add--inline"
              onClick={() => ctx.apriNuovaRiga(g.id)}
              type="button"
            >
              + Aggiungi riga in "{g.etichetta}"
            </button>
          )}
        </div>
      )}
    </div>
  );
}
